import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar, Area, AreaChart, ComposedChart } from 'recharts';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { Pie as ChartJSPie } from 'react-chartjs-2';
import { API } from '@/utils/constants';
import { formatDate, todayLocalISO, dateToLocalISO } from '@/utils/date';
import { openPrintPreview } from '@/utils/print';
import { HelpTooltip } from '@/components/HelpTooltip';
// Extracted components for better maintainability
import { PLComparisonChart, PLComparisonTable } from './components';
import { StatusBreakdownChips } from '@/components/reports/StatusBreakdownChips';
import { WorkflowHealthBar } from '@/components/reports/WorkflowHealthBar';
import { StockCostHistoryPopover } from '@/components/reports/StockCostHistoryPopover';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

/**
 * SortableJobsTable — reusable drill-in jobs table with click-to-sort headers.
 * Each column: { key, label, type ('string'|'number'|'date'), render?, sortable?, sortValue?, cellClassName?, cellStyle?(job), headClassName?, headStyle? }
 * Set `sortable: false` for action/button-only columns.
 */
const SortableJobsTable = ({ jobs, columns, defaultSort = { key: 'date_in', dir: 'desc' }, rowStyle }) => {
  const [sort, setSort] = useState(defaultSort);
  const sortedJobs = useMemo(() => {
    const col = columns.find(c => c.key === sort.key);
    if (!col) return jobs;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...jobs].sort((a, b) => {
      const va = col.sortValue ? col.sortValue(a) : a[sort.key];
      const vb = col.sortValue ? col.sortValue(b) : b[sort.key];
      if (col.type === 'number') {
        return ((parseFloat(va) || 0) - (parseFloat(vb) || 0)) * dir;
      }
      const sa = (va || '').toString().toLowerCase();
      const sb = (vb || '').toString().toLowerCase();
      return sa < sb ? -1 * dir : sa > sb ? 1 * dir : 0;
    });
  }, [jobs, sort, columns]);
  const handleSort = (k) => setSort(s => s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' });
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map(col => {
            const active = sort.key === col.key;
            const isSortable = col.sortable !== false;
            const arrow = isSortable ? (active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕') : '';
            return (
              <TableHead
                key={col.key}
                onClick={isSortable ? () => handleSort(col.key) : undefined}
                style={{ cursor: isSortable ? 'pointer' : 'default', userSelect: 'none', ...(col.headStyle || {}) }}
                className={col.headClassName}
                data-testid={`drill-sort-${col.key}`}
              >
                {col.label}
                {arrow && <span style={{ marginLeft: 6, opacity: active ? 1 : 0.35, fontSize: '0.8em' }}>{arrow}</span>}
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedJobs.map(job => (
          <TableRow key={job.id} style={rowStyle ? rowStyle(job) : undefined}>
            {columns.map(col => (
              <TableCell key={col.key} className={col.cellClassName} style={col.cellStyle ? col.cellStyle(job) : undefined}>
                {col.render ? col.render(job) : job[col.key]}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Reports Page Component
 * Comprehensive reporting and analytics dashboard
 */
const ReportsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Re-open the Customer Type drill-in modal when the user navigates back to
  // /reports with ?customerType=<name> in the URL. This fixes the nav state
  // loss: click Not Specified → open a customer → click browser Back → the
  // drill-in modal re-appears with the same filter (was previously closed).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('customerType');
    if (type) {
      // Delay slightly to let the reports page finish its initial mount work
      const t = setTimeout(() => openCustomerTypeDrillIn(type), 100);
      return () => clearTimeout(t);
    }
  }, [location.search]);

  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  // Lazy-load loading flags for the four heavy collections. Show a small
  // "Loading…" indicator on the corresponding sub-tab (Customer Types, Stock
  // Summary, Supplier Spending) while the first-visit fetch is in flight.
  const [customersLoading, setCustomersLoading] = useState(false);
  const [stockItemsLoading, setStockItemsLoading] = useState(false);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  // Read persisted state early so we can hydrate date range from sessionStorage.
  // One-time migration (Jul 2026): if the persisted state was saved before we
  // introduced the "This Quarter" default (or the user's cache is stale from
  // pre-deploy testing), reset the date range so everyone lands on the fast
  // default. Marked via `_migrated_this_quarter_v1` so we only do it once.
  const _persistedRange = (() => {
    try {
      const raw = sessionStorage.getItem('reports_ui_state');
      const parsed = raw ? (JSON.parse(raw) || {}) : {};
      if (!parsed._migrated_this_quarter_v1) {
        parsed.dateRange = 'this_quarter';
        parsed.startDate = '';
        parsed.endDate = '';
        parsed._migrated_this_quarter_v1 = true;
        sessionStorage.setItem('reports_ui_state', JSON.stringify(parsed));
      }
      return parsed;
    } catch { return { dateRange: 'this_quarter' }; }
  })();
  const [dateRange, setDateRange] = useState(_persistedRange.dateRange || 'this_quarter');
  const [startDate, setStartDate] = useState(_persistedRange.startDate || '');
  const [endDate, setEndDate] = useState(_persistedRange.endDate || '');

  // Compute start/end ISO date strings for any dateRange preset
  const getDateBoundaries = (range, customStart = '', customEnd = '') => {
    const today = new Date();
    // Jul 2026 — uses `dateToLocalISO` from utils/date, keeping all app-wide
    // date-only conversions consistent. Never use `.toISOString()` for
    // calendar days — see utils/date.js for the full rationale (Sydney
    // UTC+10 users would see everything shifted 1 day earlier before 10 AM).
    const toISO = dateToLocalISO;
    const todayISO = toISO(today);
    if (range === 'last7') {
      const w = new Date(today); w.setDate(today.getDate() - 7);
      return { start: toISO(w), end: todayISO };
    }
    if (range === 'last30') {
      const m = new Date(today); m.setDate(today.getDate() - 30);
      return { start: toISO(m), end: todayISO };
    }
    if (range === 'this_month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: toISO(first), end: todayISO };
    }
    if (range === 'last_month') {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: toISO(first), end: toISO(last) };
    }
    if (range === 'this_quarter') {
      const q = Math.floor(today.getMonth() / 3);
      const first = new Date(today.getFullYear(), q * 3, 1);
      return { start: toISO(first), end: todayISO };
    }
    if (range === 'ytd') {
      const first = new Date(today.getFullYear(), 0, 1);
      return { start: toISO(first), end: todayISO };
    }
    if (range === 'custom' && customStart && customEnd) {
      return { start: customStart, end: customEnd };
    }
    return { start: '', end: '' };
  };
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [comparisonJobs, setComparisonJobs] = useState([]);
  // Persisted UI state (active tab + drill-down filters) — read from sessionStorage so that
  // navigating away to a Job Detail and clicking Back returns the user to exactly where they were.
  const _persistedState = (() => {
    try {
      const raw = sessionStorage.getItem('reports_ui_state');
      const parsed = raw ? JSON.parse(raw) : null;
      // Jul 9, 2026 — CRITICAL: never restore drill-in / filter selections
      // across a fresh Reports navigation. Restoring `selectedFilter`,
      // `selectedPaymentMethod`, `selectedJobType`, `selectedStockItem` (or
      // an `activeTab='details'`) from a previous session triggers a fetch of
      // ALL 17k jobs (~12 MB uncompressed) via /jobs/list-paginated?limit=0,
      // which took ~14 seconds on production data — dominating the entire
      // Reports load time. The user rarely wants a drill-in to persist
      // between sessions anyway (they usually land on Reports fresh). If they
      // want to re-drill, one click restores it. Keep only the date range /
      // sort / activeTab (unless activeTab is 'details') persisted.
      if (parsed) {
        parsed.selectedFilter = null;
        parsed.selectedPaymentMethod = null;
        parsed.selectedJobType = null;
        parsed.selectedStockItem = null;
        parsed.selectedSupplier = null;
        parsed.innerStatusFilter = null;
        if (parsed.activeTab === 'details') parsed.activeTab = 'financial';
      }
      return parsed;
    } catch { return null; }
  })();
  const [activeTab, setActiveTab] = useState(_persistedState?.activeTab || 'financial');
  const [selectedFilter, setSelectedFilter] = useState(_persistedState?.selectedFilter || null); // { category: 'status', value: 'Booked' }
  // Secondary status filter applied within any drill-down (Location, Type, Referral, Priority, Customer Type)
  const [innerStatusFilter, setInnerStatusFilter] = useState(_persistedState?.innerStatusFilter || null);
  // Reset the inner status filter whenever the outer filter changes
  useEffect(() => { setInnerStatusFilter(null); }, [selectedFilter?.category, selectedFilter?.value]);

  // Referral Sources tab (Feb 2026) — status chip filter for the Revenue box.
  // `null` = "All" chip (show every status combined). Set to a status name
  // (e.g. "Finish", "Not Recoverable") to narrow the right-hand table.
  const [referralStatusChip, setReferralStatusChip] = useState(null);

  // "Jobs In" tab — Count / Count+Revenue toggle. `false` = Count only (default,
  // pure workload view). `true` = show cash-basis revenue $ alongside each row
  // so the user can eyeball intake + convert-to-cash in one glance.
  const [jobsInShowRevenue, setJobsInShowRevenue] = useState(false);
  // ── Quick-close dialog (Feb 2026) ───────────────────────────────────
  // Fired from the Actions column of the Payment Status drill-down modal so
  // the user can review-and-close inline instead of jumping to the Job Detail
  // page for each item. `closeDialogJob` holds the job being closed; the
  // status picker defaults to Not Proceeding (user's usual choice for stale
  // Quoted/Booked jobs per their workflow).
  //
  // `closeDialogMode` = 'single' (one row's Close button) | 'bulk' (Close N Selected)
  const [closeDialogJob, setCloseDialogJob] = useState(null);
  const [closeDialogStatus, setCloseDialogStatus] = useState('Not Proceeding');
  const [closeDialogBusy, setCloseDialogBusy] = useState(false);
  const [closeDialogMode, setCloseDialogMode] = useState('single');
  // Selected job ids (Set) for the bulk-close checkbox column in the drill-in
  // table. Reset whenever the drill-in filter changes so a stale selection
  // can't be applied to a different set of jobs.
  const [selectedJobIds, setSelectedJobIds] = useState(() => new Set());
  useEffect(() => { setSelectedJobIds(new Set()); }, [selectedFilter?.category, selectedFilter?.value]);
  const toggleJobSelected = (id) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const openCloseDialog = (job) => {
    setCloseDialogMode('single');
    setCloseDialogJob(job);
    // Sensible default: if already a terminal status, preselect it; otherwise
    // default to Not Proceeding (stale-lead cleanup is the most common case).
    if (['Finish', 'Not Recoverable', 'Not Proceeding'].includes(job.status)) {
      setCloseDialogStatus(job.status);
    } else {
      setCloseDialogStatus('Not Proceeding');
    }
  };
  const openBulkCloseDialog = () => {
    setCloseDialogMode('bulk');
    setCloseDialogJob(null);
    setCloseDialogStatus('Not Proceeding');
  };
  const confirmCloseJob = async () => {
    if (closeDialogMode === 'single' && !closeDialogJob) return;
    if (closeDialogMode === 'bulk' && selectedJobIds.size === 0) return;
    setCloseDialogBusy(true);
    try {
      const token = localStorage.getItem('access_token');
      if (closeDialogMode === 'single') {
        const res = await axios.put(
          `${API}/jobs/${closeDialogJob.id}`,
          { status: closeDialogStatus, closed: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const closedId = res.data?.id || closeDialogJob.id;
        setFilteredJobs((prev) => prev.filter((j) => j.id !== closedId));
        toast.success(`Job #${closeDialogJob.job_number} closed as ${closeDialogStatus}`);
      } else {
        const ids = Array.from(selectedJobIds);
        const res = await axios.post(
          `${API}/jobs/bulk-close`,
          { job_ids: ids, status: closeDialogStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const closedSet = new Set(res.data?.job_ids || []);
        setFilteredJobs((prev) => prev.filter((j) => !closedSet.has(j.id)));
        setSelectedJobIds(new Set());
        const n = res.data?.updated || 0;
        const missing = res.data?.missing_ids?.length || 0;
        if (missing) {
          toast.success(`Closed ${n} jobs as ${closeDialogStatus} (${missing} not found — refresh recommended)`);
        } else {
          toast.success(`Closed ${n} jobs as ${closeDialogStatus}`);
        }
      }
      setCloseDialogJob(null);
      setCloseDialogMode('single');
    } catch (err) {
      toast.error(`Failed to close: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setCloseDialogBusy(false);
    }
  };

  // Save scroll position continuously; restore on mount
  useEffect(() => {
    // Restore on mount — retry until the page has grown enough to scroll to the target
    const targetY = parseInt(sessionStorage.getItem('reports_scroll_y') || '0', 10);
    if (targetY > 0) {
      let attempts = 0;
      const tryRestore = () => {
        attempts++;
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        if (maxScroll >= targetY) {
          window.scrollTo(0, targetY);
        } else if (attempts < 40) {
          // Page hasn't fully rendered yet — wait and retry (max 4 sec total)
          setTimeout(tryRestore, 100);
        }
      };
      tryRestore();
    }

    // Save scroll position continuously (throttled) so the latest is in storage when we unmount
    let lastSaved = 0;
    const onScroll = () => {
      const now = Date.now();
      if (now - lastSaved > 200) {
        lastSaved = now;
        sessionStorage.setItem('reports_scroll_y', String(window.scrollY));
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      // Don't overwrite saved scroll on unmount — browser may have already scrolled to 0
      // The throttled listener above keeps sessionStorage in sync during scroll events
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  
  // Stock reports state
  const [stockItems, setStockItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(_persistedState?.selectedSupplier || null);
  const [compareSupplierPeriods, setCompareSupplierPeriods] = useState(false);
  // Supplier filter typeahead state (matches Purchases / Expenses pattern)
  const [supplierFilterSearch, setSupplierFilterSearch] = useState(_persistedState?.selectedSupplier?.name || '');
  const [showSupplierFilterDropdown, setShowSupplierFilterDropdown] = useState(false);
  
  // Payment methods report state
  const [paymentMethodsData, setPaymentMethodsData] = useState(null);
  // Money-out companion — purchases + expenses aggregated by payment method
  const [paymentMethodsOutgoing, setPaymentMethodsOutgoing] = useState(null);
  // Toggle: 'in' (customer payments received) | 'out' (money paid out to suppliers)
  const [paymentDirection, setPaymentDirection] = useState('in');
  // Payment Status tab date-field toggle. "revenue" = cash-basis (money in the
  // door on the picked days) — matches P&L, BAS, Dashboard Revenue. "booked" =
  // jobs booked in the picked days regardless of payment (matches the OLD
  // pre-Jul-2026 behaviour). Default = revenue for alignment with everything
  // else on the app.
  const [paymentDateField, setPaymentDateField] = useState('revenue');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(_persistedState?.selectedPaymentMethod || null);
  
  // Job type profitability state
  const [jobTypeProfitability, setJobTypeProfitability] = useState(null);
  const [selectedJobType, setSelectedJobType] = useState(_persistedState?.selectedJobType || null);
  const [compareProfitability, setCompareProfitability] = useState(false);
  const [previousProfitability, setPreviousProfitability] = useState(null);

  // Persist UI state to sessionStorage so back navigation restores tab + drill-down
  useEffect(() => {
    try {
      sessionStorage.setItem('reports_ui_state', JSON.stringify({
        activeTab,
        selectedFilter,
        innerStatusFilter,
        selectedSupplier,
        selectedPaymentMethod,
        selectedJobType,
        dateRange,
        startDate,
        endDate,
      }));
    } catch {}
  }, [activeTab, selectedFilter, innerStatusFilter, selectedSupplier, selectedPaymentMethod, selectedJobType, dateRange, startDate, endDate]);
  
  // Revenue trends and referral source state
  const [revenueTrends, setRevenueTrends] = useState(null);
  // Phase 2 — server-side aggregated distribution + summary (fast charts; replaces in-browser 17k-row aggregation)
  const [distributionData, setDistributionData] = useState(null);
  const [referralSourceData, setReferralSourceData] = useState(null);
  const [sourceCompare, setSourceCompare] = useState(false);
  const [previousSourceData, setPreviousSourceData] = useState(null);

  // Phase 3.1: Advanced Filtering state
  const [reportFilters, setReportFilters] = useState({
    customStartDate: '',
    customEndDate: '',
    jobType: '',
    referralSource: '',
    paymentStatus: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [compareFilters, setCompareFilters] = useState(false); // Compare to previous period for custom date range

  // Phase 3.2: Period-over-Period Comparisons state
  const [comparisonType, setComparisonType] = useState('period'); // 'period' or 'year'
  const [showComparison, setShowComparison] = useState(false);
  const [previousPeriodRevenueTrends, setPreviousPeriodRevenueTrends] = useState(null);

  // Profit & Loss and Expense Report state
  const [profitLossData, setProfitLossData] = useState(null);
  const [expenseReportData, setExpenseReportData] = useState([]);
  // Feb 2026 — Click-to-filter for the Expenses by Category / Payment Method
  // donut charts. When set, the Expense Details table below shows only
  // matching rows and a filter chip becomes visible with a clear (×) button.
  // `otherValues` is populated when the "Other" slice is clicked, listing
  // every small category/method bundled into that group.
  const [expensePieFilter, setExpensePieFilter] = useState(null);
  // { by: 'category' | 'payment_method', value: string, otherValues?: string[] }
  // Feb 2026 — Independent month filter set by clicking a bar in
  // Monthly Expense Trends. Combines with expensePieFilter (AND) so the
  // user can e.g. "show me only Accounting Fees paid in July".
  const [expenseMonthFilter, setExpenseMonthFilter] = useState(null); // "YYYY-MM"
  const [expenseTrends, setExpenseTrends] = useState([]);
  const [expenseReportFilters, setExpenseReportFilters] = useState({
    startDate: '',
    endDate: '',
    category: ''
  });

  // BAS Report state
  const [basReportData, setBasReportData] = useState(null);
  const [basQuarter, setBasQuarter] = useState('');

  // Referral Partners report (specific named partners like DRC Computers, ABC Store)
  const [partnersReport, setPartnersReport] = useState({ rows: [], total_jobs: 0, total_revenue: 0 });
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [basSalesExpanded, setBasSalesExpanded] = useState(false);
  const [basPurchasesExpanded, setBasPurchasesExpanded] = useState(false);
  const [plIncomeExpanded, setPlIncomeExpanded] = useState(false);
  // Payment-method drill (Feb 2026) — which method rows are showing their
  // individual payment records. Independent from the parent Income expand
  // so users can leave several methods open while collapsing others.
  const [expandedIncomeMethods, setExpandedIncomeMethods] = useState({});
  const toggleIncomeMethod = (method) => {
    setExpandedIncomeMethods(prev => ({ ...prev, [method]: !prev[method] }));
  };
  const [plExpensesExpanded, setPlExpensesExpanded] = useState(false);
  const [expandedExpenseCategories, setExpandedExpenseCategories] = useState({});
  // P&L drill-in: which leaf subcategories are showing their transactions,
  // and a cache of the fetched transaction lists keyed by `parent::sub`.
  const [expandedSubTx, setExpandedSubTx] = useState({});
  const [subTxCache, setSubTxCache] = useState({});

  // Toggle inline expansion of a P&L leaf row + lazy-fetch its transactions.
  // Called with source='expense' | 'purchase'. For expenses we send the
  // subcategory name, for purchases (Cost of Sales) we send nothing extra.
  const togglePnlLeaf = async (source, key, categoryName, categoryCode) => {
    setExpandedSubTx(prev => ({ ...prev, [key]: !prev[key] }));
    if (subTxCache[key]) return;  // already loaded
    try {
      const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
      const params = { source, limit: 500 };
      if (categoryName) params.category = categoryName;
      if (categoryCode) params.category_code = categoryCode;
      if (start) params.start_date = start;
      if (end)   params.end_date   = end;
      const resp = await axios.get(`${API}/pnl/category-transactions`, { params, timeout: 30000 });
      setSubTxCache(prev => ({ ...prev, [key]: resp.data.transactions || [] }));
    } catch (err) {
      console.error('Error loading P&L transactions:', err);
      toast.error('Failed to load transactions for this category');
      setSubTxCache(prev => ({ ...prev, [key]: [] }));
    }
  };

  // P&L Comparison state
  const [plCompareEnabled, setPlCompareEnabled] = useState(false);
  const [plCompareType, setPlCompareType] = useState('previous'); // 'previous', 'lastYear', 'custom'
  const [plCompareData, setPlCompareData] = useState(null);
  const [plCompareCustomStart, setPlCompareCustomStart] = useState('');
  const [plCompareCustomEnd, setPlCompareCustomEnd] = useState('');

  // Include internal/personal expenses in reports (default false — business-only view)
  const [includeInternal, setIncludeInternal] = useState(false);

  // Emergency Service drill-in — expands to show the list of jobs that carried
  // an emergency_service_amount inside the selected period. Cash-basis (matches
  // the rest of the Financial tab).
  const [emergencyDrillOpen, setEmergencyDrillOpen] = useState(false);

  // Additional Line Items — one open flag per category so the user can expand
  // Labour / Part / Other independently.
  const [lineItemsDrillOpen, setLineItemsDrillOpen] = useState({});

  // Personal-use claimed drill-in — expands to show the individual expenses /
  // stock purchases the owner flagged as personal-use. Same UX pattern as the
  // Emergency Service drill-in above.
  const [pucDrillOpen, setPucDrillOpen] = useState(false);

  // Jul 2026 — Stock Analysis tab. Per-item sales & profit breakdown from
  // job.stock_items[] aggregated across paid jobs in the selected period.
  // Cash-basis, consistent with the rest of Reports.
  const [stockAnalysisData, setStockAnalysisData] = useState(null);
  const [stockAnalysisLoading, setStockAnalysisLoading] = useState(false);
  const [stockAnalysisSort, setStockAnalysisSort] = useState({ column: 'profit', dir: 'desc' });
  const [stockAnalysisSearch, setStockAnalysisSearch] = useState('');
  const [stockAnalysisExpanded, setStockAnalysisExpanded] = useState({});

  // Feb 2026 — Stock Reconciliation tab. Detects drift between master
  // stock_items.current_quantity and (Σ purchases) − (Σ job allocations).
  // Independent of date range — this is a ledger integrity check, not a
  // period report.
  const [stockReconData, setStockReconData] = useState(null);
  const [stockReconLoading, setStockReconLoading] = useState(false);
  const [stockReconShowClean, setStockReconShowClean] = useState(false);
  const [stockReconExpanded, setStockReconExpanded] = useState({});
  const [stockReconBusyId, setStockReconBusyId] = useState(null);

  // Feb 2026 — Job Type Analysis tab. Recovery outcome breakdown per job
  // type (Finish / Not Recoverable / Not Proceeding). Filters by
  // `date_finished` so only jobs that reached a terminal status inside the
  // selected range are counted — in-progress jobs are excluded so % columns
  // aren't polluted by pending outcomes.
  const [jobTypeAnalysisData, setJobTypeAnalysisData] = useState(null);
  const [jobTypeAnalysisLoading, setJobTypeAnalysisLoading] = useState(false);
  const [jobTypeAnalysisExpanded, setJobTypeAnalysisExpanded] = useState({});

  // Feb 2026 — Review Requests tab. Lists paid customers whose invoice was
  // emailed ≥ N days ago and who haven't yet received a review request.
  const [reviewCandidatesData, setReviewCandidatesData] = useState(null);
  const [reviewCandidatesLoading, setReviewCandidatesLoading] = useState(false);
  const [reviewMinDays, setReviewMinDays] = useState(7);
  const [reviewBusyJobId, setReviewBusyJobId] = useState(null);
  const [reviewConfirmState, setReviewConfirmState] = useState({ open: false, jobId: null, preview: null });
  
  // Personal/Internal drill-in dialog
  const [personalDrillIn, setPersonalDrillIn] = useState({ open: false, scope: 'all', title: '', items: [] });
  
  // Customer Type drill-in dialog (click "Partner" or "End User" row in Customer Types tab)
  // periodNames — array of customer_name strings that had activity in the selected
  // date range. Passed in from the Customer Types table row click so the modal can
  // offer a "Only period" toggle without a second server round-trip.
  const [customerTypeDrillIn, setCustomerTypeDrillIn] = useState({ open: false, type: '', customers: [], loading: false, periodNames: [], periodOnly: false });
  const [ctSortBy, setCtSortBy]   = useState('total_spend');
  const [ctSortDir, setCtSortDir] = useState('desc');
  const onCtSortClick = (field) => {
    if (ctSortBy === field) {
      setCtSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setCtSortBy(field);
      setCtSortDir(field === 'job_count' || field === 'total_spend' ? 'desc' : 'asc');
    }
  };
  const ctSortArrow = (field) => ctSortBy !== field ? '⇅' : (ctSortDir === 'asc' ? '▲' : '▼');
  
  // Fetch the customers for the selected type (uses the paginated endpoint with limit=0 + sort)
  // If `periodNames` is provided (from Customer Types table), we default to filtering the
  // drill-in to only those customers who had activity in the selected date range. The user
  // can flip a toggle to see the full all-time list.
  const openCustomerTypeDrillIn = async (typeName, periodNames = []) => {
    // Default to period-only when we know the period-active names (i.e. row came from the
    // Customer Types table with a filter applied). Ties back to what the user actually
    // clicked — "show me the End Users who came in this week for the newsletter".
    const defaultPeriodOnly = Array.isArray(periodNames) && periodNames.length > 0;
    setCustomerTypeDrillIn({ open: true, type: typeName, customers: [], loading: true, periodNames, periodOnly: defaultPeriodOnly });
    try {
      const params = {
        limit: 0,
        sort_by: ctSortBy,
        sort_dir: ctSortDir,
      };
      if (typeName === 'Not Specified') {
        const resp = await axios.get(`${API}/customers/with-insights`, { params });
        const cs = (resp.data.customers || []).filter(c => !c.customer_type || c.customer_type === '' || c.customer_type === 'Not Specified');
        setCustomerTypeDrillIn({ open: true, type: typeName, customers: cs, loading: false, periodNames, periodOnly: defaultPeriodOnly });
      } else {
        params.customer_type = typeName;
        const resp = await axios.get(`${API}/customers/with-insights`, { params });
        setCustomerTypeDrillIn({ open: true, type: typeName, customers: resp.data.customers || [], loading: false, periodNames, periodOnly: defaultPeriodOnly });
      }
    } catch (err) {
      console.error('Failed to load customers for type:', err);
      toast.error('Failed to load customers');
      setCustomerTypeDrillIn({ open: false, type: '', customers: [], loading: false, periodNames: [], periodOnly: false });
    }
  };
  
  // Re-fetch when user changes sort INSIDE the open dialog (Not Specified is sorted client-side)
  useEffect(() => {
    if (!customerTypeDrillIn.open) return;
    if (customerTypeDrillIn.type === 'Not Specified') {
      // Sort client-side
      const cs = [...customerTypeDrillIn.customers].sort((a, b) => {
        const av = a[ctSortBy] ?? (typeof a[ctSortBy] === 'number' ? 0 : '');
        const bv = b[ctSortBy] ?? (typeof b[ctSortBy] === 'number' ? 0 : '');
        if (av < bv) return ctSortDir === 'asc' ? -1 : 1;
        if (av > bv) return ctSortDir === 'asc' ? 1 : -1;
        return 0;
      });
      setCustomerTypeDrillIn(prev => ({ ...prev, customers: cs }));
    } else {
      // Re-fetch preserves the periodNames + periodOnly state so the toggle
      // survives a sort click. Without this, sorting would reset the filter.
      openCustomerTypeDrillIn(customerTypeDrillIn.type, customerTypeDrillIn.periodNames);
    }
    // eslint-disable-next-line
  }, [ctSortBy, ctSortDir]);

  // Stock Summary report controls
  const [stockSearch, setStockSearch] = useState('');
  const [stockStatusFilter, setStockStatusFilter] = useState('all'); // 'all' | 'in_stock' | 'low' | 'out'
  // Selected stock item for inline drill-in — matches the "Jobs by State" pattern
  const [selectedStockItem, setSelectedStockItem] = useState(null);


  // Toggle expense category expansion
  const toggleExpenseCategory = (category) => {
    setExpandedExpenseCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  useEffect(() => {
    // Phase 2 — don't pre-fetch all 17k jobs on mount; aggregation endpoint covers charts.
    // Phase 3 (Jul 2026) — also removed eager fetches of /customers (~13k rows),
    // /stock-items, /purchases and /suppliers from mount. These payloads were
    // 5–10 MB combined and blocked the Financial tab (the default landing tab)
    // even though they are only consumed by the Customer Types / Stock Summary /
    // Supplier Spending sub-tabs. They are now lazy-fetched on first tab visit
    // via the `activeTab` useEffect below.
    //
    // Phase 4 (Jul 9, 2026) — REMOVED duplicate fetches of the following from
    // this mount effect:
    //   fetchPaymentMethodsReport, fetchPaymentMethodsOutgoing, fetchProfitLoss
    // Each already has a dedicated useEffect on [dateRange, startDate, endDate]
    // further below, which React fires once on initial mount anyway. Calling
    // them here caused 2× duplicate requests per endpoint on every Reports
    // navigation — 4× for ProfitLoss/PaymentMethods when other filters
    // hydrated after mount. On production this stacked to 15s cumulative wait
    // (user report — 4× profit-loss × 4s each = 16s).
    fetchSettings();
    // fetchExpenseTrends() and fetchBasReport() are ALSO deferred — they only
    // populate the P&L / Expense Report / BAS Report tabs, all of which have
    // their own tab-activation useEffects further below.
  }, []);

  // Lazy-load heavy collections only when the tab that needs them is opened.
  // Each fetch is a one-shot (guard on state.length > 0) so re-visiting the tab
  // doesn't refetch.
  useEffect(() => {
    if (activeTab === 'customer-types' && customers.length === 0) {
      fetchCustomers();
    }
    if (activeTab === 'stock-summary' && stockItems.length === 0) {
      fetchStockItems();
    }
    if (activeTab === 'supplier-spending') {
      if (purchases.length === 0) fetchPurchases();
      if (suppliers.length === 0) fetchSuppliers();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  // Refresh both Money-In AND Money-Out payment method reports whenever the
  // top-of-page date range changes. Previously Money-In was date-independent
  // which caused the "Payment Methods Breakdown" totals to disagree with the
  // "Jobs by Payment Status" card sitting right above it.
  // Payment Methods breakdown — only needed on the Payment Status tab. Was
  // firing on every Reports mount even when the user never opens that tab
  // (Jul 9, 2026 speedup).
  useEffect(() => {
    if (activeTab !== 'payment') return;
    fetchPaymentMethodsReport();
    fetchPaymentMethodsOutgoing();
    // eslint-disable-next-line
  }, [activeTab, dateRange, startDate, endDate]);

  // Lazy-load jobs when the user actually needs them (drill-in / Job Details tab
  // / "Compare to Previous Period" toggle on the Financial tab, which does its
  // period-vs-period filtering client-side over the full jobs array).
  // Charts and summary KPIs are powered by the aggregation endpoint so the
  // page paints fast — jobs only load when a workflow needs them.
  useEffect(() => {
    if (jobs.length > 0) return; // already loaded
    const needsJobs = !!selectedFilter
      || !!selectedPaymentMethod
      || !!selectedJobType
      || !!selectedStockItem
      || activeTab === 'details'
      // Feb 2026 — Customer Types tab computes its "period" columns
      // (Customers-in-period, Jobs, Revenue) from `filteredJobs`. Without
      // this the tab loaded but every period-scoped number showed 0 because
      // the jobs collection was never fetched under the lazy-load rework.
      || activeTab === 'customer-types'
      || compareEnabled;
    if (needsJobs) {
      fetchJobs();
    }
  }, [selectedFilter, selectedPaymentMethod, selectedJobType, selectedStockItem, activeTab, compareEnabled]); // eslint-disable-line

  // Keep "Compare to previous" companion fetches for Job Type Profitability + Referral Source Analysis
  useEffect(() => {
    if (compareProfitability) fetchJobTypeProfitability(true);
    if (sourceCompare) fetchReferralSourceAnalysis(true);
    // eslint-disable-next-line
  }, [compareProfitability, sourceCompare, dateRange, startDate, endDate]);

  // Keep "Previous period" fetch for Revenue Trends comparison toggle
  useEffect(() => {
    fetchPreviousPeriodRevenueTrends(monthsFromDateRange());
    // eslint-disable-next-line
  }, [showComparison, comparisonType, dateRange, startDate, endDate]);

  useEffect(() => {
    filterJobsByDateRange();
  }, [jobs, dateRange, startDate, endDate, compareEnabled]);

  // Profit & Loss — only needed on the P&L tab. Deferred (Jul 9, 2026 speedup).
  // Feb 2026 — skip the fetch when Custom Range is selected but the user
  // hasn't filled both dates yet. Without this guard, clicking "Custom Range"
  // fires a full ALL-TIME fetch (~8s) that races against the correct-dates
  // fetch a moment later, and if it resolves last the UI shows all-time
  // numbers even though the dates are filled in correctly.
  useEffect(() => {
    if (activeTab !== 'profit-loss') return;
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
    fetchProfitLoss(start, end);
  }, [activeTab, dateRange, startDate, endDate, includeInternal]);

  // Jul 2026 — Stock Analysis tab fetch. Same date-guard as P&L.
  useEffect(() => {
    if (activeTab !== 'stock-analysis') return;
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
    fetchStockAnalysis(start, end);
  }, [activeTab, dateRange, startDate, endDate]);

  // Feb 2026 — Stock Reconciliation tab fetch. No date range — this is an
  // all-time ledger check.
  useEffect(() => {
    if (activeTab !== 'stock-reconciliation') return;
    fetchStockReconciliation();
  }, [activeTab]); // eslint-disable-line

  // Feb 2026 — Review Requests tab fetch.
  useEffect(() => {
    if (activeTab !== 'review-requests') return;
    fetchReviewCandidates();
  }, [activeTab, reviewMinDays]); // eslint-disable-line

  // Feb 2026 — Job Type Analysis tab fetch (date-scoped, same guard as P&L).
  useEffect(() => {
    if (activeTab !== 'job-type-analysis') return;
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
    fetchJobTypeAnalysis(start, end);
  }, [activeTab, dateRange, startDate, endDate]); // eslint-disable-line

  // Expense Report — needed on the Expense Report tab AND the new
  // Expenses By Supplier tab (which aggregates the same data).
  useEffect(() => {
    if (activeTab !== 'expense-report' && activeTab !== 'expenses-by-supplier') return;
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    fetchExpenseReport();
  }, [activeTab, dateRange, startDate, endDate, expenseReportFilters.category, includeInternal]); // eslint-disable-line

  // BAS Report — only needed on the BAS Report tab. Deferred (Jul 9, 2026 speedup).
  useEffect(() => {
    if (activeTab !== 'bas-report') return;
    fetchBasReport();
  }, [activeTab]); // eslint-disable-line

  // Expense Trends — powers the trends chart on the P&L / Expense Report tabs.
  useEffect(() => {
    if (activeTab !== 'profit-loss' && activeTab !== 'expense-report') return;
    fetchExpenseTrends();
  }, [activeTab]); // eslint-disable-line

  // Refetch Referral Source Analysis (Financial tab + Referral Sources tab) on global date range change
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    fetchReferralSourceAnalysis(false);
    if (compareFilters) fetchReferralSourceAnalysis(true);
  }, [dateRange, startDate, endDate, compareFilters, reportFilters.jobType, reportFilters.paymentStatus]); // eslint-disable-line

  // Refetch Referral Partners report (named-partner aggregate) on date-range change
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    const fetchPartnersReport = async () => {
      const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
      setPartnersLoading(true);
      try {
        const params = {};
        if (start) params.start_date = start;
        if (end) params.end_date = end;
        const res = await axios.get(`${API}/reports/referral-partners`, { params });
        setPartnersReport(res.data || { rows: [], total_jobs: 0, total_revenue: 0 });
      } catch (err) {
        console.error('Partners report fetch failed:', err);
        setPartnersReport({ rows: [], total_jobs: 0, total_revenue: 0 });
      } finally {
        setPartnersLoading(false);
      }
    };
    fetchPartnersReport();
  }, [dateRange, startDate, endDate]); // eslint-disable-line

  // Refetch Job Type Profitability on global date range change
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    fetchJobTypeProfitability(false);
    if (compareFilters) fetchJobTypeProfitability(true);
  }, [dateRange, startDate, endDate, compareFilters, reportFilters.referralSource, reportFilters.paymentStatus]); // eslint-disable-line

  // Refetch Revenue Trends on global date range change
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    fetchRevenueTrends();
  }, [dateRange, startDate, endDate, reportFilters.jobType, reportFilters.referralSource, reportFilters.paymentStatus]); // eslint-disable-line

  // Phase 2 — server-side aggregation for charts & summary KPIs (fast first paint).
  // Replaces the heavy "fetch 17k jobs then aggregate in browser" path used previously.
  // Refetch when tab changes because Payment Status uses paymentDateField and other
  // tabs always use date_in — see fetchDistribution().
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    fetchDistribution();
  }, [dateRange, startDate, endDate, reportFilters.jobType, reportFilters.referralSource, reportFilters.paymentStatus, includeInternal, paymentDateField, activeTab]); // eslint-disable-line

  // Fetch P&L comparison data when comparison is enabled/changed
  useEffect(() => {
    if (!plCompareEnabled) {
      setPlCompareData(null);
      return;
    }

    // Calculate comparison date range based on current selection
    let compareStart = '';
    let compareEnd = '';
    const cur = getDateBoundaries(dateRange, startDate, endDate);
    const currentStart = cur.start;
    const currentEnd = cur.end;

    if (plCompareType === 'previous') {
      // Previous period of same length
      if (currentStart && currentEnd) {
        const start = new Date(currentStart);
        const end = new Date(currentEnd);
        const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        
        const prevEnd = new Date(start);
        prevEnd.setDate(prevEnd.getDate() - 1);
        const prevStart = new Date(prevEnd);
        prevStart.setDate(prevStart.getDate() - daysDiff);
        
        compareStart = dateToLocalISO(prevStart);
        compareEnd = dateToLocalISO(prevEnd);
      }
    } else if (plCompareType === 'lastYear') {
      // Same period last year
      if (currentStart && currentEnd) {
        const start = new Date(currentStart);
        const end = new Date(currentEnd);
        start.setFullYear(start.getFullYear() - 1);
        end.setFullYear(end.getFullYear() - 1);
        
        compareStart = dateToLocalISO(start);
        compareEnd = dateToLocalISO(end);
      }
    } else if (plCompareType === 'custom' && plCompareCustomStart && plCompareCustomEnd) {
      compareStart = plCompareCustomStart;
      compareEnd = plCompareCustomEnd;
    }

    if (compareStart && compareEnd) {
      fetchProfitLossComparison(compareStart, compareEnd);
    }
  }, [plCompareEnabled, plCompareType, dateRange, startDate, endDate, plCompareCustomStart, plCompareCustomEnd]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      // Use the paginated endpoint with limit=0 (no cap) — Reports need ALL jobs for accurate
      // aggregation, distribution charts and filtering across the full 17k+ dataset.
      // 45s timeout: on the full 17k-job payload this returns in ~2-4s server-side but
      // client-side deserialization can push it to 8-10s on slow connections. 45s gives
      // us headroom without hanging the UI forever if the ingress hiccups.
      const response = await axios.get(`${API}/jobs/list-paginated`, { params: { limit: 0 }, timeout: 45000 });
      // Response shape: { jobs, total, returned, sort_by, sort_dir }
      setJobs(response.data.jobs || []);
    } catch (error) {
      const reason = error?.code === 'ECONNABORTED'
        ? 'request timed out — try again in a moment'
        : (error?.response?.status ? `HTTP ${error.response.status}` : (error?.message || 'network error'));
      console.error('Error fetching jobs:', error);
      toast.error(`Failed to load jobs — ${reason}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchStockItems = async () => {
    try {
      setStockItemsLoading(true);
      const response = await axios.get(`${API}/stock-items`);
      setStockItems(response.data);
    } catch (error) {
      console.error('Error fetching stock items:', error);
    } finally {
      setStockItemsLoading(false);
    }
  };

  const fetchPurchases = async () => {
    try {
      setPurchasesLoading(true);
      const response = await axios.get(`${API}/purchases`);
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setPurchasesLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const fetchPaymentMethodsReport = async () => {
    try {
      // Pass the same date window as the Money-Out companion so the two
      // Payment Methods Breakdown views (In / Out) always represent the same
      // period the user chose in the Reports filter bar. Was previously
      // returning lifetime totals — which misaligned with "Jobs by Payment
      // Status" and confused users trying to reconcile a single day.
      const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
      const params = new URLSearchParams();
      if (start) params.set('start_date', start);
      if (end) params.set('end_date', end);
      const response = await axios.get(`${API}/payment-methods-report?${params.toString()}`);
      setPaymentMethodsData(response.data);
    } catch (error) {
      console.error('Error fetching payment methods report:', error);
      toast.error('Failed to load payment methods report');
    }
  };

  // Fetch money-out report (purchases + expenses grouped by payment method)
  const fetchPaymentMethodsOutgoing = async () => {
    try {
      const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
      const params = new URLSearchParams();
      if (start) params.set('start_date', start);
      if (end) params.set('end_date', end);
      const response = await axios.get(`${API}/payment-methods-report/outgoing?${params.toString()}`);
      setPaymentMethodsOutgoing(response.data);
    } catch (error) {
      console.error('Error fetching outgoing payment methods report:', error);
      toast.error('Failed to load money-out payment methods report');
    }
  };

  /**
   * Phase 2 — Fetch all chart breakdowns + summary KPIs from MongoDB in a single
   * server-side $facet aggregation. Returns in ~300ms even on 17k+ jobs, vs the
   * 3–5s "fetch all jobs to browser then aggregate" path used previously.
   */
  const fetchDistribution = async () => {
    try {
      const params = {};
      const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
      if (start) params.start_date = start;
      if (end)   params.end_date   = end;
      if (reportFilters.jobType)        params.job_type        = reportFilters.jobType;
      if (reportFilters.referralSource) params.referral_source = reportFilters.referralSource;
      if (reportFilters.paymentStatus)  params.payment_status  = reportFilters.paymentStatus;
      // FULL CASH-BASIS (Jul 9, 2026) — user runs a cash business and only
      // recognises revenue when a job is fully paid for. Every breakdown on
      // this endpoint (by_status, by_type, by_state, by_referral_source,
      // by_priority, by_brand, top_postcodes, monthly_revenue, summary,
      // finished_paid) now filters on `revenue_date` so tables and totals
      // agree across the page and with the bank feed / Jim2 P&L.
      //
      // Trade-off: unpaid workload (Booked / Quoted / Wait / Approved / Parts)
      // no longer contributes to these breakdowns because those jobs have no
      // revenue_date yet. That's the correct cash-basis behaviour — those
      // jobs simply haven't generated revenue yet in the sense that matters
      // for financial reporting. Users seeking a "how much work is in the
      // pipeline?" workload view should use the All Jobs page which is not
      // date-scoped to a reporting period.
      params.date_field = 'revenue_date';
      // 30s timeout — the endpoint itself returns in ~100ms server-side; a
      // longer wait almost certainly means an ingress/network hiccup and we
      // should surface it rather than silently spin.
      const resp = await axios.get(`${API}/reports/distribution`, { params, timeout: 30000 });
      setDistributionData(resp.data);
    } catch (error) {
      // Surface the failure so the user knows a card is empty because of an
      // error, not because of zero data. Include a short reason for triage.
      const reason = error?.code === 'ECONNABORTED'
        ? 'request timed out'
        : (error?.response?.status ? `HTTP ${error.response.status}` : (error?.message || 'network error'));
      console.error('Error fetching distribution:', error);
      toast.error(`Failed to load Job Distribution — ${reason}`);
    }
  };

  // Compute how many months the global date range covers (clamped 1..36)
  const monthsFromDateRange = () => {
    const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
    if (!start || !end) return 12; // 'all' fallback
    const s = new Date(start);
    const e = new Date(end);
    const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
    return Math.max(1, Math.min(36, months));
  };

  const fetchRevenueTrends = async (months) => {
    try {
      const params = { months: months ?? monthsFromDateRange() };
      
      // Add filters if they are set
      if (reportFilters.jobType) params.job_type = reportFilters.jobType;
      if (reportFilters.referralSource) params.referral_source = reportFilters.referralSource;
      if (reportFilters.paymentStatus) params.payment_status = reportFilters.paymentStatus;
      
      const response = await axios.get(`${API}/revenue-trends`, { params });
      setRevenueTrends(response.data);
    } catch (error) {
      console.error('Error fetching revenue trends:', error);
    }
  };

  const fetchPreviousPeriodRevenueTrends = async (months = 12) => {
    try {
      if (!showComparison) {
        setPreviousPeriodRevenueTrends(null);
        return;
      }

      const params = { months };
      
      // Add filters if they are set
      if (reportFilters.jobType) params.job_type = reportFilters.jobType;
      if (reportFilters.referralSource) params.referral_source = reportFilters.referralSource;
      if (reportFilters.paymentStatus) params.payment_status = reportFilters.paymentStatus;
      
      // For comparison, we'll fetch data from a previous time period
      // The revenue-trends endpoint uses months parameter to go back from today
      // To get previous period data, we need to fetch from further back
      if (comparisonType === 'year') {
        // For year comparison: fetch same period from last year (12 months further back)
        params.months = months * 2; // Fetch 24 months, we'll use the older 12 months
      } else {
        // For period comparison: fetch double the period to get previous equivalent period
        params.months = months * 2; // Fetch double period, we'll use the older half
      }
      
      const response = await axios.get(`${API}/revenue-trends`, { params });
      
      // Split the data to get only the previous period portion
      if (response.data && response.data.length > 0) {
        const totalMonths = response.data.length;
        const splitPoint = Math.floor(totalMonths / 2);
        // Get the first half which represents the older/previous period
        const previousData = response.data.slice(0, splitPoint);
        setPreviousPeriodRevenueTrends(previousData);
      } else {
        setPreviousPeriodRevenueTrends([]);
      }
    } catch (error) {
      console.error('Error fetching previous period revenue trends:', error);
    }
  };

  const fetchReferralSourceAnalysis = async (isPrevious = false) => {
    try {
      let params = {};
      const cur = getDateBoundaries(dateRange, startDate, endDate);

      if (cur.start && cur.end) {
        if (isPrevious && compareFilters) {
          // Same-length previous period
          const s = new Date(cur.start);
          const e = new Date(cur.end);
          const days = Math.ceil((e - s) / (1000 * 60 * 60 * 24));
          const prevEnd = new Date(s); prevEnd.setDate(prevEnd.getDate() - 1);
          const prevStart = new Date(prevEnd); prevStart.setDate(prevStart.getDate() - days);
          params = {
            start_date: dateToLocalISO(prevStart),
            end_date: dateToLocalISO(prevEnd)
          };
        } else if (!isPrevious) {
          params = { start_date: cur.start, end_date: cur.end };
        }
      }

      // Preserve job_type & payment_status filters from Advanced Filters
      if (reportFilters.jobType) params.job_type = reportFilters.jobType;
      if (reportFilters.paymentStatus) params.payment_status = reportFilters.paymentStatus;

      const response = await axios.get(`${API}/referral-source-analysis`, { params });

      if (isPrevious) {
        setPreviousSourceData(response.data);
      } else {
        setReferralSourceData(response.data);
      }
    } catch (error) {
      console.error('Error fetching referral source analysis:', error);
    }
  };

  // Fetch Profit & Loss data
  const fetchProfitLoss = async (startDate = '', endDate = '') => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (includeInternal) params.include_internal = 'true';

      const response = await axios.get(`${API}/profit-loss`, { params });
      setProfitLossData(response.data);
    } catch (error) {
      console.error('Error fetching profit/loss data:', error);
    }
  };

  // Jul 2026 — Fetch per-item stock sales/profit breakdown for the Stock Analysis tab.
  const fetchStockAnalysis = async (startDate = '', endDate = '') => {
    setStockAnalysisLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await axios.get(`${API}/reports/stock-analysis`, { params });
      setStockAnalysisData(response.data);
    } catch (error) {
      console.error('Error fetching stock analysis:', error);
      setStockAnalysisData({ items: [], totals: { quantity_sold: 0, revenue: 0, cost: 0, profit: 0, margin_pct: 0, item_count: 0 } });
    } finally {
      setStockAnalysisLoading(false);
    }
  };

  // Feb 2026 — Fetch stock reconciliation (purchases − allocations vs master).
  const fetchStockReconciliation = async () => {
    setStockReconLoading(true);
    try {
      const response = await axios.get(`${API}/reports/stock-reconciliation`);
      setStockReconData(response.data);
    } catch (error) {
      console.error('Error fetching stock reconciliation:', error);
      setStockReconData({ items: [], totals: { item_count: 0, mismatch_count: 0, clean_count: 0 } });
    } finally {
      setStockReconLoading(false);
    }
  };

  // Feb 2026 — Set current_quantity = expected_qty for a single stock item.
  const handleReconcileStock = async (row) => {
    const delta = row.delta;
    const msg = `Reconcile "${row.stock_item_name}"?\n\n` +
                `Current qty: ${row.current_qty}\n` +
                `Expected qty (purchases − allocations): ${row.expected_qty}\n` +
                `Change: ${delta > 0 ? '−' : '+'}${Math.abs(delta)}\n\n` +
                `The change will be logged on the stock item's activity log.`;
    if (!window.confirm(msg)) return;
    setStockReconBusyId(row.stock_item_id);
    try {
      await axios.post(`${API}/reports/stock-reconciliation/${row.stock_item_id}/reconcile`, { reason: 'Reconciled via Stock Reconciliation report' });
      toast.success(`${row.stock_item_name}: qty set to ${row.expected_qty}`);
      await fetchStockReconciliation();
    } catch (error) {
      console.error('Error reconciling stock item:', error);
      toast.error(error.response?.data?.detail || 'Failed to reconcile');
    } finally {
      setStockReconBusyId(null);
    }
  };

  // Feb 2026 — Job Type Analysis fetch (Finish/NR/NP breakdown per type).
  const fetchJobTypeAnalysis = async (startDate = '', endDate = '') => {
    setJobTypeAnalysisLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await axios.get(`${API}/reports/job-type-analysis`, { params });
      setJobTypeAnalysisData(response.data);
    } catch (error) {
      console.error('Error fetching job type analysis:', error);
      setJobTypeAnalysisData({ rows: [], totals: { total: 0, finish: 0, not_recoverable: 0, not_proceeding: 0, finish_pct: 0, not_recoverable_pct: 0, not_proceeding_pct: 0, success_pct: 0 } });
    } finally {
      setJobTypeAnalysisLoading(false);
    }
  };

  // Feb 2026 — Review-request candidates fetch.
  const fetchReviewCandidates = async () => {
    setReviewCandidatesLoading(true);
    try {
      const response = await axios.get(`${API}/reports/review-candidates`, {
        params: { min_days_since_invoice: reviewMinDays }
      });
      setReviewCandidatesData(response.data);
    } catch (error) {
      console.error('Error fetching review candidates:', error);
      setReviewCandidatesData({ items: [], total: 0 });
    } finally {
      setReviewCandidatesLoading(false);
    }
  };

  // Open the confirm dialog with the rendered subject/body for this job.
  const openReviewConfirm = async (jobId) => {
    setReviewBusyJobId(jobId);
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/review-request/preview`);
      if (!res.data.review_url_configured) {
        toast.error('Google review URL not configured — set it in Settings → Email Templates.', { duration: 5000 });
        return;
      }
      setReviewConfirmState({ open: true, jobId, preview: res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare review request');
    } finally {
      setReviewBusyJobId(null);
    }
  };

  const confirmSendReviewCandidate = async () => {
    const { jobId, preview } = reviewConfirmState;
    if (!jobId) return;
    setReviewBusyJobId(jobId);
    try {
      await axios.post(`${API}/jobs/${jobId}/email-review-request`, {
        subject: preview?.subject, body: preview?.body,
        cc: preview?.cc || '', bcc: preview?.bcc || '',
      });
      toast.success('⭐ Review request sent');
      setReviewConfirmState({ open: false, jobId: null, preview: null });
      await fetchReviewCandidates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send review request');
    } finally {
      setReviewBusyJobId(null);
    }
  };

  // Fetch P&L Comparison data
  const fetchProfitLossComparison = async (startDate, endDate) => {
    try {
      const params = {
        start_date: startDate,
        end_date: endDate
      };
      if (includeInternal) params.include_internal = 'true';
      
      const response = await axios.get(`${API}/profit-loss`, { params });
      setPlCompareData(response.data);
    } catch (error) {
      console.error('Error fetching P&L comparison data:', error);
    }
  };

  // Helper function to calculate variance
  const calcVariance = (current, previous) => {
    const diff = current - previous;
    const pct = previous !== 0 ? ((current - previous) / Math.abs(previous)) * 100 : (current !== 0 ? 100 : 0);
    return { diff, pct };
  };

  // Helper function to format variance display
  const formatVariance = (variance, isProfit = false) => {
    const isPositive = isProfit ? variance.diff >= 0 : variance.diff <= 0;
    const color = isPositive ? '#16a34a' : '#dc2626';
    const arrow = variance.diff > 0 ? '↑' : variance.diff < 0 ? '↓' : '→';
    return {
      color,
      arrow,
      diffText: `${variance.diff >= 0 ? '+' : ''}$${variance.diff.toFixed(2)}`,
      pctText: `${variance.pct >= 0 ? '+' : ''}${variance.pct.toFixed(1)}%`
    };
  };

  // Fetch Expense Report data — honours the global dateRange (top filter) for date scoping
  const fetchExpenseReport = async () => {
    try {
      const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (expenseReportFilters.category) params.category = expenseReportFilters.category;
      if (!includeInternal) params.internal_filter = 'business';
      
      const response = await axios.get(`${API}/expenses`, { params });
      setExpenseReportData(response.data);
    } catch (error) {
      console.error('Error fetching expense report:', error);
    }
  };

  // Fetch Expense Trends
  const fetchExpenseTrends = async (months = 12) => {
    try {
      const response = await axios.get(`${API}/expenses/trends`, { params: { months } });
      setExpenseTrends(response.data);
    } catch (error) {
      console.error('Error fetching expense trends:', error);
    }
  };

  // Fetch BAS Report data
  const fetchBasReport = async (quarter = '') => {
    try {
      const params = {};
      if (quarter) params.quarter = quarter;
      
      const response = await axios.get(`${API}/bas-report`, { params });
      setBasReportData(response.data);
    } catch (error) {
      console.error('Error fetching BAS report:', error);
    }
  };

  // Generate quarter options for BAS dropdown
  const generateQuarterOptions = () => {
    const options = [];
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // Generate quarters for current financial year and previous 2 years
    for (let yearOffset = 0; yearOffset <= 2; yearOffset++) {
      const fy = currentYear - yearOffset;
      // Australian financial year runs July to June
      options.push({ value: `${fy}-Q1`, label: `Q1 ${fy}/${fy+1} (Jul-Sep ${fy})` });
      options.push({ value: `${fy}-Q2`, label: `Q2 ${fy}/${fy+1} (Oct-Dec ${fy})` });
      options.push({ value: `${fy}-Q3`, label: `Q3 ${fy}/${fy+1} (Jan-Mar ${fy+1})` });
      options.push({ value: `${fy}-Q4`, label: `Q4 ${fy}/${fy+1} (Apr-Jun ${fy+1})` });
    }
    
    return options;
  };

  const fetchJobTypeProfitability = async (isPrevious = false) => {
    try {
      let params = {};
      const cur = getDateBoundaries(dateRange, startDate, endDate);

      if (cur.start && cur.end) {
        if (isPrevious && compareFilters) {
          const s = new Date(cur.start);
          const e = new Date(cur.end);
          const days = Math.ceil((e - s) / (1000 * 60 * 60 * 24));
          const prevEnd = new Date(s); prevEnd.setDate(prevEnd.getDate() - 1);
          const prevStart = new Date(prevEnd); prevStart.setDate(prevStart.getDate() - days);
          params = {
            start_date: dateToLocalISO(prevStart),
            end_date: dateToLocalISO(prevEnd)
          };
        } else if (!isPrevious) {
          params = { start_date: cur.start, end_date: cur.end };
        }
      }

      // Preserve filters
      if (reportFilters.referralSource) params.referral_source = reportFilters.referralSource;
      if (reportFilters.paymentStatus) params.payment_status = reportFilters.paymentStatus;

      const response = await axios.get(`${API}/job-type-profitability`, { params });

      if (isPrevious) {
        setPreviousProfitability(response.data);
      } else {
        setJobTypeProfitability(response.data);
      }
    } catch (error) {
      // Silently ignore cancelled/aborted requests — they happen naturally
      // when the user changes the date range quickly and a newer fetch has
      // already been fired. Showing a "Failed to load" toast in that case is
      // misleading (Jul 2026 user report).
      if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return;
      }
      console.error('Error fetching job type profitability:', error);
      toast.error('Failed to load profitability data');
    }
  };

  // Phase 3.4: CSV Export Functions
  const downloadCSV = (csvContent, filename) => {
    try {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      // Check if browser supports download attribute
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        // Fallback for browsers that don't support download attribute
        const url = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);
        window.open(url, '_blank');
        toast.info('CSV opened in new tab. Right-click and "Save As" to download.', { duration: 5000 });
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Please check your browser settings or try a different browser.');
      
      // Last resort: copy to clipboard
      navigator.clipboard.writeText(csvContent).then(() => {
        toast.info('CSV data copied to clipboard! Paste into a text editor and save as .csv', { duration: 6000 });
      }).catch(() => {
        toast.error('Unable to download. Please allow downloads in your browser settings.');
      });
    }
  };

  // Helper: produce a filename-safe slug + a human-readable label for the active global date range
  const dateRangeLabel = () => {
    const labels = {
      all: 'All Time',
      last7: 'Last 7 Days',
      last30: 'Last 30 Days',
      this_month: 'This Month',
      last_month: 'Last Month',
      this_quarter: 'This Quarter',
      ytd: 'Year to Date',
      custom: 'Custom Range',
    };
    return labels[dateRange] || 'All Time';
  };
  const dateRangeSlug = () => {
    const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
    const base = dateRange === 'all' ? 'all_time' : dateRange.replace(/-/g, '_');
    return start && end ? `${base}_${start}_to_${end}` : base;
  };
  // Two-row CSV preamble showing what filters/date range produced this file
  const csvPreamble = () => {
    const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
    const generated = new Date().toLocaleString();
    return [
      `# Date Range: ${dateRangeLabel()}${start && end ? ` (${start} to ${end})` : ''}`,
      `# Generated: ${generated}`,
      ''
    ].join('\n');
  };

  const exportJobTypeProfitability = () => {
    if (!jobTypeProfitability || !jobTypeProfitability.job_types) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Job Type', 'Jobs', 'Total Revenue', 'Avg Revenue', 'Stock Cost', 'Total Profit', 'Profit Margin (%)'];
    const rows = jobTypeProfitability.job_types.map(jt => [
      jt.job_type,
      jt.count,
      jt.total_revenue.toFixed(2),
      jt.avg_revenue.toFixed(2),
      jt.total_stock_cost.toFixed(2),
      jt.total_profit.toFixed(2),
      jt.profit_margin.toFixed(2)
    ]);

    const csvContent = csvPreamble() + [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const filename = `job_type_profitability_${dateRangeSlug()}.csv`;
    downloadCSV(csvContent, filename);
    toast.success(`✅ Downloaded: ${filename} (check your Downloads folder)`, { duration: 5000 });
  };

  const exportReferralSourceAnalysis = () => {
    if (!referralSourceData || !referralSourceData.sources) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Source', 'Jobs', 'Total Revenue', 'Avg Revenue', 'Percentage'];
    const rows = referralSourceData.sources.map(source => [
      source.source || 'Not specified',
      source.count,
      source.total_revenue.toFixed(2),
      source.avg_revenue.toFixed(2),
      source.percentage.toFixed(2)
    ]);

    const csvContent = csvPreamble() + [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const filename = `referral_source_analysis_${dateRangeSlug()}.csv`;
    downloadCSV(csvContent, filename);
    toast.success(`✅ Downloaded: ${filename} (check your Downloads folder)`, { duration: 5000 });
  };

  const exportRevenueTrends = () => {
    if (!revenueTrends || !revenueTrends.length) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Month', 'Revenue', 'Jobs'];
    const rows = revenueTrends.map(trend => [
      trend.month,
      trend.revenue.toFixed(2),
      trend.jobs
    ]);

    const csvContent = csvPreamble() + [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const filename = `revenue_trends_${dateRangeSlug()}.csv`;
    downloadCSV(csvContent, filename);
    toast.success(`✅ Downloaded: ${filename} (check your Downloads folder)`, { duration: 5000 });
  };

  const exportAllReports = () => {
    exportJobTypeProfitability();
    setTimeout(() => exportReferralSourceAnalysis(), 500);
    setTimeout(() => exportRevenueTrends(), 1000);
    setTimeout(() => {
      toast.success('✅ 3 CSV files downloaded to your Downloads folder! Check your browser\'s downloads (Ctrl+J or Cmd+J)', {
        duration: 6000
      });
    }, 1500);
  };

  const filterJobsByDateRange = () => {
    if (!jobs.length) {
      setFilteredJobs([]);
      return;
    }

    // Show all jobs by default
    if (dateRange === 'all' || !dateRange) {
      setFilteredJobs(jobs);
      return;
    }

    const { start: startStr, end: endStr } = getDateBoundaries(dateRange, startDate, endDate);
    if (!startStr || !endStr) {
      setFilteredJobs(jobs);
      return;
    }
    const start = new Date(startStr);
    const end = new Date(endStr);

    const filtered = jobs.filter(job => {
      const jobDate = new Date(job.date_in);
      return jobDate >= start && jobDate <= end;
    });

    setFilteredJobs(filtered);

    // Calculate comparison period if enabled
    if (compareEnabled) {
      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      const compStart = new Date(start);
      compStart.setDate(compStart.getDate() - daysDiff);
      const compEnd = new Date(start);

      const comparison = jobs.filter(job => {
        const jobDate = new Date(job.date_in);
        return jobDate >= compStart && jobDate < compEnd;
      });

      setComparisonJobs(comparison);
    }
  };

  const calculateRevenue = (jobsList) => {
    return jobsList.reduce((sum, job) => sum + (parseFloat(job.total_amount) || 0), 0);
  };

  const calculateGST = (jobsList) => {
    return jobsList.reduce((sum, job) => sum + (parseFloat(job.gst_amount) || 0), 0);
  };

  const getJobsByCategory = (jobsList, field) => {
    const counts = {};
    jobsList.forEach(job => {
      const value = job[field] || 'Unknown';
      counts[value] = (counts[value] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  };

  const getTopPostcodes = (jobsList, limit = 10) => {
    const counts = {};
    jobsList.forEach(job => {
      const postcode = job.postcode || 'Unknown';
      counts[postcode] = (counts[postcode] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([name, value]) => ({ name, value }));
  };

  const exportToCSV = (data, filename) => {
    try {
      if (!data || data.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Escape CSV values properly
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = Object.keys(data[0]).join(',');
      const rows = data.map(row => 
        Object.values(row).map(val => escapeCSV(val)).join(',')
      );
      const csv = csvPreamble() + [headers, ...rows].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${filename}_${dateRangeSlug()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report exported (${dateRangeLabel()})`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export. Your browser may be blocking downloads.');
    }
  };

  const exportJobsReport = () => {
    try {
      const data = filteredJobs.map(job => ({
        'Job Number': job.job_number,
        'Customer': job.customer_name,
        'Status': job.status,
        'Priority': job.priority || 'Standard',
        'Job Type': job.job_type,
        'Brand': job.brand,
        'State': job.state,
        'Postcode': job.postcode,
        'Referral Source': job.referral_source || '',
        'Date In': job.date_in,
        'Date Finished': job.date_finished || '',
        'Total Amount': job.total_amount || 0,
        'GST': job.gst_amount || 0,
        'Subtotal': job.subtotal || 0
      }));
      exportToCSV(data, 'jobs_report');
    } catch (error) {
      console.error('Export report error:', error);
      toast.error('Failed to create report');
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D', '#C49BFF', '#FF9999'];

  // Phase 2 — prefer server-side aggregation when available, fall back to client-side compute
  // until distributionData arrives. Keeps the page reactive on first paint.
  const distHas = !!distributionData;
  const distList = (key) => (distributionData?.[key] || []).map(b => ({ name: b.name, value: b.count, revenue: b.revenue }));

  // Cash-basis flag: are the summary cards showing money that actually landed
  // in the period? (Jul 2026 — user runs a cash business, wants all revenue
  // numbers to match the bank feed, not "jobs booked in period that later got
  // paid"). Backend returns both a booking-basis `summary`/`finished_paid` AND
  // a cash-basis `cash_summary`/`cash_finished_paid` on `/reports/distribution`.
  const cashSum  = distributionData?.cash_summary || {};
  const cashFin  = distributionData?.cash_finished_paid || {};
  const useCash  = !!distributionData?.cash_summary; // graceful fallback if backend hasn't upgraded

  // Feb 2026 — Server-computed cash-basis status breakdown for the
  // "Revenue by Status" table. Keyed by status string. Ensures rows sum to
  // the cash-basis summary cards above without needing the 17k-job payload
  // on the client (Reports 'financial' tab intentionally skips fetchJobs).
  const cashByStatusMap = useMemo(() => {
    const rows = distributionData?.cash_by_status || [];
    const m = {};
    rows.forEach(r => { m[r.status || 'Unknown'] = r; });
    return m;
  }, [distributionData]);

  const revenue  = useCash ? (cashSum.total_revenue  || 0)
                  : (distHas ? (distributionData.summary?.total_revenue || 0) : calculateRevenue(filteredJobs));
  const gst      = useCash ? (cashSum.total_gst      || 0)
                  : (distHas ? (distributionData.summary?.total_gst     || 0) : calculateGST(filteredJobs));
  const subtotal = revenue - gst;

  const comparisonRevenue = compareEnabled ? calculateRevenue(comparisonJobs) : 0;
  const revenueChange = compareEnabled && comparisonRevenue > 0
    ? ((revenue - comparisonRevenue) / comparisonRevenue * 100).toFixed(1)
    : 0;

  const statusData   = distHas ? distList('by_status')          : getJobsByCategory(filteredJobs, 'status');
  const typeData     = distHas ? distList('by_type')            : getJobsByCategory(filteredJobs, 'job_type');
  const brandData    = distHas ? distList('by_brand')           : getJobsByCategory(filteredJobs, 'brand');
  const stateData    = distHas ? distList('by_state')           : getJobsByCategory(filteredJobs, 'state');
  const postcodeData = distHas
    ? (distributionData.top_postcodes || []).map(b => ({ name: b.name, value: b.count, revenue: b.revenue }))
    : getTopPostcodes(filteredJobs);
  const referralData = distHas ? distList('by_referral_source') : getJobsByCategory(filteredJobs, 'referral_source');
  const priorityData = distHas ? distList('by_priority')        : getJobsByCategory(filteredJobs, 'priority');

  // Cap chart-rendered referral sources to top 10 + "Other" so massive datasets
  // (e.g. Jim2 imports with 50+ named partners + event sources) don't blow out
  // the pie/bar legend. The full granular breakdown stays available in the
  // "Source Performance Details" table below the charts.
  const TOP_REFERRAL_LIMIT = 10;
  const referralChartData = (() => {
    if (!referralSourceData || !referralSourceData.sources?.length) return [];
    const normalised = referralSourceData.sources.map(s => ({ ...s, source: s.source || 'Not specified' }));
    if (normalised.length <= TOP_REFERRAL_LIMIT) return normalised;
    const sorted = [...normalised].sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0));
    const top = sorted.slice(0, TOP_REFERRAL_LIMIT);
    const rest = sorted.slice(TOP_REFERRAL_LIMIT);
    const otherCount = rest.reduce((s, r) => s + (r.count || 0), 0);
    const otherRevenue = rest.reduce((s, r) => s + (r.total_revenue || 0), 0);
    const totalRevenue = referralSourceData.total_revenue || (otherRevenue + top.reduce((s, r) => s + (r.total_revenue || 0), 0));
    return [
      ...top,
      {
        source: `Other (${rest.length} sources)`,
        count: otherCount,
        total_revenue: otherRevenue,
        avg_revenue: otherCount > 0 ? otherRevenue / otherCount : 0,
        percentage: totalRevenue > 0 ? (otherRevenue / totalRevenue) * 100 : 0,
      },
    ];
  })();

  // Feb 2026 — cash-basis-scoped subset of jobs used by the drill-in cards on
  // the Job Distribution / By Location / Priority tabs. Those tabs' summary
  // cards come from /api/reports/distribution with date_field=revenue_date
  // (jobs whose PAYMENT landed in the period). But `filteredJobs` is
  // client-filtered by `date_in`, which caused a user-reported mismatch: the
  // Jobs by Status card said "Not Recoverable 6 · $1774" while expanding the
  // row only showed 1 job for $660 (jobs booked in June but paid in July were
  // included in the aggregate but excluded from the drill). This memo keeps
  // the drill on the same cash-basis grain as the card.
  const cashFilteredJobs = useMemo(() => {
    if (!jobs.length) return [];
    if (dateRange === 'all' || !dateRange) return jobs.filter(j => !!j.revenue_date);
    const { start: startStr, end: endStr } = getDateBoundaries(dateRange, startDate, endDate);
    if (!startStr || !endStr) return jobs.filter(j => !!j.revenue_date);
    return jobs.filter(j => j.revenue_date && j.revenue_date >= startStr && j.revenue_date <= endStr);
  }, [jobs, dateRange, startDate, endDate]);

  // Convenience: O(1) lookup for revenue per chart bucket — replaces O(N) filteredJobs.filter(...) + reduce
  const revenueFor = (key, name) => {
    const list = distributionData?.[key];
    const hit = list && list.find(b => b.name === name);
    if (hit) return hit.revenue || 0;
    // Fallback to client-side compute keyed on the job field
    const fieldMap = {
      by_status: 'status', by_type: 'job_type', by_brand: 'brand', by_state: 'state',
      by_referral_source: 'referral_source', by_priority: 'priority', by_payment_status: 'payment_status',
    };
    const field = fieldMap[key];
    if (!field) return 0;
    return calculateRevenue(filteredJobs.filter(j => (j[field] || 'Unknown') === name));
  };
  
  // Calculate customer type data
  const customerTypeData = () => {
    const counts = {};
    customers.forEach(customer => {
      const type = customer.customer_type || 'Not Specified';
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  };
  const customerTypesData = customerTypeData();

  // Feb 2026 — only show the fullscreen spinner during the initial page
  // load (before any data has come back). Later job fetches are lazy
  // (fetchJobs on drill-in click) and setLoading(true) would otherwise
  // unmount the whole page for 5-8s mid-interaction — a jarring UX when
  // clicking Jobs In rows. Once distributionData has loaded the page is
  // safe to render; per-section spinners inside each Card cover the rest.
  if (loading && !distributionData && jobs.length === 0) {
    return <div className="loading">Loading reports...</div>;
  }

  return (
    <div className="reports-container">
      {/* Print-only header — shows the active date range on printed reports */}
      <div className="print-only" style={{ display: 'none' }}>
        <h1 style={{ margin: 0, fontSize: '20px' }}>Reports & Analytics</h1>
        <p style={{ margin: '4px 0 0 0', color: '#475569', fontSize: '13px' }}>
          Date Range: <strong>{dateRangeLabel()}</strong>
          {(() => {
            const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
            return start && end ? <span> ({start} to {end})</span> : null;
          })()}
          {' · Generated '}{new Date().toLocaleString()}
        </p>
      </div>
      <style>{`
        @media print {
          .print-only { display: block !important; padding: 0 0 16px 0; border-bottom: 2px solid #0f172a; margin-bottom: 16px; }
          .reports-tabs [role="tablist"],
          .date-range-buttons,
          .export-buttons,
          .comparison-toggle,
          .custom-date-inputs { display: none !important; }
        }
      `}</style>
      <Card>
        <CardHeader>
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            Reports & Analytics
            <span
              title="Job counts include all jobs (incl. $0 Not Proceeding / Not Recoverable). Revenue $$ is cash basis — only counts amounts received (matches Jim2). BAS / P&L / Revenue Trends are pure cash basis."
              style={{
                fontSize: '0.7rem',
                padding: '2px 10px',
                borderRadius: '999px',
                backgroundColor: '#dcfce7',
                color: '#166534',
                border: '1px solid #86efac',
                fontWeight: 600,
                letterSpacing: '0.04em',
              }}
              data-testid="cash-basis-badge"
            >
              CASH BASIS
            </span>
          </CardTitle>
          <CardDescription>Comprehensive job and financial reporting — All jobs counted; revenue recognised when paid (matches Jim2)</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Date Range Selector */}
          <div className="report-filters">
            <div className="date-range-selector">
              <Label>Date Range</Label>
              <div className="date-range-buttons">
                <Button
                  variant={dateRange === 'all' ? 'default' : 'outline'}
                  onClick={() => setDateRange('all')}
                  data-testid="date-range-all"
                >
                  All Time
                </Button>
                <Button
                  variant={dateRange === 'this_month' ? 'default' : 'outline'}
                  onClick={() => setDateRange('this_month')}
                  data-testid="date-range-this-month"
                >
                  This Month
                </Button>
                <Button
                  variant={dateRange === 'last_month' ? 'default' : 'outline'}
                  onClick={() => setDateRange('last_month')}
                  data-testid="date-range-last-month"
                >
                  Last Month
                </Button>
                <Button
                  variant={dateRange === 'this_quarter' ? 'default' : 'outline'}
                  onClick={() => setDateRange('this_quarter')}
                  data-testid="date-range-this-quarter"
                >
                  This Quarter
                </Button>
                <Button
                  variant={dateRange === 'ytd' ? 'default' : 'outline'}
                  onClick={() => setDateRange('ytd')}
                  data-testid="date-range-ytd"
                >
                  Year to Date
                </Button>
                <Button
                  variant={dateRange === 'last7' ? 'default' : 'outline'}
                  onClick={() => setDateRange('last7')}
                  data-testid="date-range-last7"
                >
                  Last 7 Days
                </Button>
                <Button
                  variant={dateRange === 'last30' ? 'default' : 'outline'}
                  onClick={() => setDateRange('last30')}
                  data-testid="date-range-last30"
                >
                  Last 30 Days
                </Button>
                <Button
                  variant={dateRange === 'custom' ? 'default' : 'outline'}
                  onClick={() => setDateRange('custom')}
                  data-testid="date-range-custom"
                >
                  Custom Range
                </Button>
              </div>

              {/* Feb 2026 — "Preset resolves to: 1 Jul → 17 Jul" hint. Shown for
                  every non-custom preset so the user always knows the exact
                  dates the button just selected. Prevents silent surprises. */}
              {dateRange !== 'custom' && (() => {
                const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
                if (!start && !end) return null;
                const fmt = (iso) => {
                  if (!iso) return '';
                  const [y, m, d] = iso.split('-');
                  const monthName = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m, 10) - 1] || m;
                  return `${parseInt(d, 10)} ${monthName} ${y}`;
                };
                return (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }} data-testid="date-range-preview">
                    <span style={{ fontWeight: 600, color: '#475569' }}>📅 Showing:</span>
                    <span>{start ? fmt(start) : 'earliest'}</span>
                    <span>→</span>
                    <span>{end ? fmt(end) : 'today'}</span>
                  </div>
                );
              })()}

              {dateRange === 'custom' && (
                <div className="custom-date-inputs">
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="comparison-toggle">
                <Label>
                  <input
                    type="checkbox"
                    checked={compareEnabled}
                    onChange={(e) => setCompareEnabled(e.target.checked)}
                  />
                  {' '}Compare to Previous Period
                  {/* Show a small loading hint when the user toggles compare on
                      and the jobs array is still being lazy-fetched — without
                      this the UI looks broken for a few seconds on wide date
                      ranges (17k jobs). */}
                  {compareEnabled && jobs.length === 0 && loading && (
                    <span style={{ marginLeft: '0.5rem', color: '#64748b', fontSize: '0.85rem' }}>
                      Loading comparison data…
                    </span>
                  )}
                </Label>
              </div>
            </div>

            <div className="export-buttons">
              <Button onClick={exportJobsReport} variant="outline">
                Export to CSV
              </Button>
              <Button 
                onClick={() => {
                  try {
                    window.print();
                  } catch (error) {
                    console.error('Print error:', error);
                    toast.error('Print function may be blocked by your browser');
                  }
                }} 
                variant="outline"
              >
                Print Report
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="reports-tabs">
            <TabsList style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              height: 'auto', 
              gap: '0.25rem',
              padding: '0.5rem',
              justifyContent: 'flex-start'
            }}>
              <TabsTrigger value="financial">Financial</TabsTrigger>
              <TabsTrigger value="profit-loss">Profit & Loss</TabsTrigger>
              <TabsTrigger value="expense-report">Expense Report</TabsTrigger>
              <TabsTrigger value="bas-report">BAS Report</TabsTrigger>
              <TabsTrigger value="payment">Payment Status</TabsTrigger>
              <TabsTrigger value="distribution">Job Distribution</TabsTrigger>
              <TabsTrigger value="location">By Location</TabsTrigger>
              <TabsTrigger value="referrals">Referral Sources</TabsTrigger>
              <TabsTrigger value="cash-by-source" data-testid="cash-by-source-tab">Cash by Source</TabsTrigger>
              <TabsTrigger value="jobs-in" data-testid="jobs-in-tab">📥 Jobs In</TabsTrigger>
              <TabsTrigger value="referral-partners" data-testid="referral-partners-tab">Referral Partners</TabsTrigger>
              <TabsTrigger value="priority">Priority</TabsTrigger>
              <TabsTrigger value="customer-types">Customer Types</TabsTrigger>
              <TabsTrigger value="stock-summary">Stock Summary</TabsTrigger>
              <TabsTrigger value="stock-analysis" data-testid="stock-analysis-tab">📦 Stock Analysis</TabsTrigger>
              <TabsTrigger value="stock-reconciliation" data-testid="stock-reconciliation-tab">⚖️ Stock Reconciliation</TabsTrigger>
              <TabsTrigger value="review-requests" data-testid="review-requests-tab">⭐ Review Requests</TabsTrigger>
              <TabsTrigger value="job-type-analysis" data-testid="job-type-analysis-tab">🔬 Job Type Analysis</TabsTrigger>
              <TabsTrigger value="supplier-spending">Supplier Spending</TabsTrigger>
              <TabsTrigger value="expenses-by-supplier" data-testid="expenses-by-supplier-tab">🏢 Expenses By Supplier</TabsTrigger>
              <TabsTrigger value="details">Job Details</TabsTrigger>
            </TabsList>

            {/* Tab date-basis legend (Feb 2026) — every Reports tab is filtered
                either by the day the job was BOOKED (date_in) or by the day
                the cash actually LANDED (revenue_date). The wrong mental model
                caused a confusing $25k vs $6k discrepancy for the user, so
                this persistent chip surfaces the basis inline as they switch
                tabs — no more silent-filter guessing. */}
            {(() => {
              // Booking-date tabs = "of jobs booked in the period"; Payment-date
              // tabs = "of cash that landed in the period". Grouping by intent
              // keeps the mental model clean when a new tab is added.
              //
              // Note (Feb 2026 correction): Job Distribution + By Location +
              // Priority all pull from the CASH-BASIS aggregation
              // (/api/reports/distribution with date_field=revenue_date). Only
              // paid jobs are counted; unpaid Booked/Quoted/etc. are excluded.
              // Only Payment Status and (the new 2-box) Referral Sources +
              // Customer Types + Job Details are truly booking-date scoped
              // (they read from filteredJobs or activity_* buckets).
              const BOOKING_DATE_TABS = new Set([
                'payment-status', 'referrals', 'customer-types', 'details', 'jobs-in',
              ]);
              const isBooking = BOOKING_DATE_TABS.has(activeTab);
              const style = isBooking
                ? { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd' }   // blue = booking
                : { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }; // green = cash
              const icon = isBooking ? '📅' : '💵';
              const label = isBooking ? 'Filter: Booking date (date_in)' : 'Filter: Payment received date (revenue_date)';
              const desc = isBooking
                ? 'Counts jobs BOOKED in the selected date range, regardless of whether they are paid yet.'
                : 'Counts CASH RECEIVED in the selected date range — reconciles to your P&L, Dashboard Revenue Overview and BAS.';
              return (
                <div
                  data-testid="reports-basis-legend"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    marginTop: '0.75rem',
                    marginBottom: '0.5rem',
                    ...style,
                  }}
                  title={desc}
                >
                  <span aria-hidden>{icon}</span>
                  <span>{label}</span>
                </div>
              );
            })()}

            {/* Financial Reports Tab */}
            <TabsContent value="financial">
              <div className="financial-summary" id="financial-print-area">

                {/* Basis + GST note — reminds the user of the accounting basis
                    at a glance so figures aren't misread. Added Jul 2026 after
                    user report of "Computer Repair paid yesterday appearing in
                    Last Month" (root cause: date_in filter). */}
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.65rem 0.9rem',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderLeft: '4px solid #2563eb',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  color: '#1e3a8a',
                }} data-testid="financial-basis-note">
                  <strong>Cash-basis reporting.</strong> Revenue is counted when payment is received (matches your bank feed), not when the job was booked. All revenue figures shown <strong>include GST</strong>.
                </div>
                
                {/* Phase 3.4: Export All Reports Button */}
                <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }} className="no-print">
                  <Button 
                    onClick={exportAllReports}
                    style={{ 
                      backgroundColor: '#10b981', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    📊 Export All Reports (CSV)
                  </Button>
                  <Button
                    onClick={() => {
                      // Get the financial section content including charts
                      const financialSection = document.getElementById('financial-print-area');
                      if (!financialSection) {
                        alert('Could not find financial report content');
                        return;
                      }
                      
                      // Clone the content
                      const clonedContent = financialSection.cloneNode(true);
                      
                      // Remove no-print elements from clone
                      const noPrintElements = clonedContent.querySelectorAll('.no-print, [class*="no-print"]');
                      noPrintElements.forEach(el => el.remove());
                      
                      // Remove interactive elements (buttons, inputs, checkboxes)
                      const interactiveElements = clonedContent.querySelectorAll('button, input, select, .print-hide');
                      interactiveElements.forEach(el => el.remove());
                      
                      const contentHtml = clonedContent.innerHTML;
                      
                      // Create print window with full styling for charts
                      const printContent = `
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>Financial Report - Print</title>
                          <style>
                            * {
                              box-sizing: border-box;
                              margin: 0;
                              padding: 0;
                            }
                            body {
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                              padding: 20px;
                              color: #1a1a1a;
                              line-height: 1.5;
                              background: white;
                            }
                            .print-header {
                              text-align: center;
                              margin-bottom: 20px;
                              padding-bottom: 15px;
                              border-bottom: 2px solid #333;
                            }
                            .print-header h1 {
                              font-size: 24px;
                              margin-bottom: 5px;
                            }
                            .print-header .print-date {
                              color: #666;
                              font-size: 12px;
                            }
                            .print-actions {
                              text-align: center;
                              margin-bottom: 20px;
                              padding: 15px;
                              background: #f0f0f0;
                              border-radius: 8px;
                            }
                            .print-actions .instruction {
                              font-size: 18px;
                              font-weight: 600;
                              color: #1a1a1a;
                              margin-bottom: 10px;
                            }
                            .print-actions .shortcut {
                              display: inline-block;
                              background: #6366f1;
                              color: white;
                              padding: 8px 16px;
                              border-radius: 6px;
                              font-family: monospace;
                              font-size: 16px;
                              font-weight: bold;
                              margin: 5px;
                            }
                            .print-actions .hint {
                              font-size: 13px;
                              color: #666;
                              margin-top: 10px;
                            }
                            .print-actions button {
                              padding: 10px 24px;
                              font-size: 14px;
                              cursor: pointer;
                              border: none;
                              border-radius: 6px;
                              margin-top: 15px;
                              background: #e5e7eb;
                              color: #374151;
                            }
                            .print-actions button:hover {
                              background: #d1d5db;
                            }
                            
                            /* Summary cards - Revenue Summary styling */
                            .summary-cards {
                              display: grid;
                              grid-template-columns: repeat(4, 1fr);
                              gap: 1rem;
                              margin-bottom: 1.5rem;
                            }
                            .summary-card {
                              padding: 1rem;
                              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                              color: white;
                              border-radius: 0.5rem;
                              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                            }
                            .summary-card.finished-jobs-card {
                              background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                            }
                            .summary-label {
                              font-size: 0.75rem;
                              opacity: 0.9;
                              margin-bottom: 0.25rem;
                            }
                            .summary-value {
                              font-size: 1.25rem;
                              font-weight: 700;
                            }
                            .summary-change {
                              font-size: 0.7rem;
                              margin-top: 0.25rem;
                            }
                            .summary-change.positive { color: #86efac; }
                            .summary-change.negative { color: #fca5a5; }
                            
                            /* Preserve chart styles */
                            .recharts-wrapper {
                              margin: 0 auto;
                            }
                            svg {
                              max-width: 100%;
                              height: auto;
                            }
                            
                            /* Card styles */
                            .financial-summary > div,
                            .financial-content > div {
                              margin-bottom: 1.5rem;
                            }
                            
                            /* Table styles */
                            table {
                              width: 100%;
                              border-collapse: collapse;
                              margin: 10px 0;
                              font-size: 11px;
                            }
                            th, td {
                              border: 1px solid #ddd;
                              padding: 6px 8px;
                              text-align: left;
                            }
                            th {
                              background: #f3f4f6;
                              font-weight: 600;
                            }
                            
                            /* Hide scrollbars and overflow content */
                            * {
                              overflow: visible !important;
                            }
                            
                            /* Page break hints */
                            .recharts-responsive-container {
                              page-break-inside: avoid;
                            }
                            
                            @media print {
                              .print-actions {
                                display: none !important;
                              }
                              body {
                                padding: 0;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                              }
                              svg {
                                max-width: 100%;
                              }
                            }
                          </style>
                        </head>
                        <body>
                          <div class="print-header">
                            <h1>Financial Report</h1>
                            <div class="print-date">Printed: ${new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                          
                          <div class="print-actions">
                            <div class="instruction">To print this report with all charts, press:</div>
                            <span class="shortcut">Ctrl + P</span> <span style="color: #666;">or</span> <span class="shortcut">⌘ + P</span>
                            <div class="hint">Tip: Select "Save as PDF" to keep a digital copy with charts.</div>
                            <br>
                            <button onclick="window.close()">Close Window</button>
                          </div>
                          
                          <div class="financial-content">
                            ${contentHtml}
                          </div>
                        </body>
                        </html>
                      `;
                      
                      const blob = new Blob([printContent], { type: 'text/html' });
                      const blobUrl = URL.createObjectURL(blob);
                      const printWindow = window.open(blobUrl, '_blank', 'width=1100,height=800');
                      
                      if (!printWindow) {
                        alert('Please allow pop-ups to use the print feature, then try again.');
                        URL.revokeObjectURL(blobUrl);
                        return;
                      }
                      
                      printWindow.onload = function() {
                        URL.revokeObjectURL(blobUrl);
                      };
                      printWindow.focus();
                    }}
                    style={{ 
                      backgroundColor: '#6366f1', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    data-testid="print-financial-report"
                  >
                    🖨️ Print Report
                  </Button>
                </div>
                
                {/* Phase 3.1: Advanced Filtering Panel */}
                <Card style={{ marginBottom: '2rem', backgroundColor: '#f8fafc' }}>
                  <CardContent style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showFilters ? '1.5rem' : '0' }}>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1e40af' }}>
                        Advanced Filters
                      </h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? '▲ Hide Filters' : '▼ Show Filters'}
                      </Button>
                    </div>
                    
                    {showFilters && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                        {/* Custom Date Range */}
                        <div>
                          <Label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                            Start Date
                          </Label>
                          <input
                            type="date"
                            value={reportFilters.customStartDate}
                            onChange={(e) => setReportFilters({...reportFilters, customStartDate: e.target.value})}
                            style={{ 
                              width: '100%',
                              padding: '0.5rem', 
                              borderRadius: '0.375rem', 
                              border: '1px solid #d1d5db',
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                        
                        <div>
                          <Label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                            End Date
                          </Label>
                          <input
                            type="date"
                            value={reportFilters.customEndDate}
                            onChange={(e) => setReportFilters({...reportFilters, customEndDate: e.target.value})}
                            style={{ 
                              width: '100%',
                              padding: '0.5rem', 
                              borderRadius: '0.375rem', 
                              border: '1px solid #d1d5db',
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                        
                        {/* Job Type Filter */}
                        <div>
                          <Label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                            Job Type
                          </Label>
                          <select
                            value={reportFilters.jobType}
                            onChange={(e) => setReportFilters({...reportFilters, jobType: e.target.value})}
                            style={{ 
                              width: '100%',
                              padding: '0.5rem', 
                              borderRadius: '0.375rem', 
                              border: '1px solid #d1d5db',
                              fontSize: '0.875rem'
                            }}
                          >
                            <option value="">All Job Types</option>
                            {settings?.job_types?.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Referral Source Filter */}
                        <div>
                          <Label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                            Referral Source
                          </Label>
                          <select
                            value={reportFilters.referralSource}
                            onChange={(e) => setReportFilters({...reportFilters, referralSource: e.target.value})}
                            style={{ 
                              width: '100%',
                              padding: '0.5rem', 
                              borderRadius: '0.375rem', 
                              border: '1px solid #d1d5db',
                              fontSize: '0.875rem'
                            }}
                          >
                            <option value="">All Sources</option>
                            {settings?.referral_sources?.map((source) => (
                              <option key={source} value={source}>{source}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Payment Status Filter */}
                        <div>
                          <Label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                            Payment Status
                          </Label>
                          <select
                            value={reportFilters.paymentStatus}
                            onChange={(e) => setReportFilters({...reportFilters, paymentStatus: e.target.value})}
                            style={{ 
                              width: '100%',
                              padding: '0.5rem', 
                              borderRadius: '0.375rem', 
                              border: '1px solid #d1d5db',
                              fontSize: '0.875rem'
                            }}
                          >
                            <option value="">All Payment Status</option>
                            <option value="Paid">Paid</option>
                            <option value="Unpaid">Unpaid</option>
                            <option value="Partial">Partial</option>
                          </select>
                        </div>
                        
                        {/* Clear Filters Button */}
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <Button 
                            variant="outline"
                            onClick={() => {
                              setReportFilters({
                                customStartDate: '',
                                customEndDate: '',
                                jobType: '',
                                referralSource: '',
                                paymentStatus: ''
                              });
                            }}
                            style={{ width: '100%' }}
                          >
                            Clear All Filters
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Compare to Previous Period Option */}
                    {showFilters && reportFilters.customStartDate && reportFilters.customEndDate && (
                      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                        <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                          <input 
                            type="checkbox"
                            checked={compareFilters}
                            onChange={(e) => setCompareFilters(e.target.checked)}
                          />
                          Compare to Previous Period
                        </Label>
                        {compareFilters && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                            Will compare {reportFilters.customStartDate} to {reportFilters.customEndDate} with the equivalent previous period
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Active Filters Indicator */}
                    {(reportFilters.jobType || reportFilters.referralSource || reportFilters.paymentStatus || reportFilters.customStartDate) && (
                      <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#e0f2fe', borderRadius: '0.375rem', fontSize: '0.875rem' }}>
                        <strong>Active Filters:</strong>
                        {reportFilters.customStartDate && reportFilters.customEndDate && (
                          <span style={{ marginLeft: '0.5rem' }}>Date: {reportFilters.customStartDate} to {reportFilters.customEndDate}</span>
                        )}
                        {reportFilters.jobType && (
                          <span style={{ marginLeft: '0.5rem' }}>Job Type: {reportFilters.jobType}</span>
                        )}
                        {reportFilters.referralSource && (
                          <span style={{ marginLeft: '0.5rem' }}>Source: {reportFilters.referralSource}</span>
                        )}
                        {reportFilters.paymentStatus && (
                          <span style={{ marginLeft: '0.5rem' }}>Payment: {reportFilters.paymentStatus}</span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Phase 3.2: Revenue Trends with Period-over-Period Comparison */}
                {revenueTrends && revenueTrends.length > 0 && (
                  <Card style={{ marginBottom: '2rem' }}>
                    <CardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <CardTitle>Revenue Trends ({dateRange === 'all' ? 'Last 12 Months' : (
                          { last7: 'Last 7 Days', last30: 'Last 30 Days', this_month: 'This Month', last_month: 'Last Month', this_quarter: 'This Quarter', ytd: 'Year to Date', custom: 'Custom Range' }[dateRange] || 'Selected Period'
                        )})</CardTitle>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          {/* Phase 3.4: Export Button */}
                          <Button 
                            onClick={exportRevenueTrends}
                            size="sm"
                            variant="outline"
                            style={{ fontSize: '0.75rem' }}
                          >
                            📥 Export CSV
                          </Button>
                          
                          {/* Comparison Toggle */}
                          <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                            <input 
                              type="checkbox"
                              checked={showComparison}
                              onChange={(e) => setShowComparison(e.target.checked)}
                            />
                            Show Comparison
                          </Label>
                          
                          {/* Comparison Type Toggle */}
                          {showComparison && (
                            <div style={{ display: 'flex', gap: '0.5rem', padding: '0.25rem', backgroundColor: '#f3f4f6', borderRadius: '0.375rem' }}>
                              <button
                                onClick={() => setComparisonType('period')}
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  fontSize: '0.875rem',
                                  borderRadius: '0.25rem',
                                  border: 'none',
                                  cursor: 'pointer',
                                  backgroundColor: comparisonType === 'period' ? '#3b82f6' : 'transparent',
                                  color: comparisonType === 'period' ? 'white' : '#374151',
                                  fontWeight: comparisonType === 'period' ? '600' : '400'
                                }}
                              >
                                vs Previous Period
                              </button>
                              <button
                                onClick={() => setComparisonType('year')}
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  fontSize: '0.875rem',
                                  borderRadius: '0.25rem',
                                  border: 'none',
                                  cursor: 'pointer',
                                  backgroundColor: comparisonType === 'year' ? '#3b82f6' : 'transparent',
                                  color: comparisonType === 'year' ? 'white' : '#374151',
                                  fontWeight: comparisonType === 'year' ? '600' : '400'
                                }}
                              >
                                vs Previous Year
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {!showComparison ? (
                        // Single chart when comparison is off
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart data={revenueTrends}>
                            <defs>
                              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                            <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRevenue)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        // Two side-by-side charts when comparison is on
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                          {/* Current Period Chart */}
                          <div>
                            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                              Current Period
                            </h4>
                            <ResponsiveContainer width="100%" height={280}>
                              <AreaChart data={revenueTrends}>
                                <defs>
                                  <linearGradient id="colorRevenueCurrent" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" style={{ fontSize: '0.75rem' }} />
                                <YAxis style={{ fontSize: '0.75rem' }} />
                                <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                                <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRevenueCurrent)" />
                              </AreaChart>
                            </ResponsiveContainer>
                            {/* Current Period Stats */}
                            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '0.375rem' }}>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total Revenue</div>
                              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                                ${revenueTrends.reduce((sum, item) => sum + item.revenue, 0).toFixed(2)}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                {revenueTrends.reduce((sum, item) => sum + item.jobs, 0)} Jobs
                              </div>
                            </div>
                          </div>

                          {/* Previous Period Chart */}
                          <div>
                            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                              {comparisonType === 'year' ? 'Previous Year (Same Period)' : 'Previous Period'}
                            </h4>
                            {previousPeriodRevenueTrends && previousPeriodRevenueTrends.length > 0 ? (
                              <>
                                <ResponsiveContainer width="100%" height={280}>
                                  <AreaChart data={previousPeriodRevenueTrends}>
                                    <defs>
                                      <linearGradient id="colorRevenuePrevious" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                      </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" style={{ fontSize: '0.75rem' }} />
                                    <YAxis style={{ fontSize: '0.75rem' }} />
                                    <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                                    <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fillOpacity={1} fill="url(#colorRevenuePrevious)" />
                                  </AreaChart>
                                </ResponsiveContainer>
                                {/* Previous Period Stats with Comparison */}
                                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#eff6ff', borderRadius: '0.375rem' }}>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total Revenue</div>
                                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                                    ${previousPeriodRevenueTrends.reduce((sum, item) => sum + item.revenue, 0).toFixed(2)}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                    {previousPeriodRevenueTrends.reduce((sum, item) => sum + item.jobs, 0)} Jobs
                                  </div>
                                  {/* Percentage Change Indicator */}
                                  {(() => {
                                    const currentTotal = revenueTrends.reduce((sum, item) => sum + item.revenue, 0);
                                    const previousTotal = previousPeriodRevenueTrends.reduce((sum, item) => sum + item.revenue, 0);
                                    const change = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal * 100) : 0;
                                    const isPositive = change >= 0;
                                    return (
                                      <div style={{ 
                                        marginTop: '0.5rem', 
                                        fontSize: '0.875rem', 
                                        fontWeight: '600',
                                        color: isPositive ? '#10b981' : '#ef4444'
                                      }}>
                                        {isPositive ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% 
                                        <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '0.25rem' }}>
                                          vs {comparisonType === 'year' ? 'last year' : 'previous period'}
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '280px', color: '#9ca3af' }}>
                                No data available for comparison period
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Phase 3.4: Cumulative Revenue Chart */}
                {revenueTrends && revenueTrends.length > 0 && (
                  <Card style={{ marginBottom: '2rem' }}>
                    <CardHeader>
                      <CardTitle>Cumulative Revenue Growth</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={350}>
                        <AreaChart data={revenueTrends.map((item, index, arr) => {
                          const cumulative = arr.slice(0, index + 1).reduce((sum, i) => sum + i.revenue, 0);
                          return {
                            ...item,
                            cumulative_revenue: cumulative
                          };
                        })}>
                          <defs>
                            <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip 
                            formatter={(value, name) => {
                              if (name === 'Cumulative Revenue') return [`$${value.toFixed(2)}`, 'Cumulative Revenue'];
                              return [`$${value.toFixed(2)}`, name];
                            }}
                          />
                          <Legend />
                          <Area 
                            type="monotone" 
                            dataKey="cumulative_revenue" 
                            stroke="#3b82f6" 
                            fillOpacity={1} 
                            fill="url(#colorCumulative)" 
                            name="Cumulative Revenue"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                      <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
                        Total Cumulative Revenue: ${revenueTrends.reduce((sum, item) => sum + item.revenue, 0).toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Referral Source Breakdown */}
                {referralSourceData && referralSourceData.sources.length > 0 && (
                  <Card style={{ marginBottom: '2rem' }}>
                    <CardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <CardTitle>Revenue by Referral Source</CardTitle>
                        {/* Phase 3.4: Export Button */}
                        <Button 
                          onClick={exportReferralSourceAnalysis}
                          size="sm"
                          variant="outline"
                          style={{ fontSize: '0.75rem' }}
                        >
                          📥 Export CSV
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        {/* Pie Chart */}
                        <div style={{ position: 'relative', zIndex: 1 }}>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={referralChartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({source, percentage}) => `${source}: ${percentage.toFixed(1)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="total_revenue"
                                nameKey="source"
                              >
                                {referralChartData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value, name) => [`$${value.toFixed(2)}`, name || 'Not specified']}
                                contentStyle={{ 
                                  backgroundColor: 'white', 
                                  border: '1px solid #ccc', 
                                  borderRadius: '4px',
                                  padding: '8px',
                                  zIndex: 1000
                                }}
                                wrapperStyle={{ zIndex: 1000 }}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Bar Chart */}
                        <div style={{ position: 'relative', zIndex: 1 }}>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={referralChartData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="source" interval={0} angle={-25} textAnchor="end" height={60} tick={{ fontSize: 11 }} />
                              <YAxis />
                              <Tooltip 
                                formatter={(value, name) => [`$${value.toFixed(2)}`, 'Total Revenue']}
                                contentStyle={{ 
                                  backgroundColor: 'white', 
                                  border: '1px solid #ccc', 
                                  borderRadius: '4px',
                                  padding: '8px',
                                  zIndex: 1000
                                }}
                                wrapperStyle={{ zIndex: 1000 }}
                              />
                              <Bar dataKey="total_revenue" name="Total Revenue">
                                {referralChartData.map((entry, index) => (
                                  <Cell key={`bar-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Referral Source Table with Period Comparison */}
                      <div style={{ marginTop: '2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>Source Performance Details</h3>
                          {dateRange !== 'all' && (
                            <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input 
                                type="checkbox"
                                checked={sourceCompare}
                                onChange={(e) => setSourceCompare(e.target.checked)}
                              />
                              Compare with Previous Period
                            </Label>
                          )}
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Source</TableHead>
                              <TableHead>Jobs</TableHead>
                              <TableHead>Revenue</TableHead>
                              <TableHead>Avg Job Value</TableHead>
                              <TableHead>% of Total</TableHead>
                              {sourceCompare && previousSourceData && (
                                <TableHead>Change</TableHead>
                              )}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {referralSourceData.sources.map((source, srcIndex) => {
                              const prevSource = sourceCompare && previousSourceData
                                ? previousSourceData.sources.find(p => p.source === source.source)
                                : null;
                              const revenueChange = prevSource
                                ? ((source.total_revenue - prevSource.total_revenue) / prevSource.total_revenue * 100).toFixed(1)
                                : null;
                              
                              return (
                                <TableRow key={source.source}>
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[srcIndex % COLORS.length], marginRight: '8px', borderRadius: '2px', verticalAlign: 'middle' }}></span>
                                    <strong>{source.source || 'Not specified'}</strong>
                                  </TableCell>
                                  <TableCell>{source.count}</TableCell>
                                  <TableCell>
                                    <strong style={{ color: '#10b981' }}>
                                      ${source.total_revenue.toFixed(2)}
                                    </strong>
                                  </TableCell>
                                  <TableCell>${source.avg_revenue.toFixed(2)}</TableCell>
                                  <TableCell>
                                    <span style={{ 
                                      padding: '0.25rem 0.75rem',
                                      borderRadius: '0.375rem',
                                      backgroundColor: '#dbeafe',
                                      fontWeight: '600'
                                    }}>
                                      {source.percentage.toFixed(1)}%
                                    </span>
                                  </TableCell>
                                  {sourceCompare && previousSourceData && (
                                    <TableCell>
                                      {revenueChange !== null ? (
                                        <span style={{ 
                                          color: parseFloat(revenueChange) >= 0 ? '#10b981' : '#ef4444',
                                          fontWeight: '600'
                                        }}>
                                          {parseFloat(revenueChange) >= 0 ? '↑' : '↓'} {Math.abs(parseFloat(revenueChange))}%
                                        </span>
                                      ) : (
                                        <span style={{ color: '#6b7280' }}>New</span>
                                      )}
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle>Revenue Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="summary-cards">
                      <div 
                        className="summary-card clickable-card"
                        onClick={() => setSelectedFilter({ category: 'all_jobs', value: 'All Jobs' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="summary-label">Total Revenue</div>
                        <div className="summary-value">${revenue.toFixed(2)}</div>
                        {/* Phase 3.2: Show comparison from revenue trends */}
                        {showComparison && revenueTrends && previousPeriodRevenueTrends && previousPeriodRevenueTrends.length > 0 && (() => {
                          const currentTotal = revenueTrends.reduce((sum, item) => sum + item.revenue, 0);
                          const previousTotal = previousPeriodRevenueTrends.reduce((sum, item) => sum + item.revenue, 0);
                          const change = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal * 100) : 0;
                          const isPositive = change >= 0;
                          return (
                            <div style={{ 
                              marginTop: '0.5rem',
                              fontSize: '0.875rem', 
                              fontWeight: '600',
                              color: isPositive ? '#10b981' : '#ef4444'
                            }}>
                              {isPositive ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% vs {comparisonType === 'year' ? 'last year' : 'previous period'}
                            </div>
                          );
                        })()}
                        {/* Legacy comparison (if compareEnabled is used elsewhere) */}
                        {!showComparison && compareEnabled && (
                          <div className={`summary-change ${revenueChange >= 0 ? 'positive' : 'negative'}`}>
                            {revenueChange >= 0 ? '↑' : '↓'} {Math.abs(revenueChange)}% vs previous
                          </div>
                        )}
                      </div>
                      <div 
                        className="summary-card clickable-card"
                        onClick={() => setSelectedFilter({ category: 'all_jobs', value: 'All Jobs' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="summary-label">GST Collected</div>
                        <div className="summary-value">${gst.toFixed(2)}</div>
                      </div>
                      <div 
                        className="summary-card clickable-card"
                        onClick={() => setSelectedFilter({ category: 'all_jobs', value: 'All Jobs' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="summary-label">Subtotal (ex GST)</div>
                        <div className="summary-value">${subtotal.toFixed(2)}</div>
                      </div>
                      <div 
                        className="summary-card clickable-card"
                        onClick={() => setSelectedFilter({ category: 'all_jobs', value: 'All Jobs' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="summary-label">Total Jobs</div>
                        {/* Cash-basis: count of jobs with a revenue_date in
                            the period (i.e. jobs that generated $$ this
                            period). Divisor for Total Revenue → Avg Job Value. */}
                        <div className="summary-value">{useCash ? (cashSum.total_jobs ?? 0) : (distHas ? (distributionData.summary?.total_jobs ?? filteredJobs.length) : filteredJobs.length)}</div>
                        {/* Phase 3.2: Show job count comparison */}
                        {showComparison && revenueTrends && previousPeriodRevenueTrends && previousPeriodRevenueTrends.length > 0 && (() => {
                          const currentJobs = revenueTrends.reduce((sum, item) => sum + item.jobs, 0);
                          const previousJobs = previousPeriodRevenueTrends.reduce((sum, item) => sum + item.jobs, 0);
                          const change = previousJobs > 0 ? ((currentJobs - previousJobs) / previousJobs * 100) : 0;
                          const isPositive = change >= 0;
                          return (
                            <div style={{ 
                              marginTop: '0.5rem',
                              fontSize: '0.875rem', 
                              fontWeight: '600',
                              color: isPositive ? '#10b981' : '#ef4444'
                            }}>
                              {isPositive ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% vs {comparisonType === 'year' ? 'last year' : 'previous period'}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Finished Jobs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="summary-cards">
                      <div 
                        className="summary-card finished-jobs-card clickable-card"
                        onClick={() => setSelectedFilter({ category: 'finished_jobs', value: 'Finished' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="summary-label">Finished Revenue (Paid)</div>
                        <div className="summary-value">${(() => {
                          // Prefer cash-basis (revenue_date-in-range) so figure matches the bank feed.
                          if (useCash) return (cashFin.revenue || 0).toFixed(2);
                          if (distHas) return (distributionData.finished_paid?.revenue || 0).toFixed(2);
                          const finishedJobs = filteredJobs.filter(j => j.revenue_date);
                          return calculateRevenue(finishedJobs).toFixed(2);
                        })()}</div>
                      </div>
                      <div 
                        className="summary-card finished-jobs-card clickable-card"
                        onClick={() => setSelectedFilter({ category: 'finished_jobs', value: 'Finished' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="summary-label">Finished GST (Paid)</div>
                        <div className="summary-value">${(() => {
                          if (useCash) return (cashFin.gst || 0).toFixed(2);
                          if (distHas) return (distributionData.finished_paid?.gst || 0).toFixed(2);
                          const finishedJobs = filteredJobs.filter(j => j.revenue_date);
                          return finishedJobs.reduce((sum, job) => sum + parseFloat(job.gst_amount || 0), 0).toFixed(2);
                        })()}</div>
                      </div>
                      <div 
                        className="summary-card finished-jobs-card clickable-card"
                        onClick={() => setSelectedFilter({ category: 'finished_jobs', value: 'Finished' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="summary-label">Finished Subtotal (Paid)</div>
                        <div className="summary-value">${(() => {
                          if (useCash) return (cashFin.subtotal || 0).toFixed(2);
                          if (distHas) return (distributionData.finished_paid?.subtotal || 0).toFixed(2);
                          const finishedJobs = filteredJobs.filter(j => j.revenue_date);
                          return finishedJobs.reduce((sum, job) => sum + parseFloat(job.subtotal || 0), 0).toFixed(2);
                        })()}</div>
                      </div>
                      <div 
                        className="summary-card finished-jobs-card clickable-card"
                        onClick={() => setSelectedFilter({ category: 'finished_jobs', value: 'Finished' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="summary-label">Finished Jobs Count</div>
                        <div className="summary-value">{useCash ? (cashFin.count ?? 0) : (distHas ? (distributionData.finished_total?.count ?? 0) : filteredJobs.filter(j => j.status === 'Finish').length)}</div>
                      </div>
                    </div>

                    <div className="revenue-by-status">
                      <h3>Revenue by Status</h3>
                      <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
                        Cash basis — jobs whose payment landed in this period, grouped by their current status. Rows total to the Finished Revenue card above (only Finish / Not Recoverable / Not Proceeding statuses hold cash-basis revenue).
                      </p>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Status</TableHead>
                            <TableHead>Jobs</TableHead>
                            <TableHead>Revenue</TableHead>
                            <TableHead>Avg per Job</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {settings?.statuses.map(status => {
                            const row = cashByStatusMap[status] || { count: 0, revenue: 0 };
                            const statusCount = row.count || 0;
                            const statusRevenue = row.revenue || 0;
                            const avgRevenue = statusCount > 0 ? statusRevenue / statusCount : 0;
                            const isSelected = selectedFilter?.category === 'status' && selectedFilter?.value === status;
                            return (
                              <TableRow 
                                key={status}
                                onClick={() => setSelectedFilter({ category: 'status', value: status })}
                                style={{ 
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                  fontWeight: isSelected ? '600' : 'normal'
                                }}
                                className="hover:bg-gray-50"
                              >
                                <TableCell>{status}</TableCell>
                                <TableCell>{statusCount}</TableCell>
                                <TableCell>${statusRevenue.toFixed(2)}</TableCell>
                                <TableCell>${avgRevenue.toFixed(2)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Filtered Jobs Display */}
                    {(selectedFilter?.category === 'all_jobs' || selectedFilter?.category === 'finished_jobs' || selectedFilter?.category === 'status') && (
                      <Card style={{ marginTop: '2rem' }}>
                        <CardHeader>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CardTitle>
                              {selectedFilter.category === 'all_jobs' 
                                ? `All Jobs (${filteredJobs.length})`
                                : selectedFilter.category === 'finished_jobs'
                                ? `Finished Jobs (${filteredJobs.filter(j => j.status === 'Finish').length})`
                                : `Jobs with Status: ${selectedFilter.value} (${filteredJobs.filter(j => j.status === selectedFilter.value).length})`
                              }
                            </CardTitle>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setSelectedFilter(null)}
                            >
                              Clear Filter
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <SortableJobsTable
                            jobs={
                              selectedFilter.category === 'all_jobs'
                                ? filteredJobs
                                : selectedFilter.category === 'finished_jobs'
                                  ? filteredJobs.filter(j => j.status === 'Finish')
                                  : filteredJobs.filter(j => j.status === selectedFilter.value)
                            }
                            columns={[
                              { key: 'job_number',     label: 'Job #',         type: 'number', cellClassName: 'font-semibold' },
                              { key: 'customer_name',  label: 'Customer',      type: 'string' },
                              { key: 'status',         label: 'Status',        type: 'string' },
                              { key: 'job_type',       label: 'Type',          type: 'string' },
                              { key: 'date_in',        label: 'Date In',       type: 'string', render: (j) => formatDate(j.date_in) },
                              { key: 'date_finished',  label: 'Date Finished', type: 'string', render: (j) => formatDate(j.date_finished) },
                              { key: 'total_amount',   label: 'Total',         type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                              { key: 'actions',        label: 'Actions',       sortable: false, render: (j) => (
                                <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)}>View</Button>
                              ) },
                            ]}
                          />
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>

                {/* Job Type Profitability Section */}
                <div style={{ marginTop: '3rem' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e40af' }}>
                    Job Type Profitability Analysis
                  </h2>

                  {/* Compare with Previous Period toggle (date range driven by the global filter at top of page) */}
                  <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {dateRange !== 'all' && (
                      <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input 
                          type="checkbox"
                          checked={compareProfitability}
                          onChange={(e) => setCompareProfitability(e.target.checked)}
                        />
                        Compare with Previous Period
                      </Label>
                    )}
                  </div>

                  {jobTypeProfitability ? (
                    <>
                      {/* Summary Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                        <Card>
                          <CardContent style={{ padding: '1.5rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              Total Jobs
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>
                              {jobTypeProfitability.totals.total_jobs}
                            </div>
                            {compareProfitability && previousProfitability && (
                              <div style={{ fontSize: '0.875rem', color: '#059669', marginTop: '0.25rem' }}>
                                {((jobTypeProfitability.totals.total_jobs - previousProfitability.totals.total_jobs) / previousProfitability.totals.total_jobs * 100).toFixed(1)}% vs previous
                              </div>
                            )}
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent style={{ padding: '1.5rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              Total Revenue
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                              ${jobTypeProfitability.totals.total_revenue.toFixed(2)}
                            </div>
                            {compareProfitability && previousProfitability && (
                              <div style={{ fontSize: '0.875rem', color: '#059669', marginTop: '0.25rem' }}>
                                {((jobTypeProfitability.totals.total_revenue - previousProfitability.totals.total_revenue) / previousProfitability.totals.total_revenue * 100).toFixed(1)}% vs previous
                              </div>
                            )}
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent style={{ padding: '1.5rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              Total Profit
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                              ${jobTypeProfitability.totals.total_profit.toFixed(2)}
                            </div>
                            {compareProfitability && previousProfitability && (
                              <div style={{ fontSize: '0.875rem', color: '#059669', marginTop: '0.25rem' }}>
                                {((jobTypeProfitability.totals.total_profit - previousProfitability.totals.total_profit) / previousProfitability.totals.total_profit * 100).toFixed(1)}% vs previous
                              </div>
                            )}
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent style={{ padding: '1.5rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              Avg Profit Margin
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
                              {jobTypeProfitability.totals.avg_profit_margin.toFixed(1)}%
                            </div>
                            {compareProfitability && previousProfitability && (
                              <div style={{ fontSize: '0.875rem', color: '#059669', marginTop: '0.25rem' }}>
                                {(jobTypeProfitability.totals.avg_profit_margin - previousProfitability.totals.avg_profit_margin).toFixed(1)}% vs previous
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Job Type Table */}
                      <Card>
                        <CardHeader>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CardTitle>Job Type Performance</CardTitle>
                            {/* Phase 3.4: Export Button */}
                            <Button 
                              onClick={exportJobTypeProfitability}
                              size="sm"
                              variant="outline"
                              style={{ fontSize: '0.75rem' }}
                            >
                              📥 Export CSV
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Job Type</TableHead>
                                <TableHead>Jobs</TableHead>
                                <TableHead>Total Revenue</TableHead>
                                <TableHead>Avg Revenue</TableHead>
                                <TableHead>Stock Cost</TableHead>
                                <TableHead>Total Profit</TableHead>
                                <TableHead>Profit Margin</TableHead>
                                {compareProfitability && previousProfitability && (
                                  <TableHead>Change</TableHead>
                                )}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {jobTypeProfitability.job_types
                                .filter(jt => !selectedJobType || jt.job_type === selectedJobType)
                                .map((jt) => {
                                  const prevJt = compareProfitability && previousProfitability 
                                    ? previousProfitability.job_types.find(p => p.job_type === jt.job_type)
                                    : null;
                                  const revenueChange = prevJt 
                                    ? ((jt.total_revenue - prevJt.total_revenue) / prevJt.total_revenue * 100).toFixed(1)
                                    : null;
                                  
                                  return (
                                    <TableRow 
                                      key={jt.job_type}
                                      onClick={() => setSelectedJobType(selectedJobType === jt.job_type ? null : jt.job_type)}
                                      style={{ 
                                        cursor: 'pointer',
                                        backgroundColor: selectedJobType === jt.job_type ? '#e0f2fe' : 'transparent'
                                      }}
                                    >
                                      <TableCell>
                                        <strong>{jt.job_type}</strong>
                                      </TableCell>
                                      <TableCell>{jt.count}</TableCell>
                                      <TableCell>
                                        <strong style={{ color: '#10b981' }}>
                                          ${jt.total_revenue.toFixed(2)}
                                        </strong>
                                      </TableCell>
                                      <TableCell>${jt.avg_revenue.toFixed(2)}</TableCell>
                                      <TableCell style={{ color: '#ef4444' }}>
                                        ${jt.total_stock_cost.toFixed(2)}
                                      </TableCell>
                                      <TableCell>
                                        <strong style={{ color: jt.total_profit >= 0 ? '#10b981' : '#ef4444' }}>
                                          ${jt.total_profit.toFixed(2)}
                                        </strong>
                                      </TableCell>
                                      <TableCell>
                                        <span style={{ 
                                          padding: '0.25rem 0.75rem',
                                          borderRadius: '0.375rem',
                                          fontWeight: '600',
                                          backgroundColor: 
                                            jt.profit_margin >= 50 ? '#d1fae5' :
                                            jt.profit_margin >= 30 ? '#fef3c7' :
                                            jt.profit_margin >= 0 ? '#fee2e2' :
                                            '#fecaca',
                                          color:
                                            jt.profit_margin >= 50 ? '#065f46' :
                                            jt.profit_margin >= 30 ? '#92400e' :
                                            jt.profit_margin >= 0 ? '#991b1b' :
                                            '#7f1d1d'
                                        }}>
                                          {jt.profit_margin.toFixed(1)}%
                                        </span>
                                      </TableCell>
                                      {compareProfitability && previousProfitability && (
                                        <TableCell>
                                          {revenueChange !== null ? (
                                            <span style={{ 
                                              color: parseFloat(revenueChange) >= 0 ? '#10b981' : '#ef4444',
                                              fontWeight: '600'
                                            }}>
                                              {parseFloat(revenueChange) >= 0 ? '↑' : '↓'} {Math.abs(parseFloat(revenueChange))}%
                                            </span>
                                          ) : (
                                            <span style={{ color: '#6b7280' }}>New</span>
                                          )}
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                          {selectedJobType && (
                            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem' }}>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setSelectedJobType(null)}
                              >
                                Clear Filter - Show All Job Types
                              </Button>
                            </div>
                          )}

                          {/* Inline drill-in — jobs of the selected type, matched to the SAME
                              cash-basis (revenue_date) date range the Job Type aggregate uses
                              server-side. This way the drill-in count matches the row above. */}
                          {selectedJobType && (() => {
                            const jobsLoading = loading && (!jobs || jobs.length === 0);
                            const { start: rangeStart, end: rangeEnd } = getDateBoundaries(dateRange, startDate, endDate);
                            const inRange = (dateStr) => {
                              if (!rangeStart && !rangeEnd) return true;
                              if (!dateStr) return false;
                              const iso = String(dateStr).slice(0, 10);
                              if (rangeStart && iso < rangeStart) return false;
                              if (rangeEnd && iso > rangeEnd) return false;
                              return true;
                            };
                            const typeJobs = (jobs || []).filter(j =>
                              j.job_type === selectedJobType && inRange(j.revenue_date)
                            );
                            const innerFiltered = innerStatusFilter ? typeJobs.filter(j => j.status === innerStatusFilter) : typeJobs;
                            return (
                              <Card style={{ marginTop: '2rem' }}>
                                <CardHeader>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <CardTitle>
                                      Jobs of type {selectedJobType} {jobsLoading ? '(loading…)' : `(${innerFiltered.length}${innerStatusFilter ? ` of ${typeJobs.length}` : ''})`}
                                    </CardTitle>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => { setSelectedJobType(null); setInnerStatusFilter(null); }}
                                    >
                                      Clear Filter
                                    </Button>
                                  </div>
                                </CardHeader>
                                <CardContent>
                                  {jobsLoading ? (
                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading jobs…</div>
                                  ) : (
                                    <>
                                      <WorkflowHealthBar
                                        jobs={typeJobs}
                                        statusColors={settings?.status_colors || {}}
                                        label={selectedJobType}
                                      />
                                      <StatusBreakdownChips
                                        jobs={typeJobs}
                                        statusColors={settings?.status_colors || {}}
                                        value={innerStatusFilter}
                                        onChange={setInnerStatusFilter}
                                        label={selectedJobType}
                                      />
                                      <SortableJobsTable
                                        jobs={innerFiltered}
                                        columns={[
                                          { key: 'job_number',    label: 'Job #',    type: 'number', cellClassName: 'font-semibold' },
                                          { key: 'customer_name', label: 'Customer', type: 'string' },
                                          { key: 'status',        label: 'Status',   type: 'string' },
                                          { key: 'job_type',      label: 'Type',     type: 'string' },
                                          { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                                          { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                                          { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                                            <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)} data-testid={`job-type-drill-view-${j.job_number}`}>View</Button>
                                          ) },
                                        ]}
                                      />
                                    </>
                                  )}
                                </CardContent>
                              </Card>
                            );
                          })()}
                          
                          {/* Job Type Distribution Pie Charts */}
                          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1e40af' }}>
                              Job Type Distribution Analysis
                            </h3>
                            
                            {jobTypeProfitability.job_types && jobTypeProfitability.job_types.length > 0 ? (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                {/* Revenue Distribution Pie Chart */}
                                <div>
                                  <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: '#374151', textAlign: 'center' }}>
                                    Revenue Distribution by Job Type
                                  </h4>
                                  <ResponsiveContainer width="100%" height={350}>
                                    <PieChart>
                                      <Pie
                                        data={jobTypeProfitability.job_types.map(jt => ({
                                          name: jt.job_type,
                                          value: jt.total_revenue,
                                          count: jt.count,
                                          profit: jt.total_profit,
                                          margin: jt.profit_margin
                                        }))}
                                        cx="50%"
                                        cy="45%"
                                        labelLine={true}
                                        label={(entry) => entry.percent >= 0.05 ? `$${entry.value.toFixed(0)}` : ''}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                      >
                                        {jobTypeProfitability.job_types.map((entry, index) => {
                                          const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
                                          return <Cell key={`cell-revenue-${index}`} fill={colors[index % colors.length]} />;
                                        })}
                                      </Pie>
                                      <Tooltip 
                                        formatter={(value, name, props) => {
                                          return [
                                            `$${value.toFixed(2)}`,
                                            `${props.payload.name} (${props.payload.count} jobs, ${props.payload.margin.toFixed(1)}% margin)`
                                          ];
                                        }}
                                        contentStyle={{ 
                                          backgroundColor: 'white', 
                                          border: '1px solid #e5e7eb',
                                          borderRadius: '0.375rem',
                                          padding: '0.75rem',
                                          zIndex: 1000
                                        }}
                                      />
                                      <Legend 
                                        verticalAlign="bottom" 
                                        height={60}
                                        wrapperStyle={{ fontSize: '0.75rem' }}
                                        formatter={(value, entry) => {
                                          const totalRevenue = jobTypeProfitability.job_types.reduce((sum, jt) => sum + jt.total_revenue, 0);
                                          const percentage = totalRevenue > 0 ? ((entry.payload.value / totalRevenue) * 100).toFixed(1) : 0;
                                          return `${value} (${percentage}%)`;
                                        }}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                  <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                    Total Revenue: ${jobTypeProfitability.job_types.reduce((sum, jt) => sum + jt.total_revenue, 0).toFixed(2)}
                                  </div>
                                </div>

                                {/* Job Count Distribution Pie Chart */}
                                <div>
                                  <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: '#374151', textAlign: 'center' }}>
                                    Job Count Distribution by Job Type
                                  </h4>
                                  <ResponsiveContainer width="100%" height={350}>
                                    <PieChart>
                                      <Pie
                                        data={jobTypeProfitability.job_types.map(jt => ({
                                          name: jt.job_type,
                                          value: jt.count,
                                          revenue: jt.total_revenue,
                                          profit: jt.total_profit,
                                          margin: jt.profit_margin
                                        }))}
                                        cx="50%"
                                        cy="45%"
                                        labelLine={true}
                                        label={(entry) => entry.percent >= 0.05 ? `${entry.value} jobs` : ''}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                      >
                                        {jobTypeProfitability.job_types.map((entry, index) => {
                                          const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
                                          return <Cell key={`cell-count-${index}`} fill={colors[index % colors.length]} />;
                                        })}
                                      </Pie>
                                      <Tooltip 
                                        formatter={(value, name, props) => {
                                          return [
                                            `${value} jobs`,
                                            `${props.payload.name} ($${props.payload.revenue.toFixed(2)} revenue, ${props.payload.margin.toFixed(1)}% margin)`
                                          ];
                                        }}
                                        contentStyle={{ 
                                          backgroundColor: 'white', 
                                          border: '1px solid #e5e7eb',
                                          borderRadius: '0.375rem',
                                          padding: '0.75rem',
                                          zIndex: 1000
                                        }}
                                      />
                                      <Legend 
                                        verticalAlign="bottom" 
                                        height={60}
                                        wrapperStyle={{ fontSize: '0.75rem' }}
                                        formatter={(value, entry) => {
                                          const totalJobs = jobTypeProfitability.job_types.reduce((sum, jt) => sum + jt.count, 0);
                                          const percentage = totalJobs > 0 ? ((entry.payload.value / totalJobs) * 100).toFixed(1) : 0;
                                          return `${value} (${percentage}%)`;
                                        }}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                  <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                    Total Jobs: {jobTypeProfitability.job_types.reduce((sum, jt) => sum + jt.count, 0)}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                                No data available for pie charts
                              </div>
                            )}
                          </div>
                          
                          {/* Phase 3.4: Additional Visualizations */}
                          {jobTypeProfitability.job_types && jobTypeProfitability.job_types.length > 0 && (
                            <>
                              {/* Bar Chart for Job Type Revenue Comparison */}
                              <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1e40af' }}>
                                  Job Type Revenue & Profit Comparison
                                </h3>
                                <ResponsiveContainer width="100%" height={400}>
                                  <BarChart data={jobTypeProfitability.job_types}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="job_type" angle={-45} textAnchor="end" height={100} />
                                    <YAxis />
                                    <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                                    <Legend />
                                    <Bar dataKey="total_revenue" fill="#10b981" name="Total Revenue" />
                                    <Bar dataKey="total_profit" fill="#3b82f6" name="Total Profit" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>

                              {/* Combo Chart: Revenue vs Profit Margins */}
                              <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1e40af' }}>
                                  Revenue vs Profit Margin Analysis
                                </h3>
                                <ResponsiveContainer width="100%" height={400}>
                                  <ComposedChart data={jobTypeProfitability.job_types}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="job_type" angle={-45} textAnchor="end" height={100} />
                                    <YAxis yAxisId="left" />
                                    <YAxis yAxisId="right" orientation="right" />
                                    <Tooltip />
                                    <Legend />
                                    <Bar yAxisId="left" dataKey="total_revenue" fill="#10b981" name="Total Revenue ($)" />
                                    <Line yAxisId="right" type="monotone" dataKey="profit_margin" stroke="#f59e0b" strokeWidth={3} name="Profit Margin (%)" />
                                  </ComposedChart>
                                </ResponsiveContainer>
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  ) : (
                    <Card>
                      <CardContent style={{ padding: '2rem', textAlign: 'center' }}>
                        <p>Loading profitability data...</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Profit & Loss Tab */}
            <TabsContent value="profit-loss">
              <div className="profit-loss-container" data-testid="profit-loss-tab">
                <Card style={{ marginBottom: '1.5rem' }}>
                  <CardHeader>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <CardTitle>
                          Profit & Loss Statement
                          <HelpTooltip 
                            content="A detailed income statement showing revenue (from completed jobs), expenses by category, and net profit. Enable comparison to see period-over-period changes." 
                            side="right"
                          />
                        </CardTitle>
                        <CardDescription>
                          Real-time business financial overview including expenses
                          {!includeInternal && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>
                              · Internal/personal items EXCLUDED
                            </span>
                          )}
                        </CardDescription>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', padding: '0.5rem 0.75rem', backgroundColor: includeInternal ? '#fef3c7' : '#f1f5f9', border: '1px solid', borderColor: includeInternal ? '#fcd34d' : '#cbd5e1', borderRadius: '0.5rem', width: 'fit-content' }}>
                          <input
                            type="checkbox"
                            id="pl-include-internal"
                            checked={includeInternal}
                            onChange={(e) => setIncludeInternal(e.target.checked)}
                            data-testid="pl-include-internal-toggle"
                            style={{ cursor: 'pointer' }}
                          />
                          <label htmlFor="pl-include-internal" style={{ fontSize: '0.8rem', cursor: 'pointer', color: '#334155' }}>
                            Include internal/personal expenses & purchases
                          </label>
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          if (!profitLossData) {
                            alert('Please wait for data to load before printing.');
                            return;
                          }
                          
                          // Build date string
                          const reportDateStr = dateRange.startDate && dateRange.endDate 
                            ? dateRange.startDate + ' to ' + dateRange.endDate
                            : 'As at ' + new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
                          
                          // Build expenses rows HTML
                          const expensesHtml = (profitLossData.expenses.by_category_hierarchical || []).map(parent => {
                            const parentWeight = parent.subcategories?.length > 0 ? '600' : '400';
                            const subcatHtml = (parent.subcategories || []).map(sub => 
                              '<tr class="subcategory"><td><span class="code">' + (sub.code || '----') + '</span>' + sub.category + '</td><td class="text-right">$' + sub.total.toFixed(2) + '</td></tr>'
                            ).join('');
                            return '<tr><td style="font-weight: ' + parentWeight + ';"><span class="code">' + (parent.code || '----') + '</span>' + parent.category + '</td><td class="text-right" style="font-weight: ' + parentWeight + ';">$' + parent.total.toFixed(2) + '</td></tr>' + subcatHtml;
                          }).join('');
                          
                          // Build GST position label
                          const gstLabel = profitLossData.summary.gst_position >= 0 ? 'Owing' : 'Refund';
                          const gstColor = profitLossData.summary.gst_position >= 0 ? '#dc2626' : '#16a34a';
                          const netProfitClass = profitLossData.summary.net_profit >= 0 ? 'net-positive' : 'net-negative';
                          
                          const contentHtml = `
                            <style>
                              .pl-section { margin-bottom: 20px; }
                              .pl-section-header { padding: 10px 15px; margin-bottom: 8px; border-left: 4px solid; }
                              .pl-section-header.income { background: #f0fdf4; border-color: #16a34a; }
                              .pl-section-header.cost { background: #fef3c7; border-color: #d97706; }
                              .pl-section-header.expenses { background: #fef2f2; border-color: #dc2626; }
                              .pl-section-title { font-size: 14px; font-weight: 700; letter-spacing: 0.05em; margin: 0; }
                              .pl-section-title.income { color: #166534; }
                              .pl-section-title.cost { color: #92400e; }
                              .pl-section-title.expenses { color: #991b1b; }
                              .pl-table { width: 100%; border-collapse: collapse; }
                              .pl-table td { padding: 6px 15px; border: none; }
                              .pl-table .code { color: #6b7280; font-family: monospace; font-size: 12px; margin-right: 12px; }
                              .pl-table .subtotal td { border-top: 1px solid #e5e7eb; font-weight: 600; }
                              .pl-table .subcategory td { padding-left: 40px; font-size: 12px; color: #4b5563; background: #f9fafb; }
                              .pl-highlight { padding: 15px 20px; border-radius: 8px; display: flex; justify-content: space-between; align-items: center; margin: 15px 0; }
                              .pl-highlight.gross { background: #eff6ff; }
                              .pl-highlight.net-positive { background: #f0fdf4; border: 2px solid #16a34a; }
                              .pl-highlight.net-negative { background: #fef2f2; border: 2px solid #dc2626; }
                              .pl-highlight-label { font-size: 16px; font-weight: 700; }
                              .pl-highlight-value { font-size: 20px; font-weight: 700; }
                              .pl-highlight.gross .pl-highlight-label, .pl-highlight.gross .pl-highlight-value { color: #1e40af; }
                              .pl-highlight.net-positive .pl-highlight-label, .pl-highlight.net-positive .pl-highlight-value { color: #166534; }
                              .pl-highlight.net-negative .pl-highlight-label, .pl-highlight.net-negative .pl-highlight-value { color: #991b1b; }
                              .gst-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; padding: 15px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; margin-top: 20px; }
                              .gst-item { text-align: left; }
                              .gst-label { font-size: 12px; color: #64748b; }
                              .gst-value { font-size: 16px; font-weight: 600; margin-top: 4px; }
                              .gst-value.collected { color: #16a34a; }
                              .gst-value.paid { color: #dc2626; }
                              .text-right { text-align: right; }
                            </style>
                            
                            <div style="text-align: center; margin-bottom: 25px; border-bottom: 2px solid #1f2937; padding-bottom: 15px;">
                              <h2 style="font-size: 20px; font-weight: 700; color: #1f2937; margin: 0;">Profit and Loss Statement</h2>
                              <p style="font-size: 13px; color: #6b7280; margin-top: 8px;">${reportDateStr}</p>
                              <p style="font-size: 12px; color: ${includeInternal ? '#92400e' : '#475569'}; margin-top: 4px; font-weight: 600;">
                                Internal/Personal items: ${includeInternal ? 'INCLUDED' : 'EXCLUDED'}
                              </p>
                            </div>

                            <div class="pl-section">
                              <div class="pl-section-header income">
                                <h3 class="pl-section-title income">INCOME</h3>
                              </div>
                              <table class="pl-table">
                                <tbody>
                                  <tr>
                                    <td><span class="code">4000</span>Job Revenue (ex GST)</td>
                                    <td class="text-right">$${profitLossData.income.revenue_ex_gst.toFixed(2)}</td>
                                  </tr>
                                  <tr class="subtotal">
                                    <td style="color: #166534;">Total Income</td>
                                    <td class="text-right" style="color: #166534;">$${profitLossData.income.revenue_ex_gst.toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            <div class="pl-section">
                              <div class="pl-section-header cost">
                                <h3 class="pl-section-title cost">COST OF SALES</h3>
                              </div>
                              <table class="pl-table">
                                <tbody>
                                  <tr>
                                    <td><span class="code">5000</span>Stock/Parts Cost</td>
                                    <td class="text-right">$${profitLossData.cost_of_goods_sold.stock_cost.toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td><span class="code">5100</span>Stock Purchases</td>
                                    <td class="text-right">$${(profitLossData.cost_of_goods_sold.stock_purchases_ex_gst ?? profitLossData.expenses.stock_purchases).toFixed(2)}</td>
                                  </tr>
                                  <tr class="subtotal">
                                    <td style="color: #92400e;">Total Cost of Sales</td>
                                    <td class="text-right" style="color: #92400e;">$${(profitLossData.cost_of_goods_sold.total_cost_of_sales).toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            <div class="pl-highlight gross">
                              <span class="pl-highlight-label">GROSS PROFIT</span>
                              <span class="pl-highlight-value">$${profitLossData.summary.gross_profit.toFixed(2)}</span>
                            </div>

                            <div class="pl-section">
                              <div class="pl-section-header expenses">
                                <h3 class="pl-section-title expenses">OPERATING EXPENSES</h3>
                              </div>
                              <table class="pl-table">
                                <tbody>
                                  ${expensesHtml}
                                  <tr class="subtotal">
                                    <td style="color: #991b1b;">Total Operating Expenses</td>
                                    <td class="text-right" style="color: #991b1b;">$${profitLossData.expenses.expenses_ex_gst.toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            <div class="pl-highlight ${netProfitClass}">
                              <div>
                                <span class="pl-highlight-label">NET PROFIT</span>
                                <p style="font-size: 11px; color: #6b7280; margin: 4px 0 0 0;">Profit Margin: ${profitLossData.summary.profit_margin.toFixed(1)}%</p>
                              </div>
                              <span class="pl-highlight-value">$${profitLossData.summary.net_profit.toFixed(2)}</span>
                            </div>

                            <div class="gst-summary">
                              <div class="gst-item">
                                <div class="gst-label">GST Collected</div>
                                <div class="gst-value collected">$${profitLossData.income.gst_collected.toFixed(2)}</div>
                              </div>
                              <div class="gst-item">
                                <div class="gst-label">GST Paid</div>
                                <div class="gst-value paid">$${profitLossData.expenses.gst_paid.toFixed(2)}</div>
                              </div>
                              <div class="gst-item">
                                <div class="gst-label">GST ${gstLabel}</div>
                                <div class="gst-value" style="color: ${gstColor};">$${Math.abs(profitLossData.summary.gst_position).toFixed(2)}</div>
                              </div>
                            </div>
                          `;
                          openPrintPreview('Profit & Loss Statement', contentHtml, { printerHint: settings?.printer_job_card });
                        }}
                        data-testid="print-profit-loss"
                        style={{ 
                          backgroundColor: '#6366f1', 
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                      >
                        🖨️ Print
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* P&L Comparison Controls */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem', 
                      marginBottom: '1.5rem',
                      padding: '1rem',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      flexWrap: 'wrap'
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={plCompareEnabled}
                          onChange={(e) => setPlCompareEnabled(e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontWeight: '500', color: '#374151' }}>Compare Periods</span>
                      </label>
                      
                      {plCompareEnabled && (
                        <>
                          <select
                            value={plCompareType}
                            onChange={(e) => setPlCompareType(e.target.value)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              backgroundColor: 'white',
                              fontSize: '0.875rem'
                            }}
                          >
                            <option value="previous">Previous Period</option>
                            <option value="lastYear">Same Period Last Year</option>
                            <option value="custom">Custom Period</option>
                          </select>
                          
                          {plCompareType === 'custom' && (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <input
                                type="date"
                                value={plCompareCustomStart}
                                onChange={(e) => setPlCompareCustomStart(e.target.value)}
                                style={{
                                  padding: '0.5rem',
                                  borderRadius: '6px',
                                  border: '1px solid #d1d5db',
                                  fontSize: '0.875rem'
                                }}
                              />
                              <span style={{ color: '#6b7280' }}>to</span>
                              <input
                                type="date"
                                value={plCompareCustomEnd}
                                onChange={(e) => setPlCompareCustomEnd(e.target.value)}
                                style={{
                                  padding: '0.5rem',
                                  borderRadius: '6px',
                                  border: '1px solid #d1d5db',
                                  fontSize: '0.875rem'
                                }}
                              />
                            </div>
                          )}
                          
                          {plCompareData && (
                            <span style={{ 
                              fontSize: '0.75rem', 
                              color: '#6b7280',
                              backgroundColor: '#e0f2fe',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px'
                            }}>
                              Comparing to: {plCompareType === 'custom' && plCompareCustomStart && plCompareCustomEnd
                                ? `${plCompareCustomStart} - ${plCompareCustomEnd}`
                                : plCompareType === 'lastYear'
                                  ? 'Same period last year'
                                  : 'Previous period'}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {profitLossData ? (
                      <div style={{ display: 'grid', gap: '2rem' }}>
                        {/* Summary Cards with Comparison */}
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${includeInternal && profitLossData.internal ? 6 : 5}, 1fr)`, gap: '1rem' }}>
                          <Card style={{ backgroundColor: '#f0fdf4' }}>
                            <CardHeader className="pb-2">
                              <CardDescription style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                Total Revenue
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.05rem 0.35rem', borderRadius: '999px', backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac', letterSpacing: '0.02em' }} title="Figure INCLUDES GST — this is the gross amount collected from customers (before subtracting GST payable to ATO).">INC GST</span>
                              </CardDescription>
                              <CardTitle className="text-2xl" style={{ color: '#16a34a' }}>
                                ${profitLossData.income.total_revenue.toFixed(2)}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="text-xs text-muted-foreground">
                                {profitLossData.income.job_count} Jobs
                              </div>
                              {plCompareEnabled && plCompareData && (() => {
                                const v = calcVariance(profitLossData.income.total_revenue, plCompareData.income.total_revenue);
                                const f = formatVariance(v, true);
                                return (
                                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: f.color, fontWeight: '500' }}>
                                    {f.arrow} {f.diffText} ({f.pctText})
                                  </div>
                                );
                              })()}
                            </CardContent>
                          </Card>
                          <Card style={{ backgroundColor: '#fef2f2' }}>
                            <CardHeader className="pb-2">
                              <CardDescription style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                Total Expenses
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.05rem 0.35rem', borderRadius: '999px', backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', letterSpacing: '0.02em' }} title="Figure INCLUDES GST — this is the gross amount you paid suppliers (before claiming GST credits from ATO).">INC GST</span>
                              </CardDescription>
                              <CardTitle className="text-2xl" style={{ color: '#dc2626' }}>
                                ${profitLossData.expenses.total_expenses.toFixed(2)}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="text-xs text-muted-foreground">
                                {profitLossData.expenses.expense_count} Expenses
                              </div>
                              {plCompareEnabled && plCompareData && (() => {
                                const v = calcVariance(profitLossData.expenses.total_expenses, plCompareData.expenses.total_expenses);
                                const f = formatVariance(v, false); // Lower expenses is better
                                return (
                                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: f.color, fontWeight: '500' }}>
                                    {f.arrow} {f.diffText} ({f.pctText})
                                  </div>
                                );
                              })()}
                            </CardContent>
                          </Card>
                          {/* Cost of Sales (Feb 2026) — user requested INC-GST
                              display for a uniform "bank statement" mental
                              model with Total Revenue and Total Expenses. Uses
                              expenses.stock_purchases (inc-GST). Note: the
                              Gross Profit calc still uses the ex-GST figure
                              internally because P&L math requires it. */}
                          <Card style={{ backgroundColor: '#fff7ed' }}>
                            <CardHeader className="pb-2">
                              <CardDescription style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                Cost of Sales
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.05rem 0.35rem', borderRadius: '999px', backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac', letterSpacing: '0.02em' }} title="Figure INCLUDES GST — the gross amount you paid suppliers for stock (before claiming GST credits from ATO). Same 'bank statement' view as Total Revenue and Total Expenses.">INC GST</span>
                              </CardDescription>
                              <CardTitle className="text-2xl" style={{ color: '#c2410c' }}>
                                ${(profitLossData.expenses?.stock_purchases || 0).toFixed(2)}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="text-xs text-muted-foreground">
                                {(profitLossData.cost_of_goods_sold?.stock_purchase_count || 0)} Stock purchases
                              </div>
                              {plCompareEnabled && plCompareData && (() => {
                                const v = calcVariance(profitLossData.expenses?.stock_purchases || 0, plCompareData.expenses?.stock_purchases || 0);
                                const f = formatVariance(v, false); // Lower COGS is better
                                return (
                                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: f.color, fontWeight: '500' }}>
                                    {f.arrow} {f.diffText} ({f.pctText})
                                  </div>
                                );
                              })()}
                            </CardContent>
                          </Card>
                          <Card style={{ backgroundColor: '#eff6ff' }}>
                            <CardHeader className="pb-2">
                              <CardDescription style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                Gross Profit
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.05rem 0.35rem', borderRadius: '999px', backgroundColor: '#dbeafe', color: '#1e3a8a', border: '1px solid #93c5fd', letterSpacing: '0.02em' }} title="Figure EXCLUDES GST — GST has been stripped from both revenue and stock costs. This is what the business actually earned before overheads.">EX GST</span>
                              </CardDescription>
                              <CardTitle className="text-2xl" style={{ color: '#2563eb' }}>
                                ${profitLossData.summary.gross_profit.toFixed(2)}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="text-xs text-muted-foreground">
                                Before expenses
                              </div>
                              {plCompareEnabled && plCompareData && (() => {
                                const v = calcVariance(profitLossData.summary.gross_profit, plCompareData.summary.gross_profit);
                                const f = formatVariance(v, true);
                                return (
                                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: f.color, fontWeight: '500' }}>
                                    {f.arrow} {f.diffText} ({f.pctText})
                                  </div>
                                );
                              })()}
                            </CardContent>
                          </Card>
                          <Card style={{ backgroundColor: profitLossData.summary.net_profit >= 0 ? '#f0fdf4' : '#fef2f2' }}>
                            <CardHeader className="pb-2">
                              <CardDescription style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                Net Profit
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.05rem 0.35rem', borderRadius: '999px', backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac', letterSpacing: '0.02em' }} title="Figure EXCLUDES GST — this is the taxable business profit. GST collected and GST paid have both been stripped out.">EX GST</span>
                              </CardDescription>
                              <CardTitle className="text-2xl" style={{ color: profitLossData.summary.net_profit >= 0 ? '#16a34a' : '#dc2626' }}>
                                ${profitLossData.summary.net_profit.toFixed(2)}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="text-xs text-muted-foreground">
                                {profitLossData.summary.profit_margin.toFixed(1)}% Margin
                              </div>
                              {plCompareEnabled && plCompareData && (() => {
                                const v = calcVariance(profitLossData.summary.net_profit, plCompareData.summary.net_profit);
                                const f = formatVariance(v, true);
                                return (
                                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: f.color, fontWeight: '500' }}>
                                    {f.arrow} {f.diffText} ({f.pctText})
                                  </div>
                                );
                              })()}
                            </CardContent>
                          </Card>
                          
                          {/* Personal / Internal KPI — appears only when Include Personal is toggled on */}
                          {includeInternal && profitLossData.internal && (
                            <Card
                              style={{ backgroundColor: '#fffbeb', borderColor: '#fcd34d', cursor: 'pointer' }}
                              onClick={() => setPersonalDrillIn({ open: true, scope: 'all', title: 'All Personal Items', items: profitLossData.internal.details || [] })}
                              data-testid="personal-kpi-card"
                              title="Click to see every personal item"
                            >
                              <CardHeader className="pb-2">
                                <CardDescription style={{ color: '#92400e', fontWeight: 600 }}>Personal (not in P&amp;L)</CardDescription>
                                <CardTitle className="text-2xl" style={{ color: '#b45309' }}>
                                  ${profitLossData.internal.total.toFixed(2)}
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="pt-0">
                                <div className="text-xs" style={{ color: '#92400e' }}>
                                  {profitLossData.internal.item_count} item{profitLossData.internal.item_count === 1 ? '' : 's'} · click to drill in
                                </div>
                                {plCompareEnabled && plCompareData && plCompareData.internal && (() => {
                                  const v = calcVariance(profitLossData.internal.total, plCompareData.internal.total);
                                  const f = formatVariance(v, false); // lower personal spend is "better"
                                  return (
                                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: f.color, fontWeight: '500' }}>
                                      {f.arrow} {f.diffText} ({f.pctText})
                                    </div>
                                  );
                                })()}
                              </CardContent>
                            </Card>
                          )}
                        </div>

                        {/* Comparison Summary Table - Extracted Component */}
                        {plCompareEnabled && plCompareData && (
                          <PLComparisonTable 
                            profitLossData={profitLossData}
                            plCompareData={plCompareData}
                            calcVariance={calcVariance}
                            formatVariance={formatVariance}
                          />
                        )}

                        {/* P&L Comparison Chart - Extracted Component */}
                        {plCompareEnabled && plCompareData && (
                          <PLComparisonChart 
                            profitLossData={profitLossData}
                            plCompareData={plCompareData}
                            calcVariance={calcVariance}
                            formatVariance={formatVariance}
                          />
                        )}

                        {/* Xero-Style Professional P&L Statement */}
                        <Card style={{ border: '1px solid #e5e7eb' }}>
                          <CardContent style={{ padding: '2rem' }}>
                            {/* Report Header */}
                            <div style={{ textAlign: 'center', marginBottom: '2rem', borderBottom: '2px solid #1f2937', paddingBottom: '1rem' }}>
                              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>Profit and Loss Statement</h2>
                              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                {dateRange.startDate && dateRange.endDate 
                                  ? `${dateRange.startDate} to ${dateRange.endDate}`
                                  : `As at ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`
                                }
                              </p>
                            </div>

                            {/* INCOME Section (Feb 2026) — drillable by
                                payment method. Click the parent "Job Revenue"
                                row to expand → shows sub-rows per payment
                                method (EFTPOS / Bank Transfer / Cash / …).
                                Click a method row to expand → shows individual
                                payment records with per-row View buttons that
                                navigate to the job detail page. This is the
                                reconciliation surface — user matches these
                                against their bank statement / EFTPOS batches. */}
                            <div style={{ marginBottom: '1.5rem' }}>
                              <div style={{ 
                                backgroundColor: '#f0fdf4', 
                                padding: '0.75rem 1rem', 
                                borderLeft: '4px solid #16a34a',
                                marginBottom: '0.5rem',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#166534', margin: 0, letterSpacing: '0.05em' }}>INCOME</h3>
                                {(profitLossData.income.by_payment_method || []).length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const allOpen = (profitLossData.income.by_payment_method || []).every(m => expandedIncomeMethods[m.method]);
                                      const next = {};
                                      (profitLossData.income.by_payment_method || []).forEach(m => { next[m.method] = !allOpen; });
                                      setExpandedIncomeMethods(next);
                                    }}
                                    data-testid="pl-income-toggle-all"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#166534' }}
                                  >
                                    {Object.values(expandedIncomeMethods).some(v => v) ? '▼ Collapse All' : '▶ Expand All'}
                                  </button>
                                )}
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                  {/* Parent row — click to expand payment methods */}
                                  <tr
                                    style={{
                                      cursor: (profitLossData.income.by_payment_method || []).length > 0 ? 'pointer' : 'default',
                                      backgroundColor: plIncomeExpanded ? '#f0fdf4' : 'transparent',
                                    }}
                                    onClick={() => (profitLossData.income.by_payment_method || []).length > 0 && setPlIncomeExpanded(v => !v)}
                                    data-testid="pl-income-parent-row"
                                    title="Click to see the breakdown by payment method"
                                  >
                                    <td style={{ padding: '0.5rem 1rem', color: '#374151' }}>
                                      <span style={{ color: '#6b7280', fontFamily: 'monospace', marginRight: '1rem' }}>4000</span>
                                      {(profitLossData.income.by_payment_method || []).length > 0 && (
                                        <span style={{ marginRight: '0.5rem', color: '#9ca3af', fontSize: '0.75rem' }}>{plIncomeExpanded ? '▼' : '▶'}</span>
                                      )}
                                      <span style={{ fontWeight: 600 }}>Job Revenue (ex GST)</span>
                                    </td>
                                    <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontWeight: 600 }}>
                                      ${profitLossData.income.revenue_ex_gst.toFixed(2)}
                                    </td>
                                  </tr>

                                  {/* Payment-method sub-rows */}
                                  {plIncomeExpanded && (profitLossData.income.by_payment_method || []).map((m, mIdx) => {
                                    const isMethodOpen = !!expandedIncomeMethods[m.method];
                                    const testSlug = (m.method || 'unknown').replace(/\W+/g, '-').toLowerCase();
                                    // Chart-of-accounts style code — assigns 41xx numbers stably per method name.
                                    const codeMap = { 'EFTPOS': '4110', 'Bank Transfer': '4120', 'Cash': '4130', 'Credit Card': '4140', 'Cheque': '4150', 'Unknown': '4190', 'Migrated / No Record': '4199', 'Unreconciled (no active payment)': '4198' };
                                    const code = codeMap[m.method] || '4180';
                                    return (
                                      <Fragment key={mIdx}>
                                        <tr
                                          style={{ backgroundColor: isMethodOpen ? '#dcfce7' : '#f9fafb', cursor: 'pointer' }}
                                          onClick={() => toggleIncomeMethod(m.method)}
                                          data-testid={`pl-income-method-${testSlug}`}
                                          title="Click to see the individual payment records for this method"
                                        >
                                          <td style={{ padding: '0.375rem 1rem 0.375rem 3rem', color: '#374151', fontSize: '0.875rem' }}>
                                            <span style={{ marginRight: '0.5rem', color: '#94a3b8' }}>{isMethodOpen ? '▼' : '▶'}</span>
                                            <span style={{ fontFamily: 'monospace', marginRight: '1rem', color: '#6b7280' }}>{code}</span>
                                            {m.method}
                                            <span style={{ marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.75rem' }}>({m.count} {m.count === 1 ? 'payment' : 'payments'})</span>
                                          </td>
                                          <td style={{ padding: '0.375rem 1rem', textAlign: 'right', color: '#374151', fontSize: '0.875rem', fontWeight: 500 }}>
                                            ${(m.ex_gst || 0).toFixed(2)}
                                          </td>
                                        </tr>
                                        {isMethodOpen && (
                                          <tr style={{ backgroundColor: '#f0fdf4' }}>
                                            <td colSpan={2} style={{ padding: '0.5rem 1rem 0.75rem 4rem' }}>
                                              <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }} data-testid={`pl-income-payments-${testSlug}`}>
                                                <thead>
                                                  <tr style={{ backgroundColor: '#bbf7d0', color: '#166534' }}>
                                                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 600 }}>Date</th>
                                                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 600 }}>Job #</th>
                                                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 600 }}>Customer</th>
                                                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 600 }}>Reference</th>
                                                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>Ex GST</th>
                                                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>GST</th>
                                                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>Total</th>
                                                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 600 }}>&nbsp;</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {m.payments.map((p, pIdx) => (
                                                    <tr key={pIdx} style={{ borderTop: '1px solid #bbf7d0' }} data-testid={`pl-income-payment-row-${p.job_number}-${pIdx}`}>
                                                      <td style={{ padding: '0.25rem 0.5rem' }}>{formatDate(p.date)}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem', fontWeight: 600 }}>#{p.job_number}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem' }}>{p.customer_name || '—'}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem', color: '#64748b' }}>{p.reference || '—'}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>${(p.ex_gst || 0).toFixed(2)}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', color: '#64748b' }}>${(p.gst || 0).toFixed(2)}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>${(p.amount || 0).toFixed(2)}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>
                                                        {p.job_id ? (
                                                          <a
                                                            href={`/job/${p.job_id}`}
                                                            onClick={(e) => { e.preventDefault(); navigate(`/job/${p.job_id}`); }}
                                                            data-testid={`pl-income-view-${p.job_number}`}
                                                            style={{ fontSize: '0.7rem', color: '#0369a1', textDecoration: 'underline', cursor: 'pointer' }}
                                                          >View</a>
                                                        ) : (
                                                          <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>—</span>
                                                        )}
                                                      </td>
                                                    </tr>
                                                  ))}
                                                  <tr style={{ borderTop: '2px solid #86efac', backgroundColor: '#dcfce7' }}>
                                                    <td colSpan={4} style={{ padding: '0.35rem 0.5rem', fontWeight: 700, color: '#166534' }}>Subtotal — {m.method}</td>
                                                    <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#166534' }}>${(m.ex_gst || 0).toFixed(2)}</td>
                                                    <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#166534' }}>${(m.gst || 0).toFixed(2)}</td>
                                                    <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#166534' }}>${(m.total || 0).toFixed(2)}</td>
                                                    <td>&nbsp;</td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </td>
                                          </tr>
                                        )}
                                      </Fragment>
                                    );
                                  })}

                                  <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#166534' }}>Total Income</td>
                                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700', color: '#166534' }}>${profitLossData.income.revenue_ex_gst.toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            {/* COST OF SALES Section */}
                            <div style={{ marginBottom: '1.5rem' }}>
                              <div style={{ 
                                backgroundColor: '#fef3c7', 
                                padding: '0.75rem 1rem', 
                                borderLeft: '4px solid #d97706',
                                marginBottom: '0.5rem'
                              }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#92400e', margin: 0, letterSpacing: '0.05em' }}>COST OF SALES</h3>
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                  <tr>
                                    <td style={{ padding: '0.5rem 1rem', color: '#374151' }}>
                                      <span style={{ color: '#6b7280', fontFamily: 'monospace', marginRight: '1rem' }}>5000</span>
                                      Stock/Parts Cost
                                    </td>
                                    <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontWeight: '500' }}>${profitLossData.cost_of_goods_sold.stock_cost.toFixed(2)}</td>
                                  </tr>
                                  <tr
                                    style={{ cursor: 'pointer', backgroundColor: expandedSubTx['cos::stock_purchases'] ? '#fff7ed' : undefined }}
                                    onClick={() => togglePnlLeaf('purchase', 'cos::stock_purchases', null, null)}
                                    data-testid="pnl-leaf-stock-purchases"
                                    title="Click to see the purchases in this period"
                                  >
                                    <td style={{ padding: '0.5rem 1rem', color: '#374151' }}>
                                      <span style={{ marginRight: '0.5rem', color: '#94a3b8' }}>{expandedSubTx['cos::stock_purchases'] ? '▼' : '▶'}</span>
                                      <span style={{ color: '#6b7280', fontFamily: 'monospace', marginRight: '1rem' }}>5100</span>
                                      Stock Purchases
                                    </td>
                                    <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontWeight: '500' }}>${(profitLossData.cost_of_goods_sold?.stock_purchases_ex_gst ?? profitLossData.expenses.stock_purchases).toFixed(2)}</td>
                                  </tr>
                                  {expandedSubTx['cos::stock_purchases'] && (
                                    <tr style={{ backgroundColor: '#fffbeb' }}>
                                      <td colSpan={2} style={{ padding: '0.5rem 1rem 0.75rem 4rem' }}>
                                        {(() => {
                                          const txs = subTxCache['cos::stock_purchases'];
                                          if (txs === undefined || txs === null) return <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>Loading…</div>;
                                          if (txs.length === 0) return <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>No purchases in this period.</div>;
                                          return (
                                            <div>
                                              <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                                <thead>
                                                  <tr style={{ color: '#64748b', textAlign: 'left' }}>
                                                    <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Date</th>
                                                    <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Supplier</th>
                                                    <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Invoice #</th>
                                                    <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Method</th>
                                                    <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500, textAlign: 'right' }}>Total</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {txs.map(t => (
                                                    <tr key={t.id} style={{ borderTop: '1px solid #fef3c7' }}>
                                                      <td style={{ padding: '0.25rem 0.5rem' }}>{formatDate(t.date)}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem' }}>{t.supplier_name || '—'}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem' }}>{t.invoice_number || '—'}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem' }}>{t.payment_method || '—'}</td>
                                                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', fontWeight: 500 }}>${(t.total || 0).toFixed(2)}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                              <div style={{ marginTop: '0.5rem' }}>
                                                <a href="/purchases" style={{ fontSize: '0.75rem', color: '#0369a1', textDecoration: 'underline' }} data-testid="pnl-leaf-open-in-purchases">
                                                  → Open in Purchases page (full toolkit)
                                                </a>
                                              </div>
                                            </div>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  )}
                                  <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#92400e' }}>Total Cost of Sales</td>
                                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700', color: '#92400e' }}>${(profitLossData.cost_of_goods_sold.stock_cost + profitLossData.expenses.stock_purchases).toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            {/* GROSS PROFIT */}
                            <div style={{ 
                              backgroundColor: '#eff6ff', 
                              padding: '1rem', 
                              marginBottom: '1.5rem',
                              borderRadius: '0.5rem',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ fontSize: '1.125rem', fontWeight: '700', color: '#1e40af' }}>GROSS PROFIT</span>
                              <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e40af' }}>${profitLossData.summary.gross_profit.toFixed(2)}</span>
                            </div>

                            {/* OPERATING EXPENSES Section */}
                            <div style={{ marginBottom: '1.5rem' }}>
                              <div style={{ 
                                backgroundColor: '#fef2f2', 
                                padding: '0.75rem 1rem', 
                                borderLeft: '4px solid #dc2626',
                                marginBottom: '0.5rem',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#991b1b', margin: 0, letterSpacing: '0.05em' }}>OPERATING EXPENSES</h3>
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Expand/collapse all categories
                                    const allExpanded = Object.values(expandedExpenseCategories).every(v => v);
                                    const newState = {};
                                    profitLossData.expenses.by_category_hierarchical?.forEach(p => {
                                      if (p.subcategories?.length > 0) {
                                        newState[p.category] = !allExpanded;
                                      }
                                    });
                                    setExpandedExpenseCategories(newState);
                                  }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#991b1b' }}
                                >
                                  {Object.values(expandedExpenseCategories).some(v => v) ? '▼ Collapse All' : '▶ Expand All'}
                                </button>
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                  {profitLossData.expenses.by_category_hierarchical?.map((parent, idx) => {
                                    // Feb 2026 — parents WITHOUT subcategories are themselves leaves
                                    // (e.g. "Other" / code 9999). Reuse the same togglePnlLeaf drill-in
                                    // as the subcategory rows so every non-expandable row is clickable.
                                    const isLeafParent = !parent.subcategories?.length;
                                    const parentKey = isLeafParent ? `__leaf__::${parent.category}` : null;
                                    const isParentLeafExpanded = isLeafParent && !!expandedSubTx[parentKey];
                                    const parentLeafTxs = isLeafParent ? (subTxCache[parentKey] || null) : null;
                                    return (
                                    <Fragment key={idx}>
                                      <tr 
                                        style={{ 
                                          cursor: 'pointer',
                                          backgroundColor: isLeafParent
                                            ? (isParentLeafExpanded ? '#fff7ed' : 'transparent')
                                            : '#fafafa'
                                        }}
                                        onClick={() => {
                                          if (isLeafParent) {
                                            // Feb 2026 — for the "Other" bucket the code
                                            // shown ("9999") is a UI-side fallback that
                                            // doesn't exist in the DB, so passing it
                                            // would 0-out the drill. Pass the code only
                                            // when it's a real 4-digit-ish code that
                                            // could match stored `category` values.
                                            const codeForApi = (parent.code && /^\d{3,5}$/.test(parent.code) && parent.code !== '9999')
                                              ? parent.code : null;
                                            togglePnlLeaf('expense', parentKey, parent.category, codeForApi);
                                          } else {
                                            toggleExpenseCategory(parent.category);
                                          }
                                        }}
                                        data-testid={isLeafParent ? `pnl-leaf-${parentKey.replace(/\W+/g,'-').toLowerCase()}` : undefined}
                                        title={isLeafParent ? 'Click to see the transactions in this category' : undefined}
                                      >
                                        <td style={{ padding: '0.5rem 1rem', color: '#374151' }}>
                                          <span style={{ color: '#6b7280', fontFamily: 'monospace', marginRight: '1rem', fontSize: '0.875rem' }}>
                                            {parent.code || '----'}
                                          </span>
                                          {(parent.subcategories?.length > 0 || isLeafParent) && (
                                            <span style={{ marginRight: '0.5rem', color: '#9ca3af', fontSize: '0.75rem' }}>
                                              {isLeafParent
                                                ? (isParentLeafExpanded ? '▼' : '▶')
                                                : (expandedExpenseCategories[parent.category] ? '▼' : '▶')}
                                            </span>
                                          )}
                                          <span style={{ fontWeight: parent.subcategories?.length > 0 ? '600' : '400' }}>
                                            {parent.category}
                                          </span>
                                        </td>
                                        <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontWeight: parent.subcategories?.length > 0 ? '600' : '500' }}>
                                          ${parent.total.toFixed(2)}
                                        </td>
                                      </tr>
                                      {/* Leaf-parent inline transactions (Feb 2026) — same
                                          rendering shape as the subcategory-leaf branch below. */}
                                      {isLeafParent && isParentLeafExpanded && (
                                        <tr style={{ backgroundColor: '#fffbeb' }}>
                                          <td colSpan={2} style={{ padding: '0.5rem 1rem 0.75rem 3rem' }}>
                                            {parentLeafTxs === null ? (
                                              <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>Loading…</div>
                                            ) : parentLeafTxs.length === 0 ? (
                                              <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>No transactions in this category for the selected period.</div>
                                            ) : (
                                              <div>
                                                <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                                  <thead>
                                                    <tr style={{ color: '#64748b', textAlign: 'left' }}>
                                                      <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Date</th>
                                                      <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Description</th>
                                                      <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Supplier</th>
                                                      <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Method</th>
                                                      <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500, textAlign: 'right' }}>Total</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {parentLeafTxs.map(t => (
                                                      <tr key={t.id} style={{ borderTop: '1px solid #fef3c7' }}>
                                                        <td style={{ padding: '0.25rem 0.5rem' }}>{formatDate(t.date)}</td>
                                                        <td style={{ padding: '0.25rem 0.5rem' }}>{t.description || '—'}</td>
                                                        <td style={{ padding: '0.25rem 0.5rem' }}>{t.supplier_name || '—'}</td>
                                                        <td style={{ padding: '0.25rem 0.5rem' }}>{t.payment_method || '—'}</td>
                                                        <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', fontWeight: 500 }}>${(t.total || 0).toFixed(2)}</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                                <div style={{ marginTop: '0.5rem' }}>
                                                  <a
                                                    href={`/expenses?category=${encodeURIComponent(parent.category)}`}
                                                    style={{ fontSize: '0.75rem', color: '#0369a1', textDecoration: 'underline' }}
                                                    data-testid={`pnl-leaf-open-in-expenses-${parentKey.replace(/\W+/g,'-').toLowerCase()}`}
                                                  >
                                                    → Open in Expenses page (full toolkit)
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      )}
                                      {/* Subcategories */}
                                      {expandedExpenseCategories[parent.category] && parent.subcategories?.map((sub, subIdx) => {
                                        const subKey = `${parent.category}::${sub.category}`;
                                        const isTxExpanded = !!expandedSubTx[subKey];
                                        const txs = subTxCache[subKey] || null;
                                        return (
                                          <Fragment key={`${idx}-${subIdx}`}>
                                          <tr
                                            style={{ backgroundColor: isTxExpanded ? '#fff7ed' : '#f9fafb', cursor: 'pointer' }}
                                            onClick={() => togglePnlLeaf('expense', subKey, sub.category, sub.code)}
                                            data-testid={`pnl-leaf-${subKey.replace(/\W+/g,'-').toLowerCase()}`}
                                            title="Click to see the transactions in this category"
                                          >
                                            <td style={{ padding: '0.375rem 1rem 0.375rem 3rem', color: '#6b7280', fontSize: '0.875rem' }}>
                                              <span style={{ marginRight: '0.5rem', color: '#94a3b8' }}>{isTxExpanded ? '▼' : '▶'}</span>
                                              <span style={{ fontFamily: 'monospace', marginRight: '1rem' }}>{sub.code || '----'}</span>
                                              {sub.category}
                                            </td>
                                            <td style={{ padding: '0.375rem 1rem', textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>
                                              ${sub.total.toFixed(2)}
                                            </td>
                                          </tr>
                                          {isTxExpanded && (
                                            <tr style={{ backgroundColor: '#fffbeb' }}>
                                              <td colSpan={2} style={{ padding: '0.5rem 1rem 0.75rem 4rem' }}>
                                                {txs === null ? (
                                                  <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>Loading…</div>
                                                ) : txs.length === 0 ? (
                                                  <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>No transactions in this category for the selected period.</div>
                                                ) : (
                                                  <div>
                                                    <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                                      <thead>
                                                        <tr style={{ color: '#64748b', textAlign: 'left' }}>
                                                          <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Date</th>
                                                          <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Description</th>
                                                          <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Supplier</th>
                                                          <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500 }}>Method</th>
                                                          <th style={{ padding: '0.25rem 0.5rem', fontWeight: 500, textAlign: 'right' }}>Total</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {txs.map(t => (
                                                          <tr key={t.id} style={{ borderTop: '1px solid #fef3c7' }}>
                                                            <td style={{ padding: '0.25rem 0.5rem' }}>{formatDate(t.date)}</td>
                                                            <td style={{ padding: '0.25rem 0.5rem' }}>{t.description || '—'}</td>
                                                            <td style={{ padding: '0.25rem 0.5rem' }}>{t.supplier_name || '—'}</td>
                                                            <td style={{ padding: '0.25rem 0.5rem' }}>{t.payment_method || '—'}</td>
                                                            <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', fontWeight: 500 }}>${(t.total || 0).toFixed(2)}</td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                    <div style={{ marginTop: '0.5rem' }}>
                                                      <a
                                                        href={`/expenses?category=${encodeURIComponent(sub.category)}`}
                                                        style={{ fontSize: '0.75rem', color: '#0369a1', textDecoration: 'underline' }}
                                                        data-testid={`pnl-leaf-open-in-expenses-${subKey.replace(/\W+/g,'-').toLowerCase()}`}
                                                      >
                                                        → Open in Expenses page (full toolkit)
                                                      </a>
                                                    </div>
                                                  </div>
                                                )}
                                              </td>
                                            </tr>
                                          )}
                                          </Fragment>
                                        );
                                      })}
                                    </Fragment>
                                    );
                                  })}
                                  <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#991b1b' }}>Total Operating Expenses</td>
                                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700', color: '#991b1b' }}>${profitLossData.expenses.expenses_ex_gst.toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            {/* NET PROFIT */}
                            <div style={{ 
                              backgroundColor: profitLossData.summary.net_profit >= 0 ? '#f0fdf4' : '#fef2f2', 
                              padding: '1.25rem', 
                              borderRadius: '0.5rem',
                              border: `2px solid ${profitLossData.summary.net_profit >= 0 ? '#16a34a' : '#dc2626'}`,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div>
                                <span style={{ fontSize: '1.25rem', fontWeight: '700', color: profitLossData.summary.net_profit >= 0 ? '#166534' : '#991b1b' }}>NET PROFIT</span>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                                  Profit Margin: {profitLossData.summary.profit_margin.toFixed(1)}%
                                </p>
                              </div>
                              <span style={{ fontSize: '1.5rem', fontWeight: '700', color: profitLossData.summary.net_profit >= 0 ? '#166534' : '#991b1b' }}>
                                ${profitLossData.summary.net_profit.toFixed(2)}
                              </span>
                            </div>

                            {/* Personal-use claimed add-back (Feb 2026) — only
                                renders when the owner has flagged one or more
                                deductible expenses/purchases as personal-use.
                                Shows what P&L would have been WITHOUT those
                                claims so the owner sees the "true" business
                                earning power at EOFY. Red palette matches the
                                "PERSONAL · CLAIMED" badge on the Expenses list. */}
                            {profitLossData.personal_use_claimed && profitLossData.personal_use_claimed.total > 0 && (
                              <div style={{ marginTop: '1rem' }} data-testid="pl-personal-use-addback">
                                <div
                                  onClick={() => setPucDrillOpen(v => !v)}
                                  style={{ padding: '1rem 1.25rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', border: '1px solid #fca5a5', borderLeft: '4px solid #dc2626', cursor: 'pointer' }}
                                  data-testid="pl-puc-toggle"
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: '240px' }}>
                                      <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                                        <span style={{ fontSize: '0.75rem', color: '#991b1b', marginRight: '0.35rem' }}>{pucDrillOpen ? '▾' : '▸'}</span>
                                        🏠 Personal-use items you claimed
                                      </div>
                                      <div style={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.4 }}>
                                        Items paid from the business (still deductible) but flagged as personal-use — e.g. home CCTV, laptop for personal work.
                                        Add these back to Net Profit to see what the business earned before you diverted funds into personal-use assets.
                                        {' '}Click to {pucDrillOpen ? 'hide' : 'see'} the {profitLossData.personal_use_claimed.item_count} contributing item{profitLossData.personal_use_claimed.item_count === 1 ? '' : 's'}.
                                      </div>
                                      <div style={{ marginTop: '0.35rem', fontSize: '0.7rem', color: '#78716c', fontStyle: 'italic' }} data-testid="pl-puc-included-note">
                                        ℹ️ Already deducted from Net Profit above (as expenses/stock purchases) — this add-back shows profit without them.
                                      </div>
                                      {profitLossData.personal_use_claimed.by_category?.length > 0 && (
                                        <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#64748b' }}>
                                          {profitLossData.personal_use_claimed.by_category.map((c, i) => (
                                            <span key={c.category} data-testid={`pl-puc-cat-${i}`}>
                                              {c.category}: ${c.total.toFixed(2)} ({c.count})
                                              {i < profitLossData.personal_use_claimed.by_category.length - 1 ? ' · ' : ''}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Add-back (ex-GST)</div>
                                      <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#991b1b' }} data-testid="pl-puc-addback-value">
                                        +${profitLossData.personal_use_claimed.total_ex_gst.toFixed(2)}
                                      </div>
                                      <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.35rem' }}>True Business Profit</div>
                                      <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#065f46' }} data-testid="pl-true-business-profit">
                                        ${profitLossData.summary.true_business_profit.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {pucDrillOpen && (profitLossData.personal_use_claimed.details?.length || 0) > 0 && (
                                  <div style={{ marginTop: '0.5rem', padding: '0.75rem 1rem', backgroundColor: 'white', border: '1px solid #fecaca', borderRadius: '0.5rem' }} data-testid="pl-puc-drill">
                                    <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr style={{ textAlign: 'left', color: '#78716c', borderBottom: '1px solid #fecaca' }}>
                                          <th style={{ padding: '0.4rem 0.5rem' }}>Date</th>
                                          <th style={{ padding: '0.4rem 0.5rem' }}>Description</th>
                                          <th style={{ padding: '0.4rem 0.5rem' }}>Category</th>
                                          <th style={{ padding: '0.4rem 0.5rem' }}>Supplier</th>
                                          <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Ex-GST</th>
                                          <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>GST</th>
                                          <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {profitLossData.personal_use_claimed.details.map((row, i) => (
                                          <tr
                                            key={`${row.id || 'puc'}-${i}`}
                                            data-testid={`pl-puc-row-${i}`}
                                            style={{ borderBottom: '1px solid #fee2e2' }}
                                          >
                                            <td style={{ padding: '0.4rem 0.5rem', color: '#64748b' }}>{row.date ? new Date(row.date).toLocaleDateString('en-AU') : ''}</td>
                                            <td style={{ padding: '0.4rem 0.5rem' }}>{row.description}</td>
                                            <td style={{ padding: '0.4rem 0.5rem', color: '#64748b' }}>{row.category}</td>
                                            <td style={{ padding: '0.4rem 0.5rem', color: '#64748b' }}>{row.supplier}</td>
                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>${row.amount_ex_gst.toFixed(2)}</td>
                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: '#64748b' }}>${row.gst.toFixed(2)}</td>
                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>${row.total.toFixed(2)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Emergency Service Revenue (Jul 2026) — only renders
                                when at least one job in the period had an emergency
                                surcharge line item. Amber palette to match the
                                emergency badge convention used elsewhere in the app.
                                Cash-basis: counts jobs whose revenue_date falls in
                                the selected period (same rule as everything else on
                                the Financial tab). */}
                            {profitLossData.emergency_service && profitLossData.emergency_service.total_inc_gst > 0 && (
                              <div style={{ marginTop: '1rem' }} data-testid="pl-emergency-service-card">
                                <div
                                  onClick={() => setEmergencyDrillOpen(v => !v)}
                                  style={{
                                    padding: '1rem 1.25rem',
                                    backgroundColor: '#fffbeb',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #fcd34d',
                                    borderLeft: '4px solid #f59e0b',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: '240px' }}>
                                    <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                                      ⚡ Emergency Service Revenue
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#78716c', lineHeight: 1.4 }}>
                                      Total of the &quot;Emergency Service&quot; invoice line across jobs paid in this period.
                                      {' '}Click to {emergencyDrillOpen ? 'hide' : 'see'} the {profitLossData.emergency_service.job_count} contributing job{profitLossData.emergency_service.job_count === 1 ? '' : 's'}.
                                    </div>
                                    <div style={{ marginTop: '0.35rem', fontSize: '0.7rem', color: '#78716c', fontStyle: 'italic' }} data-testid="pl-emergency-included-note">
                                      ℹ️ Already included in Net Profit above — shown separately as an informational breakdown.
                                    </div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.7rem', color: '#78716c' }}>Total (inc GST)</div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#b45309' }} data-testid="pl-emergency-total">
                                      ${profitLossData.emergency_service.total_inc_gst.toFixed(2)}
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: '#78716c', marginTop: '0.25rem' }}>
                                      {profitLossData.emergency_service.job_count} job{profitLossData.emergency_service.job_count === 1 ? '' : 's'} · {profitLossData.emergency_service.pct_of_revenue.toFixed(1)}% of revenue
                                    </div>
                                  </div>
                                </div>
                                {emergencyDrillOpen && profitLossData.emergency_service.details.length > 0 && (
                                  <div style={{ marginTop: '0.5rem', padding: '0.75rem 1rem', backgroundColor: 'white', border: '1px solid #fde68a', borderRadius: '0.5rem' }} data-testid="pl-emergency-drill">
                                    <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr style={{ textAlign: 'left', color: '#78716c', borderBottom: '1px solid #fde68a' }}>
                                          <th style={{ padding: '0.4rem 0.5rem' }}>Job #</th>
                                          <th style={{ padding: '0.4rem 0.5rem' }}>Customer</th>
                                          <th style={{ padding: '0.4rem 0.5rem' }}>Type</th>
                                          <th style={{ padding: '0.4rem 0.5rem' }}>Paid</th>
                                          <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Emergency $</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {profitLossData.emergency_service.details.map((row) => (
                                          <tr
                                            key={row.id}
                                            onClick={() => navigate(`/job/${row.id}`)}
                                            data-testid={`pl-emergency-row-${row.id}`}
                                            style={{ cursor: 'pointer', borderBottom: '1px solid #fef3c7' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fffbeb'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                          >
                                            <td style={{ padding: '0.4rem 0.5rem', color: '#2563eb', fontWeight: 600 }}>#{row.job_number}</td>
                                            <td style={{ padding: '0.4rem 0.5rem' }}>{row.customer_name}</td>
                                            <td style={{ padding: '0.4rem 0.5rem', color: '#64748b' }}>{row.job_type}</td>
                                            <td style={{ padding: '0.4rem 0.5rem', color: '#64748b' }}>{row.date ? new Date(row.date).toLocaleDateString('en-AU') : ''}</td>
                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: 600, color: '#b45309' }}>${row.amount.toFixed(2)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Additional Line Items (Jul 2026) — one card per
                                category (Labour / Part / Other) in a compact
                                grid. Renders only when there's at least one
                                paid job in the period with a line item. */}
                            {profitLossData.line_items && profitLossData.line_items.total_inc_gst > 0 && (
                              <div style={{ marginTop: '1rem' }} data-testid="pl-line-items-card">
                                <div style={{ padding: '1rem 1.25rem', backgroundColor: '#f0fdfa', borderRadius: '0.5rem', border: '1px solid #99f6e4', borderLeft: '4px solid #0d9488' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                                    <div>
                                      <div style={{ fontWeight: 700, color: '#115e59', fontSize: '0.95rem' }}>➕ Additional Line Items Revenue</div>
                                      <div style={{ fontSize: '0.75rem', color: '#0f766e', marginTop: '0.25rem' }}>
                                        Mixed-job labour, non-stock parts, one-off services. Click any category to see the contributing jobs.
                                      </div>
                                      <div style={{ marginTop: '0.35rem', fontSize: '0.7rem', color: '#0f766e', fontStyle: 'italic' }} data-testid="pl-line-items-included-note">
                                        ℹ️ Already included in Net Profit above — shown separately as an informational breakdown.
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontSize: '0.7rem', color: '#0f766e' }}>Total (inc GST)</div>
                                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0d9488' }} data-testid="pl-line-items-total">
                                        ${profitLossData.line_items.total_inc_gst.toFixed(2)}
                                      </div>
                                      <div style={{ fontSize: '0.7rem', color: '#0f766e', marginTop: '0.15rem' }}>
                                        {profitLossData.line_items.pct_of_revenue.toFixed(1)}% of revenue
                                      </div>
                                    </div>
                                  </div>
                                  {/* Per-category rows */}
                                  <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.4rem' }}>
                                    {profitLossData.line_items.by_category.map((cat) => {
                                      const isOpen = !!lineItemsDrillOpen[cat.category];
                                      return (
                                        <div key={cat.category}>
                                          <div
                                            onClick={() => setLineItemsDrillOpen(v => ({ ...v, [cat.category]: !isOpen }))}
                                            data-testid={`pl-line-items-cat-${cat.category}`}
                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', backgroundColor: 'white', border: '1px solid #ccfbf1', borderRadius: '0.375rem', cursor: 'pointer' }}
                                          >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                                              <span style={{ fontSize: '0.75rem', color: '#0f766e' }}>{isOpen ? '▾' : '▸'}</span>
                                              <span style={{ fontWeight: 600, color: '#115e59' }}>{cat.category}</span>
                                              <span style={{ color: '#64748b' }}>· {cat.job_count} job{cat.job_count === 1 ? '' : 's'}</span>
                                            </div>
                                            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0d9488' }}>${cat.total_inc_gst.toFixed(2)}</span>
                                          </div>
                                          {isOpen && cat.details.length > 0 && (
                                            <div style={{ margin: '0.35rem 0 0.5rem 1.25rem', padding: '0.5rem 0.75rem', backgroundColor: 'white', border: '1px solid #ccfbf1', borderRadius: '0.375rem' }} data-testid={`pl-line-items-drill-${cat.category}`}>
                                              <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                                <thead>
                                                  <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #ccfbf1' }}>
                                                    <th style={{ padding: '0.3rem 0.5rem' }}>Job #</th>
                                                    <th style={{ padding: '0.3rem 0.5rem' }}>Customer</th>
                                                    <th style={{ padding: '0.3rem 0.5rem' }}>Description</th>
                                                    <th style={{ padding: '0.3rem 0.5rem' }}>Paid</th>
                                                    <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>$</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {cat.details.map((row, i) => (
                                                    <tr
                                                      key={`${row.id}-${i}`}
                                                      onClick={() => navigate(`/job/${row.id}`)}
                                                      style={{ cursor: 'pointer', borderBottom: '1px solid #f0fdfa' }}
                                                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0fdfa'; }}
                                                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                    >
                                                      <td style={{ padding: '0.3rem 0.5rem', color: '#2563eb', fontWeight: 600 }}>#{row.job_number}</td>
                                                      <td style={{ padding: '0.3rem 0.5rem' }}>{row.customer_name}</td>
                                                      <td style={{ padding: '0.3rem 0.5rem', color: '#334155' }}>{row.description}</td>
                                                      <td style={{ padding: '0.3rem 0.5rem', color: '#64748b' }}>{row.date ? new Date(row.date).toLocaleDateString('en-AU') : ''}</td>
                                                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 600, color: '#0d9488' }}>${row.amount.toFixed(2)}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* GST Summary */}
                            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
                              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#475569', marginBottom: '0.75rem' }}>GST Summary</h4>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
                                <div>
                                  <span style={{ color: '#64748b' }}>GST Collected</span>
                                  <p style={{ fontWeight: '600', color: '#16a34a', margin: '0.25rem 0 0 0' }}>${profitLossData.income.gst_collected.toFixed(2)}</p>
                                </div>
                                <div>
                                  <span style={{ color: '#64748b' }}>GST Paid</span>
                                  <p style={{ fontWeight: '600', color: '#dc2626', margin: '0.25rem 0 0 0' }}>${profitLossData.expenses.gst_paid.toFixed(2)}</p>
                                </div>
                                <div>
                                  <span style={{ color: '#64748b' }}>GST {profitLossData.summary.gst_position >= 0 ? 'Owing' : 'Refund'}</span>
                                  <p style={{ fontWeight: '600', color: profitLossData.summary.gst_position >= 0 ? '#dc2626' : '#16a34a', margin: '0.25rem 0 0 0' }}>
                                    ${Math.abs(profitLossData.summary.gst_position).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                            {/* ── Personal / Internal Items (only when toggle ON) ───────────── */}
                            {includeInternal && profitLossData.internal && profitLossData.internal.item_count > 0 && (
                              <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#fffbeb', borderRadius: '0.5rem', border: '2px solid #fcd34d' }} data-testid="pl-personal-section">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #fcd34d' }}>
                                  <div>
                                    <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#92400e', margin: 0 }}>Personal / Internal Items</h4>
                                    <p style={{ fontSize: '0.75rem', color: '#92400e', margin: '0.25rem 0 0 0', fontStyle: 'italic' }}>Excluded from Net Profit above — shown here for reference only</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setPersonalDrillIn({ open: true, scope: 'all', title: 'All Personal Items', items: profitLossData.internal.details || [] })}
                                    style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', backgroundColor: '#b45309', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: 600 }}
                                    data-testid="pl-personal-view-all-btn"
                                  >
                                    View all {profitLossData.internal.item_count} items
                                  </button>
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead style={{ width: '70%' }}>Category</TableHead>
                                      <TableHead className="text-right">Amount</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {(profitLossData.internal.by_category_hierarchical || []).map((parent, pIdx) => (
                                      <Fragment key={`pi-${pIdx}`}>
                                        <TableRow
                                          style={{ cursor: 'pointer', backgroundColor: '#fef3c7' }}
                                          onClick={() => {
                                            const matchingItems = (profitLossData.internal.details || []).filter(d =>
                                              // Match this parent OR any of its subs
                                              d.category === parent.category || (parent.subcategories || []).some(s => d.category === s.category || d.category.endsWith(s.category))
                                            );
                                            setPersonalDrillIn({ open: true, scope: 'parent', title: `${parent.category} — Personal`, items: matchingItems });
                                          }}
                                          data-testid={`pl-personal-row-${pIdx}`}
                                        >
                                          <TableCell style={{ fontWeight: 600, color: '#78350f' }}>
                                            {parent.code && <span style={{ marginRight: '0.5rem', fontFamily: 'monospace', color: '#a16207' }}>{parent.code}</span>}
                                            {parent.category}
                                          </TableCell>
                                          <TableCell className="text-right" style={{ fontWeight: 600, color: '#78350f' }}>${parent.total.toFixed(2)}</TableCell>
                                        </TableRow>
                                        {(parent.subcategories || []).map((sub, sIdx) => (
                                          <TableRow
                                            key={`pi-${pIdx}-${sIdx}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                              const matchingItems = (profitLossData.internal.details || []).filter(d => d.category === sub.category || d.category.endsWith(sub.category));
                                              setPersonalDrillIn({ open: true, scope: 'sub', title: `${sub.category} — Personal`, items: matchingItems });
                                            }}
                                          >
                                            <TableCell style={{ paddingLeft: '2.5rem', color: '#92400e' }}>
                                              {sub.code && <span style={{ marginRight: '0.5rem', fontFamily: 'monospace', color: '#a16207' }}>{sub.code}</span>}
                                              {sub.category}
                                            </TableCell>
                                            <TableCell className="text-right" style={{ color: '#92400e' }}>${sub.total.toFixed(2)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </Fragment>
                                    ))}
                                    <TableRow style={{ backgroundColor: '#fde68a', fontWeight: 700 }}>
                                      <TableCell style={{ color: '#78350f' }}>Total Personal Spending</TableCell>
                                      <TableCell className="text-right" style={{ color: '#78350f' }}>${profitLossData.internal.total.toFixed(2)}</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                        {plIncomeExpanded && profitLossData.income.details && profitLossData.income.details.length > 0 && (
                          <Card style={{ marginTop: '1rem' }}>
                            <CardHeader>
                              <CardTitle className="text-lg" style={{ color: '#16a34a' }}>Income Details ({profitLossData.income.details.length} transactions)</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Date</TableHead>
                                      <TableHead>Description</TableHead>
                                      <TableHead>Job Type</TableHead>
                                      <TableHead>Status</TableHead>
                                      <TableHead className="text-right">Ex GST</TableHead>
                                      <TableHead className="text-right">GST</TableHead>
                                      <TableHead className="text-right">Total</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {profitLossData.income.details.map((item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>{formatDate(item.date)}</TableCell>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell>{item.job_type}</TableCell>
                                        <TableCell>
                                          <span style={{
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            backgroundColor: item.status === 'Finished' ? '#dcfce7' : '#fef3c7',
                                            color: item.status === 'Finished' ? '#166534' : '#92400e'
                                          }}>
                                            {item.status}
                                          </span>
                                        </TableCell>
                                        <TableCell className="text-right">${item.amount_ex_gst.toFixed(2)}</TableCell>
                                        <TableCell className="text-right">${item.gst.toFixed(2)}</TableCell>
                                        <TableCell className="text-right font-medium">${item.total.toFixed(2)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Expanded Expenses Details */}
                        {plExpensesExpanded && profitLossData.expenses.details && profitLossData.expenses.details.length > 0 && (
                          <Card style={{ marginTop: '1rem' }}>
                            <CardHeader>
                              <CardTitle className="text-lg" style={{ color: '#dc2626' }}>Expense Details ({profitLossData.expenses.details.length} transactions)</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Date</TableHead>
                                      <TableHead>Description</TableHead>
                                      <TableHead>Category</TableHead>
                                      <TableHead>Supplier</TableHead>
                                      <TableHead>Type</TableHead>
                                      <TableHead className="text-right">Ex GST</TableHead>
                                      <TableHead className="text-right">GST</TableHead>
                                      <TableHead className="text-right">Total</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {profitLossData.expenses.details.map((item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>{formatDate(item.date)}</TableCell>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell>{item.category}</TableCell>
                                        <TableCell>{item.supplier || '-'}</TableCell>
                                        <TableCell>
                                          <span style={{
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            backgroundColor: item.type === 'Stock Purchase' ? '#dbeafe' : '#f3e8ff',
                                            color: item.type === 'Stock Purchase' ? '#1e40af' : '#7c3aed'
                                          }}>
                                            {item.type}
                                          </span>
                                        </TableCell>
                                        <TableCell className="text-right">${item.amount_ex_gst.toFixed(2)}</TableCell>
                                        <TableCell className="text-right">${item.gst.toFixed(2)}</TableCell>
                                        <TableCell className="text-right font-medium">${item.total.toFixed(2)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* GST Summary */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">GST Summary</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem', textAlign: 'center' }}>
                              <div>
                                <div className="text-sm text-muted-foreground">GST Collected</div>
                                <div className="text-xl font-bold" style={{ color: '#16a34a' }}>${profitLossData.income.gst_collected.toFixed(2)}</div>
                              </div>
                              <div>
                                <div className="text-sm text-muted-foreground">GST Paid</div>
                                <div className="text-xl font-bold" style={{ color: '#dc2626' }}>${profitLossData.expenses.gst_paid.toFixed(2)}</div>
                              </div>
                              <div>
                                <div className="text-sm text-muted-foreground">GST Owing to ATO</div>
                                <div className="text-xl font-bold" style={{ color: profitLossData.summary.gst_position >= 0 ? '#dc2626' : '#16a34a' }}>
                                  ${Math.abs(profitLossData.summary.gst_position).toFixed(2)}
                                  {profitLossData.summary.gst_position < 0 ? ' (Refund)' : ''}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground py-8">Loading profit & loss data...</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Expense Report Tab */}
            <TabsContent value="expense-report">
              <div className="expense-report-container" data-testid="expense-report-tab">
                {/* Filters */}
                <Card style={{ marginBottom: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle className="text-lg">Expense Report Filters</CardTitle>
                    <CardDescription>
                      Date range is set by the global filter at the top of the page. Use the category dropdown below to drill in further.
                      {!includeInternal && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>
                          · Internal/personal items EXCLUDED
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', padding: '0.5rem 0.75rem', backgroundColor: includeInternal ? '#fef3c7' : '#f1f5f9', border: '1px solid', borderColor: includeInternal ? '#fcd34d' : '#cbd5e1', borderRadius: '0.5rem', width: 'fit-content' }}>
                      <input
                        type="checkbox"
                        id="exp-rep-include-internal"
                        checked={includeInternal}
                        onChange={(e) => setIncludeInternal(e.target.checked)}
                        data-testid="exp-rep-include-internal-toggle"
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="exp-rep-include-internal" style={{ fontSize: '0.8rem', cursor: 'pointer', color: '#334155' }}>
                        Include internal/personal expenses
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', alignItems: 'end', marginBottom: '1rem' }}>
                      <div>
                        <Label>Category</Label>
                        <Select
                          value={expenseReportFilters.category || 'all'}
                          onValueChange={(value) => setExpenseReportFilters({ ...expenseReportFilters, category: value === 'all' ? '' : value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All categories" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {(() => {
                              // Feb 2026 — prefer the current code+name list (same source
                              // the Expense entry form uses) so newly-added categories show
                              // here immediately. Falls back to the legacy flat list.
                              const withCodes = settings?.expense_categories_with_codes || [];
                              if (withCodes.length > 0) {
                                return withCodes.map((cat) => (
                                  <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                                ));
                              }
                              return (settings?.expense_categories || []).map((cat) => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Button
                          variant="outline"
                          onClick={() => setExpenseReportFilters({ startDate: '', endDate: '', category: '' })}
                          data-testid="clear-expense-filters"
                          style={{ width: '100%' }}
                        >
                          Clear Category
                        </Button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const { start, end } = getDateBoundaries(dateRange, startDate, endDate);
                          const params = new URLSearchParams();
                          if (start) params.append('start_date', start);
                          if (end) params.append('end_date', end);
                          if (expenseReportFilters.category) params.append('category', expenseReportFilters.category);
                          if (!includeInternal) params.append('internal_filter', 'business');
                          window.open(`${API}/expenses/export/csv?${params.toString()}`, '_blank');
                        }}
                        data-testid="export-expenses-csv"
                        style={{ backgroundColor: '#10b981', color: 'white' }}
                      >
                        📊 Export CSV
                      </Button>
                      <Button
                        onClick={() => {
                            const totalExpenses = expenseReportData.reduce((sum, e) => sum + (e.total_amount || 0), 0);
                            const totalExGST = expenseReportData.reduce((sum, e) => sum + (e.amount_ex_gst || 0), 0);
                            const totalGST = expenseReportData.reduce((sum, e) => sum + (e.gst_amount || 0), 0);
                            
                            const filterInfo = [];
                            const _bounds = getDateBoundaries(dateRange, startDate, endDate);
                            if (_bounds.start) filterInfo.push(`From: ${_bounds.start}`);
                            if (_bounds.end) filterInfo.push(`To: ${_bounds.end}`);
                            if (expenseReportFilters.category) filterInfo.push(`Category: ${expenseReportFilters.category}`);
                            filterInfo.push(`Internal/Personal items: ${includeInternal ? 'INCLUDED' : 'EXCLUDED'}`);
                            
                            const contentHtml = `
                              ${filterInfo.length > 0 ? `<div style="margin-bottom: 15px; padding: 10px; background: #f0f0f0; border-radius: 6px; font-size: 12px;">
                                <strong>Filters Applied:</strong> ${filterInfo.join(' | ')}
                              </div>` : ''}
                              
                              <div class="summary-grid" style="grid-template-columns: repeat(4, 1fr);">
                                <div class="summary-card red">
                                  <div class="summary-label">Total Expenses</div>
                                  <div class="summary-value red">$${totalExpenses.toFixed(2)}</div>
                                </div>
                                <div class="summary-card">
                                  <div class="summary-label">Amount (ex GST)</div>
                                  <div class="summary-value">$${totalExGST.toFixed(2)}</div>
                                </div>
                                <div class="summary-card green">
                                  <div class="summary-label">GST Claimable</div>
                                  <div class="summary-value green">$${totalGST.toFixed(2)}</div>
                                </div>
                                <div class="summary-card blue">
                                  <div class="summary-label">Expense Count</div>
                                  <div class="summary-value blue">${expenseReportData.length}</div>
                                </div>
                              </div>
                              
                              <div class="section-title">Expense Details</div>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Description</th>
                                    <th>Category</th>
                                    <th>Supplier</th>
                                    <th class="text-right">Ex GST</th>
                                    <th class="text-right">GST</th>
                                    <th class="text-right">Total</th>
                                    <th>Payment</th>
                                    <th>Internal</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  ${expenseReportData.length === 0 ? `
                                    <tr>
                                      <td colspan="9" style="text-align: center; padding: 20px; color: #666;">No expenses found for the selected filters.</td>
                                    </tr>
                                  ` : expenseReportData.map(exp => `
                                    <tr${exp.is_internal ? ' style="background: #fffbeb;"' : ''}>
                                      <td>${formatDate(exp.date)}</td>
                                      <td>${exp.description || ''}${exp.reference ? `<br><span style="font-size: 10px; color: #666;">Ref: ${exp.reference}</span>` : ''}</td>
                                      <td>${exp.category || ''}</td>
                                      <td>${exp.supplier_name || '-'}</td>
                                      <td class="text-right">$${(exp.amount_ex_gst || 0).toFixed(2)}</td>
                                      <td class="text-right">$${(exp.gst_amount || 0).toFixed(2)}</td>
                                      <td class="text-right">$${(exp.total_amount || 0).toFixed(2)}</td>
                                      <td>${exp.payment_method || '-'}</td>
                                      <td>${exp.is_internal ? '<span style="color:#92400e;font-weight:600;">Yes</span>' : '-'}</td>
                                    </tr>
                                  `).join('')}
                                  ${expenseReportData.length > 0 ? `
                                    <tr class="totals-row">
                                      <td colspan="4"><strong>TOTALS</strong></td>
                                      <td class="text-right"><strong>$${totalExGST.toFixed(2)}</strong></td>
                                      <td class="text-right"><strong>$${totalGST.toFixed(2)}</strong></td>
                                      <td class="text-right"><strong>$${totalExpenses.toFixed(2)}</strong></td>
                                      <td colspan="2"></td>
                                    </tr>
                                  ` : ''}
                                </tbody>
                              </table>
                            `;
                            openPrintPreview('Expense Report', contentHtml, { printerHint: settings?.printer_job_card });
                          }}
                          data-testid="print-expense-report"
                          style={{ backgroundColor: '#6366f1', color: 'white' }}
                        >
                          🖨️ Print
                        </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Total Expenses</CardDescription>
                      <CardTitle className="text-2xl" style={{ color: '#dc2626' }}>
                        ${expenseReportData.reduce((sum, e) => sum + e.total_amount, 0).toFixed(2)}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Amount (ex GST)</CardDescription>
                      <CardTitle className="text-2xl">
                        ${expenseReportData.reduce((sum, e) => sum + e.amount_ex_gst, 0).toFixed(2)}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>GST Claimable</CardDescription>
                      <CardTitle className="text-2xl" style={{ color: '#16a34a' }}>
                        ${expenseReportData.reduce((sum, e) => sum + e.gst_amount, 0).toFixed(2)}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Expense Count</CardDescription>
                      <CardTitle className="text-2xl">{expenseReportData.length}</CardTitle>
                    </CardHeader>
                  </Card>
                </div>

                {/* Expense Trends Chart */}
                {expenseTrends.length > 0 && (
                  <Card style={{ marginBottom: '1.5rem' }}>
                    <CardHeader>
                      <CardTitle>Monthly Expense Trends</CardTitle>
                      <CardDescription>Last 12 months of expense spend. Click any bar to filter the table below to that month.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={expenseTrends}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                          <Bar
                            dataKey="total_amount"
                            fill="#dc2626"
                            name="Total Expenses"
                            style={{ cursor: 'pointer' }}
                            onClick={(data) => {
                              if (!data || !data.month) return;
                              setExpenseMonthFilter(prev => prev === data.month ? null : data.month);
                              setTimeout(() => {
                                const el = document.querySelector('[data-testid="expense-details-card"]');
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }, 50);
                            }}
                          >
                            {expenseTrends.map((entry, idx) => (
                              <Cell
                                key={`bar-${idx}`}
                                fill={expenseMonthFilter === entry.month ? '#7f1d1d' : '#dc2626'}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Expenses by Category Pie Chart */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle>Expenses by Category</CardTitle>
                      <CardDescription>Category share of total spend in the period. Slices under 3% are grouped as "Other". Click any slice to filter the table below.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const categoryData = {};
                        expenseReportData.forEach(e => {
                          categoryData[e.category] = (categoryData[e.category] || 0) + e.total_amount;
                        });
                        const raw = Object.entries(categoryData).map(([name, value]) => ({ name, value }));
                        const totalAll = raw.reduce((s, d) => s + d.value, 0);
                        const THRESHOLD = 0.03; // 3%
                        const big = raw.filter(d => totalAll > 0 && (d.value / totalAll) >= THRESHOLD).sort((a, b) => b.value - a.value);
                        const small = raw.filter(d => totalAll > 0 && (d.value / totalAll) < THRESHOLD);
                        const otherTotal = small.reduce((s, d) => s + d.value, 0);
                        const otherNames = small.map(d => d.name);
                        const pieData = otherTotal > 0
                          ? [...big, { name: `Other (${small.length})`, value: otherTotal, isOther: true, otherValues: otherNames }]
                          : big;
                        const handleSliceClick = (payload) => {
                          if (!payload) return;
                          if (payload.isOther) {
                            setExpensePieFilter({ by: 'category', value: payload.name, otherValues: payload.otherValues });
                          } else {
                            setExpensePieFilter({ by: 'category', value: payload.name });
                          }
                          // Scroll to the details table
                          setTimeout(() => {
                            const el = document.querySelector('[data-testid="expense-details-card"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 50);
                        };
                        return pieData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={340}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={110}
                                paddingAngle={1}
                                fill="#8884d8"
                                dataKey="value"
                                onClick={handleSliceClick}
                                style={{ cursor: 'pointer' }}
                              >
                                {pieData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                              <Legend
                                layout="vertical"
                                align="right"
                                verticalAlign="middle"
                                wrapperStyle={{ fontSize: '0.75rem', paddingLeft: '0.5rem', cursor: 'pointer' }}
                                onClick={(o) => handleSliceClick(o.payload)}
                                formatter={(value, entry) => {
                                  const pct = totalAll > 0 ? ((entry.payload.value / totalAll) * 100).toFixed(1) : 0;
                                  return `${value} (${pct}%)`;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="text-center text-muted-foreground py-8">No expense data</div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Expenses by Payment Method</CardTitle>
                      <CardDescription>How you paid — matches your bank / card statements. Slices under 3% are grouped as "Other". Click any slice to filter the table below.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const methodData = {};
                        expenseReportData.forEach(e => {
                          methodData[e.payment_method] = (methodData[e.payment_method] || 0) + e.total_amount;
                        });
                        const raw = Object.entries(methodData).map(([name, value]) => ({ name, value }));
                        const totalAll = raw.reduce((s, d) => s + d.value, 0);
                        const THRESHOLD = 0.03;
                        const big = raw.filter(d => totalAll > 0 && (d.value / totalAll) >= THRESHOLD).sort((a, b) => b.value - a.value);
                        const small = raw.filter(d => totalAll > 0 && (d.value / totalAll) < THRESHOLD);
                        const otherTotal = small.reduce((s, d) => s + d.value, 0);
                        const otherNames = small.map(d => d.name);
                        const pieData = otherTotal > 0
                          ? [...big, { name: `Other (${small.length})`, value: otherTotal, isOther: true, otherValues: otherNames }]
                          : big;
                        const handleSliceClick = (payload) => {
                          if (!payload) return;
                          if (payload.isOther) {
                            setExpensePieFilter({ by: 'payment_method', value: payload.name, otherValues: payload.otherValues });
                          } else {
                            setExpensePieFilter({ by: 'payment_method', value: payload.name });
                          }
                          setTimeout(() => {
                            const el = document.querySelector('[data-testid="expense-details-card"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 50);
                        };
                        return pieData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={340}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={110}
                                paddingAngle={1}
                                fill="#8884d8"
                                dataKey="value"
                                onClick={handleSliceClick}
                                style={{ cursor: 'pointer' }}
                              >
                                {pieData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                              <Legend
                                layout="vertical"
                                align="right"
                                verticalAlign="middle"
                                wrapperStyle={{ fontSize: '0.75rem', paddingLeft: '0.5rem', cursor: 'pointer' }}
                                onClick={(o) => handleSliceClick(o.payload)}
                                formatter={(value, entry) => {
                                  const pct = totalAll > 0 ? ((entry.payload.value / totalAll) * 100).toFixed(1) : 0;
                                  return `${value} (${pct}%)`;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="text-center text-muted-foreground py-8">No expense data</div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                {/* Expense List Table */}
                <Card data-testid="expense-details-card">
                  <CardHeader>
                    <CardTitle>Expense Details</CardTitle>
                    {(expensePieFilter || expenseMonthFilter) && (() => {
                      // Feb 2026 — Combined-filter row (chart click filters
                      // stack: pie slice ∩ trend bar). Each chip clears
                      // independently so the user can peel back to just one.
                      const filteredCount = expenseReportData.filter(e => {
                        if (expensePieFilter) {
                          if (expensePieFilter.otherValues) {
                            const inGroup = expensePieFilter.by === 'category'
                              ? expensePieFilter.otherValues.includes(e.category)
                              : expensePieFilter.otherValues.includes(e.payment_method);
                            if (!inGroup) return false;
                          } else {
                            const match = expensePieFilter.by === 'category'
                              ? e.category === expensePieFilter.value
                              : e.payment_method === expensePieFilter.value;
                            if (!match) return false;
                          }
                        }
                        if (expenseMonthFilter) {
                          const em = (e.date || '').slice(0, 7);
                          if (em !== expenseMonthFilter) return false;
                        }
                        return true;
                      }).length;
                      const chipStyle = {
                        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                        backgroundColor: '#dbeafe', color: '#1e40af',
                        padding: '0.25rem 0.6rem', borderRadius: '999px',
                        fontSize: '0.8rem', fontWeight: 500,
                      };
                      const btnStyle = {
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: '#1e40af', fontWeight: 700, padding: '0 0 0 0.25rem',
                        fontSize: '1rem', lineHeight: 1,
                      };
                      return (
                        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.85rem', color: '#475569' }}>Filtered:</span>
                          {expensePieFilter && (
                            <span data-testid="expense-pie-filter-chip" style={chipStyle}>
                              {expensePieFilter.by === 'category' ? '📂 Category:' : '💳 Payment method:'} {expensePieFilter.value}
                              {expensePieFilter.otherValues && (
                                <span style={{ opacity: 0.7 }} title={expensePieFilter.otherValues.join(', ')}>
                                  ({expensePieFilter.otherValues.length} values)
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => setExpensePieFilter(null)}
                                data-testid="expense-pie-filter-clear"
                                style={btnStyle}
                                title="Clear category / payment method filter"
                              >×</button>
                            </span>
                          )}
                          {expenseMonthFilter && (
                            <span data-testid="expense-month-filter-chip" style={chipStyle}>
                              📅 Month: {expenseMonthFilter}
                              <button
                                type="button"
                                onClick={() => setExpenseMonthFilter(null)}
                                data-testid="expense-month-filter-clear"
                                style={btnStyle}
                                title="Clear month filter"
                              >×</button>
                            </span>
                          )}
                          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            Showing {filteredCount} of {expenseReportData.length} expenses
                          </span>
                        </div>
                      );
                    })()}
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Supplier</TableHead>
                          <TableHead className="text-right">Amount (ex GST)</TableHead>
                          <TableHead className="text-right">GST</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead>Payment Method</TableHead>
                          <TableHead style={{ width: '4.5rem' }}></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const filtered = (expensePieFilter || expenseMonthFilter)
                            ? expenseReportData.filter(e => {
                                if (expensePieFilter) {
                                  if (expensePieFilter.otherValues) {
                                    const inGroup = expensePieFilter.by === 'category'
                                      ? expensePieFilter.otherValues.includes(e.category)
                                      : expensePieFilter.otherValues.includes(e.payment_method);
                                    if (!inGroup) return false;
                                  } else {
                                    const match = expensePieFilter.by === 'category'
                                      ? e.category === expensePieFilter.value
                                      : e.payment_method === expensePieFilter.value;
                                    if (!match) return false;
                                  }
                                }
                                if (expenseMonthFilter) {
                                  const em = (e.date || '').slice(0, 7);
                                  if (em !== expenseMonthFilter) return false;
                                }
                                return true;
                              })
                            : expenseReportData;
                          if (filtered.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                  {(expensePieFilter || expenseMonthFilter) ? 'No expenses match the active filter(s).' : 'No expenses found for the selected filters.'}
                                </TableCell>
                              </TableRow>
                            );
                          }
                          return filtered.map((expense) => (
                            <TableRow key={expense.id}>
                              <TableCell>{formatDate(expense.date)}</TableCell>
                              <TableCell>
                                <div>{expense.description}</div>
                                {expense.reference && <div className="text-xs text-muted-foreground">Ref: {expense.reference}</div>}
                              </TableCell>
                              <TableCell>{expense.category}</TableCell>
                              <TableCell>{expense.supplier_name || '-'}</TableCell>
                              <TableCell className="text-right">${expense.amount_ex_gst.toFixed(2)}</TableCell>
                              <TableCell className="text-right">${expense.gst_amount.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-medium">${expense.total_amount.toFixed(2)}</TableCell>
                              <TableCell>{expense.payment_method}</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/expenses?highlight=${expense.id}`)}
                                  data-testid={`expense-view-btn-${expense.id}`}
                                  title="Open this expense on the Expenses page"
                                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
                                >
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          ));
                        })()}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* BAS Report Tab */}
            <TabsContent value="bas-report">
              <div className="bas-report-container" data-testid="bas-report-tab">
                {/* Quarter Selection */}
                <Card style={{ marginBottom: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>
                      Business Activity Statement (BAS)
                      <HelpTooltip 
                        content="Calculates GST collected (from sales) vs GST paid (on purchases/expenses) for ATO reporting. Select a quarter to match your BAS lodgment period." 
                        side="right"
                      />
                    </CardTitle>
                    <CardDescription>GST reporting for the Australian Taxation Office (ATO)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '1rem', alignItems: 'end' }}>
                      <div>
                        <Label>Select Quarter</Label>
                        <Select
                          value={basQuarter || 'current'}
                          onValueChange={(value) => {
                            setBasQuarter(value === 'current' ? '' : value);
                            fetchBasReport(value === 'current' ? '' : value);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Current Quarter" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="current">Current Quarter</SelectItem>
                            {generateQuarterOptions().map((q) => (
                              <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={() => fetchBasReport(basQuarter)} data-testid="refresh-bas-btn">
                        Refresh
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const params = basQuarter ? `?quarter=${basQuarter}` : '';
                          window.open(`${API}/bas-report/export${params}`, '_blank');
                        }}
                        data-testid="export-bas-csv"
                        style={{ backgroundColor: '#10b981', color: 'white' }}
                      >
                        📊 Export CSV
                      </Button>
                      <button 
                        type="button"
                        onClick={() => {
                          if (!basReportData) {
                            alert('Please wait for data to load before printing.');
                            return;
                          }
                          const contentHtml = `
                            <div style="margin-bottom: 15px; padding: 10px; background: #f1f5f9; border-radius: 6px;">
                              <strong>${basReportData.period.description}</strong>
                            </div>

                            <div class="summary-grid" style="grid-template-columns: repeat(4, 1fr);">
                              <div class="summary-card green">
                                <div class="summary-label">1A - GST Collected</div>
                                <div class="summary-value green">$${basReportData.gst_calculation.label_1A_gst_collected.toFixed(2)}</div>
                                <div style="font-size: 11px; color: #666; margin-top: 4px;">From ${basReportData.sales.transaction_count} sales</div>
                              </div>
                              <div class="summary-card red">
                                <div class="summary-label">1B - GST Credits</div>
                                <div class="summary-value red">$${basReportData.gst_calculation.label_1B_gst_credits.toFixed(2)}</div>
                                <div style="font-size: 11px; color: #666; margin-top: 4px;">From ${basReportData.purchases.transaction_count} purchases</div>
                              </div>
                              <div class="summary-card ${basReportData.gst_calculation.net_gst_payable >= 0 ? 'yellow' : 'blue'}">
                                <div class="summary-label">Net GST</div>
                                <div class="summary-value" style="color: ${basReportData.gst_calculation.net_gst_payable >= 0 ? '#d97706' : '#2563eb'};">$${Math.abs(basReportData.gst_calculation.net_gst_payable).toFixed(2)}</div>
                                <div style="font-size: 11px; font-weight: 600; color: ${basReportData.gst_calculation.net_gst_payable >= 0 ? '#d97706' : '#2563eb'}; margin-top: 4px;">${basReportData.gst_calculation.status}</div>
                              </div>
                              <div class="summary-card">
                                <div class="summary-label">Net Profit (ex GST)</div>
                                <div class="summary-value" style="color: ${basReportData.summary.net_profit_ex_gst >= 0 ? '#16a34a' : '#dc2626'};">$${basReportData.summary.net_profit_ex_gst.toFixed(2)}</div>
                                <div style="font-size: 11px; color: #666; margin-top: 4px;">Income minus expenses</div>
                              </div>
                            </div>

                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0;">
                              <div>
                                <div class="section-title" style="color: #16a34a;">Sales (Income)</div>
                                <table>
                                  <tbody>
                                    <tr>
                                      <td><strong>G1 - Total Sales (inc GST)</strong></td>
                                      <td class="text-right"><strong>$${basReportData.sales.G1_total_sales_including_gst.toFixed(2)}</strong></td>
                                    </tr>
                                    <tr>
                                      <td>Sales (ex GST)</td>
                                      <td class="text-right">$${basReportData.sales.sales_ex_gst.toFixed(2)}</td>
                                    </tr>
                                    <tr class="totals-row" style="background: #f0fdf4;">
                                      <td><strong>1A - GST on Sales</strong></td>
                                      <td class="text-right"><strong>$${basReportData.sales.label_1A_gst_on_sales.toFixed(2)}</strong></td>
                                    </tr>
                                    <tr>
                                      <td style="color: #666;">Transactions</td>
                                      <td class="text-right" style="color: #666;">${basReportData.sales.transaction_count}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <div>
                                <div class="section-title" style="color: #dc2626;">Purchases (Expenses)</div>
                                <table>
                                  <tbody>
                                    <tr>
                                      <td><strong>G11 - Non-capital Purchases (inc GST)</strong></td>
                                      <td class="text-right"><strong>$${basReportData.purchases.G11_non_capital_purchases_including_gst.toFixed(2)}</strong></td>
                                    </tr>
                                    <tr>
                                      <td>Purchases (ex GST)</td>
                                      <td class="text-right">$${basReportData.purchases.purchases_ex_gst.toFixed(2)}</td>
                                    </tr>
                                    <tr class="totals-row" style="background: #fef2f2;">
                                      <td><strong>1B - GST on Purchases</strong></td>
                                      <td class="text-right"><strong>$${basReportData.purchases.label_1B_gst_on_purchases.toFixed(2)}</strong></td>
                                    </tr>
                                    <tr>
                                      <td style="color: #666;">Transactions</td>
                                      <td class="text-right" style="color: #666;">${basReportData.purchases.transaction_count}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            ${basReportData.purchases.by_category.length > 0 ? `
                              <div class="section-title">Purchases by Category</div>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Category</th>
                                    <th class="text-right">Total (inc GST)</th>
                                    <th class="text-right">GST</th>
                                    <th class="text-right">Count</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  ${basReportData.purchases.by_category.map(cat => `
                                    <tr>
                                      <td>${cat.category}</td>
                                      <td class="text-right">$${cat.total_including_gst.toFixed(2)}</td>
                                      <td class="text-right">$${cat.gst.toFixed(2)}</td>
                                      <td class="text-right">${cat.count}</td>
                                    </tr>
                                  `).join('')}
                                </tbody>
                              </table>
                            ` : ''}

                            <div class="section-title">GST Calculation for BAS</div>
                            <div style="max-width: 400px; padding: 15px; background: #f8fafc; border-radius: 8px;">
                              <div style="display: grid; grid-template-columns: 1fr auto; gap: 8px;">
                                <div>1A - GST on Sales (Collected)</div>
                                <div class="text-right" style="font-weight: 500;">$${basReportData.gst_calculation.label_1A_gst_collected.toFixed(2)}</div>
                                
                                <div>1B - GST on Purchases (Credits)</div>
                                <div class="text-right" style="font-weight: 500;">- $${basReportData.gst_calculation.label_1B_gst_credits.toFixed(2)}</div>
                                
                                <div style="border-top: 2px solid #e2e8f0; padding-top: 8px; margin-top: 8px; font-weight: 700;">Net GST Amount</div>
                                <div style="border-top: 2px solid #e2e8f0; padding-top: 8px; margin-top: 8px; text-align: right; font-weight: 700;">$${basReportData.gst_calculation.net_gst_payable.toFixed(2)}</div>
                                
                                <div style="grid-column: span 2; margin-top: 8px; color: ${basReportData.gst_calculation.net_gst_payable >= 0 ? '#d97706' : '#2563eb'}; font-weight: 600;">
                                  ${basReportData.gst_calculation.status}
                                  ${basReportData.gst_calculation.net_gst_payable > 0 ? ' - This amount is payable to the ATO' : ''}
                                  ${basReportData.gst_calculation.net_gst_payable < 0 ? ' - You may be entitled to a refund' : ''}
                                </div>
                              </div>
                            </div>
                          `;
                          openPrintPreview('Business Activity Statement (BAS)', contentHtml, { printerHint: settings?.printer_job_card });
                        }}
                        data-testid="print-bas-report"
                        style={{ 
                          backgroundColor: '#6366f1', 
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                      >
                        🖨️ Print
                      </button>
                    </div>
                  </CardContent>
                </Card>

                {basReportData ? (
                  <>
                    {/* Period Info */}
                    <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', backgroundColor: '#f1f5f9', borderRadius: '8px' }}>
                      <strong>{basReportData.period.description}</strong>
                      <span style={{ float: 'right', fontSize: '0.875rem', color: '#64748b' }}>
                        Printed: {new Date().toLocaleDateString('en-AU')}
                      </span>
                    </div>

                    {/* GST Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                      <Card style={{ backgroundColor: '#f0fdf4' }}>
                        <CardHeader className="pb-2">
                          <CardDescription>1A - GST Collected</CardDescription>
                          <CardTitle className="text-2xl" style={{ color: '#16a34a' }}>
                            ${basReportData.gst_calculation.label_1A_gst_collected.toFixed(2)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="text-xs text-muted-foreground">From {basReportData.sales.transaction_count} sales</div>
                        </CardContent>
                      </Card>
                      <Card style={{ backgroundColor: '#fef2f2' }}>
                        <CardHeader className="pb-2">
                          <CardDescription>1B - GST Credits</CardDescription>
                          <CardTitle className="text-2xl" style={{ color: '#dc2626' }}>
                            ${basReportData.gst_calculation.label_1B_gst_credits.toFixed(2)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="text-xs text-muted-foreground">From {basReportData.purchases.transaction_count} purchases</div>
                        </CardContent>
                      </Card>
                      <Card style={{ backgroundColor: basReportData.gst_calculation.net_gst_payable >= 0 ? '#fef3c7' : '#dbeafe' }}>
                        <CardHeader className="pb-2">
                          <CardDescription>Net GST</CardDescription>
                          <CardTitle className="text-2xl" style={{ color: basReportData.gst_calculation.net_gst_payable >= 0 ? '#d97706' : '#2563eb' }}>
                            ${Math.abs(basReportData.gst_calculation.net_gst_payable).toFixed(2)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="text-xs font-medium" style={{ color: basReportData.gst_calculation.net_gst_payable >= 0 ? '#d97706' : '#2563eb' }}>
                            {basReportData.gst_calculation.status}
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardDescription>Net Profit (ex GST)</CardDescription>
                          <CardTitle className="text-2xl" style={{ color: basReportData.summary.net_profit_ex_gst >= 0 ? '#16a34a' : '#dc2626' }}>
                            ${basReportData.summary.net_profit_ex_gst.toFixed(2)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="text-xs text-muted-foreground">Income minus expenses</div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Sales and Purchases Details */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                      {/* Sales Section */}
                      <Card>
                        <CardHeader 
                          style={{ backgroundColor: '#f0fdf4', cursor: 'pointer' }}
                          onClick={() => setBasSalesExpanded(!basSalesExpanded)}
                          data-testid="bas-sales-header"
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CardTitle className="text-lg" style={{ color: '#16a34a' }}>Sales (Income)</CardTitle>
                            <span style={{ color: '#16a34a' }}>{basSalesExpanded ? '▼' : '▶'} View Details</span>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell className="font-medium">G1 - Total Sales (inc GST)</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.sales.G1_total_sales_including_gst.toFixed(2)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Sales (ex GST)</TableCell>
                                <TableCell className="text-right">${basReportData.sales.sales_ex_gst.toFixed(2)}</TableCell>
                              </TableRow>
                              <TableRow style={{ backgroundColor: '#f0fdf4' }}>
                                <TableCell className="font-medium">1A - GST on Sales</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.sales.label_1A_gst_on_sales.toFixed(2)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="text-muted-foreground">Transactions</TableCell>
                                <TableCell className="text-right text-muted-foreground">{basReportData.sales.transaction_count}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>

                      {/* Purchases Section */}
                      <Card>
                        <CardHeader 
                          style={{ backgroundColor: '#fef2f2', cursor: 'pointer' }}
                          onClick={() => setBasPurchasesExpanded(!basPurchasesExpanded)}
                          data-testid="bas-purchases-header"
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CardTitle className="text-lg" style={{ color: '#dc2626' }}>Purchases (Expenses)</CardTitle>
                            <span style={{ color: '#dc2626' }}>{basPurchasesExpanded ? '▼' : '▶'} View Details</span>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell className="font-medium">G11 - Non-capital Purchases (inc GST)</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.purchases.G11_non_capital_purchases_including_gst.toFixed(2)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Purchases (ex GST)</TableCell>
                                <TableCell className="text-right">${basReportData.purchases.purchases_ex_gst.toFixed(2)}</TableCell>
                              </TableRow>
                              <TableRow style={{ backgroundColor: '#fef2f2' }}>
                                <TableCell className="font-medium">1B - GST on Purchases</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.purchases.label_1B_gst_on_purchases.toFixed(2)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="text-muted-foreground">Transactions</TableCell>
                                <TableCell className="text-right text-muted-foreground">{basReportData.purchases.transaction_count}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Expanded Sales Details */}
                    {basSalesExpanded && basReportData.sales.details && basReportData.sales.details.length > 0 && (
                      <Card style={{ marginBottom: '1.5rem', borderColor: '#16a34a', borderWidth: '2px' }}>
                        <CardHeader style={{ backgroundColor: '#f0fdf4' }}>
                          <CardTitle className="text-lg" style={{ color: '#16a34a' }}>Sales Transactions Detail</CardTitle>
                          <CardDescription>{basReportData.sales.details.length} transactions in this period</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Job #</TableHead>
                                <TableHead>Customer</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Amount (ex GST)</TableHead>
                                <TableHead className="text-right">GST</TableHead>
                                <TableHead className="text-right">Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {basReportData.sales.details.map((sale, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>{formatDate(sale.date)}</TableCell>
                                  <TableCell className="font-medium">{sale.job_number || '-'}</TableCell>
                                  <TableCell>{sale.customer_name}</TableCell>
                                  <TableCell>{sale.description}</TableCell>
                                  <TableCell>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-100">
                                      {sale.status}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right">${sale.amount_ex_gst.toFixed(2)}</TableCell>
                                  <TableCell className="text-right">${sale.gst.toFixed(2)}</TableCell>
                                  <TableCell className="text-right font-medium">${sale.total.toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow style={{ backgroundColor: '#f0fdf4' }}>
                                <TableCell colSpan={5} className="font-bold">TOTALS</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.sales.sales_ex_gst.toFixed(2)}</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.sales.label_1A_gst_on_sales.toFixed(2)}</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.sales.G1_total_sales_including_gst.toFixed(2)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}

                    {/* Expanded Purchases Details */}
                    {basPurchasesExpanded && basReportData.purchases.details && basReportData.purchases.details.length > 0 && (
                      <Card style={{ marginBottom: '1.5rem', borderColor: '#dc2626', borderWidth: '2px' }}>
                        <CardHeader style={{ backgroundColor: '#fef2f2' }}>
                          <CardTitle className="text-lg" style={{ color: '#dc2626' }}>Purchases Transactions Detail</CardTitle>
                          <CardDescription>{basReportData.purchases.details.length} transactions in this period</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Supplier</TableHead>
                                <TableHead>Payment</TableHead>
                                <TableHead className="text-right">Amount (ex GST)</TableHead>
                                <TableHead className="text-right">GST</TableHead>
                                <TableHead className="text-right">Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {basReportData.purchases.details.map((purchase, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>{formatDate(purchase.date)}</TableCell>
                                  <TableCell>{purchase.description}</TableCell>
                                  <TableCell>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-100">
                                      {purchase.category}
                                    </span>
                                  </TableCell>
                                  <TableCell>{purchase.supplier || '-'}</TableCell>
                                  <TableCell>{purchase.payment_method}</TableCell>
                                  <TableCell className="text-right">${purchase.amount_ex_gst.toFixed(2)}</TableCell>
                                  <TableCell className="text-right">${purchase.gst.toFixed(2)}</TableCell>
                                  <TableCell className="text-right font-medium">${purchase.total.toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow style={{ backgroundColor: '#fef2f2' }}>
                                <TableCell colSpan={5} className="font-bold">TOTALS</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.purchases.purchases_ex_gst.toFixed(2)}</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.purchases.label_1B_gst_on_purchases.toFixed(2)}</TableCell>
                                <TableCell className="text-right font-bold">${basReportData.purchases.G11_non_capital_purchases_including_gst.toFixed(2)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}

                    {/* Purchases by Category */}
                    {basReportData.purchases.by_category.length > 0 && (
                      <Card style={{ marginBottom: '1.5rem' }}>
                        <CardHeader>
                          <CardTitle className="text-lg">Purchases by Category</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Category</TableHead>
                                <TableHead className="text-right">Total (inc GST)</TableHead>
                                <TableHead className="text-right">GST</TableHead>
                                <TableHead className="text-right">Count</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {basReportData.purchases.by_category.map((cat, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>{cat.category}</TableCell>
                                  <TableCell className="text-right">${cat.total_including_gst.toFixed(2)}</TableCell>
                                  <TableCell className="text-right">${cat.gst.toFixed(2)}</TableCell>
                                  <TableCell className="text-right">{cat.count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}

                    {/* GST Calculation Summary */}
                    <Card style={{ backgroundColor: '#f8fafc' }}>
                      <CardHeader>
                        <CardTitle className="text-lg">GST Calculation for BAS</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', maxWidth: '400px' }}>
                          <div>1A - GST on Sales (Collected)</div>
                          <div className="text-right font-medium">${basReportData.gst_calculation.label_1A_gst_collected.toFixed(2)}</div>
                          
                          <div>1B - GST on Purchases (Credits)</div>
                          <div className="text-right font-medium">- ${basReportData.gst_calculation.label_1B_gst_credits.toFixed(2)}</div>
                          
                          <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '0.5rem', marginTop: '0.5rem' }} className="font-bold">
                            Net GST Amount
                          </div>
                          <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '0.5rem', marginTop: '0.5rem' }} className="text-right font-bold">
                            ${basReportData.gst_calculation.net_gst_payable.toFixed(2)}
                          </div>
                          
                          <div className="text-muted-foreground" style={{ gridColumn: 'span 2', marginTop: '0.5rem' }}>
                            <strong style={{ color: basReportData.gst_calculation.net_gst_payable >= 0 ? '#d97706' : '#2563eb' }}>
                              {basReportData.gst_calculation.status}
                            </strong>
                            {basReportData.gst_calculation.net_gst_payable > 0 && (
                              <span> - This amount is payable to the ATO</span>
                            )}
                            {basReportData.gst_calculation.net_gst_payable < 0 && (
                              <span> - You may be entitled to a refund</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground py-8">Loading BAS report data...</div>
                )}
              </div>
            </TabsContent>

            {/* Payment Status Tab */}
            <TabsContent value="payment">
              <div className="payment-reports">
                {/* Date-field toggle: cash-basis (money received) vs booked
                    (activity volume). Default is cash-basis to align with
                    P&L / BAS / Dashboard Revenue / Payment Methods Breakdown. */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    marginBottom: '1rem',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem',
                  }}
                  data-testid="payment-date-field-toggle"
                >
                  <div style={{ fontSize: '0.8rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <strong>Filter jobs by:</strong>{' '}
                    {paymentDateField === 'revenue'
                      ? 'Payment received date (cash-basis — matches P&L, BAS, Dashboard Revenue)'
                      : 'Booking date (activity volume — includes unpaid jobs booked in the period)'}
                    <HelpTooltip
                      content={
                        <div style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                          <div style={{ marginBottom: '0.4rem' }}>
                            <strong style={{ color: '#065f46' }}>💰 Payment date</strong> — cash-basis. Only counts jobs that have been PAID, and puts them in the period the payment landed. Matches P&L, BAS and Dashboard Revenue. Use this to answer <em>&quot;how much money did I actually receive?&quot;</em>
                          </div>
                          <div>
                            <strong style={{ color: '#1d4ed8' }}>📅 Booking date</strong> — activity view. Counts EVERY job booked in the period, paid or not. Use this to answer <em>&quot;how many jobs came in this week?&quot;</em> or to see recently-booked jobs still awaiting payment.
                          </div>
                          <div style={{ marginTop: '0.4rem', color: '#64748b', fontSize: '0.75rem' }}>
                            This toggle only affects the Payment Status tab. Other tabs (Job Distribution, By Location, etc.) always use Booking date.
                          </div>
                        </div>
                      }
                      side="bottom"
                    />
                  </div>
                  <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: '9999px', overflow: 'hidden' }}>
                    <button
                      type="button"
                      onClick={() => setPaymentDateField('revenue')}
                      data-testid="payment-date-field-revenue"
                      style={{
                        padding: '0.35rem 0.85rem',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        border: 'none',
                        cursor: 'pointer',
                        background: paymentDateField === 'revenue' ? '#10b981' : 'transparent',
                        color: paymentDateField === 'revenue' ? '#ffffff' : '#475569',
                      }}
                    >
                      💰 Payment date
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentDateField('booked')}
                      data-testid="payment-date-field-booked"
                      style={{
                        padding: '0.35rem 0.85rem',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        border: 'none',
                        cursor: 'pointer',
                        background: paymentDateField === 'booked' ? '#2563eb' : 'transparent',
                        color: paymentDateField === 'booked' ? '#ffffff' : '#475569',
                      }}
                    >
                      📅 Booking date
                    </button>
                  </div>
                </div>
                <div className="chart-row">
                  {/* Payment Summary Cards */}
                  <Card className="chart-card">
                    <CardHeader>
                      <CardTitle>Payment Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="payment-overview-grid">
                        <div className="payment-stat-card">
                          <div className="stat-label" title="Sum of revenue_amount for jobs whose revenue_date falls in the selected period. Revenue is recognised when the job is finalised — matches P&L, BAS and Dashboard Revenue.">Total Revenue</div>
                          <div className="stat-value revenue">
                            ${(distHas ? (distributionData.summary?.total_revenue || 0) : calculateRevenue(filteredJobs)).toFixed(2)}
                          </div>
                        </div>
                        <div className="payment-stat-card">
                          <div className="stat-label" title="Sum of individual payments (bank / card / cash) actually received in the period. May differ from Total Revenue if a customer paid a deposit in an earlier period but the job was invoiced in this one, or vice versa.">Amount Collected</div>
                          <div className="stat-value collected">
                            ${(distHas ? (distributionData.summary?.total_paid || 0) : filteredJobs.reduce((sum, j) => sum + (j.amount_paid || 0), 0)).toFixed(2)}
                          </div>
                        </div>
                        <div className="payment-stat-card">
                          <div className="stat-label" title="Total amount still owing across all jobs in the selected period.">Outstanding Balance</div>
                          <div className="stat-value outstanding">
                            ${(distHas ? (distributionData.summary?.total_balance || 0) : filteredJobs.reduce((sum, j) => sum + (j.balance_owing || 0), 0)).toFixed(2)}
                          </div>
                        </div>
                        <div className="payment-stat-card">
                          <div className="stat-label" title="Amount Collected ÷ Total Revenue. 100% means everything invoiced this period has been paid.">Collection Rate</div>
                          <div className="stat-value rate">
                            {(() => {
                              const totalRevenue = distHas ? (distributionData.summary?.total_revenue || 0) : calculateRevenue(filteredJobs);
                              const collected    = distHas ? (distributionData.summary?.total_paid || 0)    : filteredJobs.reduce((sum, j) => sum + (j.amount_paid || 0), 0);
                              return totalRevenue > 0 ? ((collected / totalRevenue) * 100).toFixed(1) : '0.0';
                            })()}%
                          </div>
                        </div>
                        <div 
                          className="payment-stat-card clickable-card"
                          onClick={() => {
                            const overpaidJobs = filteredJobs.filter(j => (j.balance_owing || 0) < 0);
                            if (overpaidJobs.length > 0) {
                              setSelectedFilter({ category: 'overpaid', value: 'Overpaid' });
                            }
                          }}
                          style={{ cursor: 'pointer', backgroundColor: '#fef2f2' }}
                        >
                          <div className="stat-label">⚠️ Overpaid Jobs</div>
                          <div className="stat-value" style={{ color: '#dc2626' }}>
                            {distHas ? (distributionData.overpaid_summary?.count ?? 0) : filteredJobs.filter(j => (j.balance_owing || 0) < 0).length}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#991b1b', marginTop: '0.5rem' }}>
                            ${(distHas ? (distributionData.overpaid_summary?.overpaid_amount || 0) : Math.abs(filteredJobs.filter(j => (j.balance_owing || 0) < 0).reduce((sum, j) => sum + (j.balance_owing || 0), 0))).toFixed(2)} overpaid
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Jobs by Payment Status */}
                  <Card className="chart-card">
                    <CardHeader>
                      <CardTitle>Jobs by Payment Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Status</TableHead>
                            <TableHead>Jobs</TableHead>
                            <TableHead>Percentage</TableHead>
                            <TableHead>Total Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {['Paid', 'Awaiting Payment', 'In Progress'].map(status => {
                            // Feb 2026 — this card is an OPERATIONAL workload view
                            // (of jobs BOOKED in the selected date range, how are
                            // they distributed by payment state?). Prefer the new
                            // `activity_by_payment_status` bucket which is scoped
                            // by date_in so Awaiting Payment / In Progress jobs
                            // (which have no revenue_date) are visible with real
                            // counts. Fall back to the legacy cash-basis bucket
                            // for backend versions that haven't shipped the
                            // activity aggregation yet.
                            const bucket = ((distributionData?.activity_by_payment_status)
                              || (distributionData?.by_payment_status)
                              || []).find(b => b.name === status);
                            const cnt    = bucket?.count || 0;
                            const total  = distributionData?.activity_summary?.total_jobs
                              ?? distributionData?.summary?.total_jobs
                              ?? 0;
                            const percentage = total > 0 ? ((cnt / total) * 100).toFixed(1) : '0.0';
                            // Paid → recognised revenue. Awaiting Payment / In Progress →
                            // outstanding balance ("money on the shelf").
                            const amount = status === 'Paid'
                              ? (bucket?.revenue || bucket?.total || 0)
                              : (bucket?.balance || bucket?.total || 0);
                            const isSelected = selectedFilter?.category === 'payment_status' && selectedFilter?.value === status;
                            const statusJobs = { length: cnt };
                            return (
                              <TableRow 
                                key={status}
                                onClick={() => setSelectedFilter({ category: 'payment_status', value: status })}
                                style={{ 
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#e0f2fe' : 'transparent'
                                }}
                                data-testid={`payment-status-row-${status.replace(/\s+/g,'-').toLowerCase()}`}
                              >
                                <TableCell>
                                  <span className={`payment-status-badge ${status.toLowerCase().replace(/\s+/g,'-')}`}>
                                    {status === 'Paid' && '💰 '}
                                    {status === 'Awaiting Payment' && '⏳ '}
                                    {status === 'In Progress' && '🔧 '}
                                    {status}
                                  </span>
                                </TableCell>
                                <TableCell>{statusJobs.length}</TableCell>
                                <TableCell>{percentage}%</TableCell>
                                <TableCell>${amount.toFixed(2)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Filtered Jobs Display */}
                {selectedFilter?.category === 'payment_status' && (() => {
                  // Derive payment state the same way the server does (revenue_date +
                  // status + balance_owing). Keeps drill-in list in sync with the
                  // counts in the table above. Feb 2026 — also excludes
                  // closed-but-unpaid legacy Jim2 jobs (matches the backend fix
                  // that dropped In Progress from 6,498 → ~80).
                  const derivePayState = (j) => {
                    if (j.revenue_date) return 'Paid';
                    if (j.closed) return null; // closed-unpaid = historical noise, not workload
                    if (['Finish', 'Not Recoverable', 'Not Proceeding'].includes(j.status) && (j.balance_owing || 0) > 0) return 'Awaiting Payment';
                    return 'In Progress';
                  };
                  const matched = filteredJobs.filter(j => derivePayState(j) === selectedFilter.value);
                  return (
                  <Card style={{ marginTop: '2rem' }}>
                    <CardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                        <CardTitle>
                          {selectedFilter.value} Jobs ({matched.length})
                        </CardTitle>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {/* Bulk-close toolbar — surfaces only when the user has
                              ticked at least one row. Kept inline with the header
                              so it doesn't push the table around when empty. */}
                          {selectedJobIds.size > 0 && (
                            <>
                              <span data-testid="bulk-close-selected-count" style={{ fontSize: '0.85rem', color: '#475569' }}>
                                {selectedJobIds.size} selected
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid="bulk-close-clear"
                                onClick={() => setSelectedJobIds(new Set())}
                              >
                                Clear
                              </Button>
                              <Button
                                size="sm"
                                data-testid="bulk-close-open"
                                onClick={openBulkCloseDialog}
                                style={{ background: '#dc2626', color: '#fff' }}
                              >
                                Close {selectedJobIds.size} Selected&hellip;
                              </Button>
                            </>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedFilter(null)}
                          >
                            Clear Filter
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <SortableJobsTable
                        jobs={matched}
                        columns={[
                          { key: '_sel', sortable: false,
                            label: (
                              <input
                                type="checkbox"
                                data-testid="bulk-select-all"
                                checked={matched.length > 0 && matched.every(j => selectedJobIds.has(j.id))}
                                ref={(el) => { if (el) {
                                  const someSelected = matched.some(j => selectedJobIds.has(j.id));
                                  const allSelected = matched.length > 0 && matched.every(j => selectedJobIds.has(j.id));
                                  el.indeterminate = someSelected && !allSelected;
                                }}}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedJobIds((prev) => {
                                    const allNow = matched.length > 0 && matched.every(j => prev.has(j.id));
                                    if (allNow) return new Set();
                                    const next = new Set(prev);
                                    matched.forEach(j => next.add(j.id));
                                    return next;
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: 'pointer' }}
                              />
                            ),
                            headStyle: { width: 32, textAlign: 'center' },
                            cellStyle: () => ({ textAlign: 'center', width: 32 }),
                            render: (j) => (
                              <input
                                type="checkbox"
                                data-testid={`bulk-select-${j.job_number}`}
                                checked={selectedJobIds.has(j.id)}
                                onChange={() => toggleJobSelected(j.id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: 'pointer' }}
                              />
                            ) },
                          { key: 'job_number',    label: 'Job #',    type: 'number' },
                          { key: 'customer_name', label: 'Customer', type: 'string' },
                          { key: 'status',        label: 'Status',   type: 'string' },
                          { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${(j.total_amount || 0).toFixed(2)}` },
                          { key: 'amount_paid',   label: 'Paid',     type: 'number', cellClassName: 'paid-amount', render: (j) => `$${(j.amount_paid || 0).toFixed(2)}` },
                          { key: 'balance_owing', label: 'Balance',  type: 'number', cellClassName: 'balance-amount', render: (j) => `$${(j.balance_owing || 0).toFixed(2)}` },
                          { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                            <div style={{ display: 'flex', gap: '0.35rem' }}>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`payment-status-drill-view-${j.job_number}`}
                                onClick={() => navigate(`/job/${j.id}`)}
                              >
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`payment-status-drill-close-${j.job_number}`}
                                onClick={() => openCloseDialog(j)}
                                style={{ borderColor: '#dc2626', color: '#dc2626' }}
                                title="Mark this job closed without leaving Reports"
                              >
                                Close
                              </Button>
                            </div>
                          ) },
                        ]}
                      />
                    </CardContent>
                  </Card>
                  );
                })()}

                {/* Overpaid Jobs Display */}
                {selectedFilter?.category === 'overpaid' && (
                  <Card style={{ marginTop: '2rem' }}>
                    <CardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <CardTitle style={{ color: '#dc2626' }}>
                          ⚠️ Overpaid Jobs ({filteredJobs.filter(j => (j.balance_owing || 0) < 0).length})
                        </CardTitle>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedFilter(null)}
                        >
                          Clear Filter
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div style={{ backgroundColor: '#fef2f2', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                        <p style={{ fontSize: '0.875rem', color: '#991b1b' }}>
                          <strong>⚠️ Warning:</strong> These jobs have received more payment than their total amount. 
                          This may indicate a data entry error or refund situation. Please review and correct.
                        </p>
                      </div>
                      <SortableJobsTable
                        jobs={filteredJobs.filter(j => (j.balance_owing || 0) < 0)}
                        rowStyle={() => ({ backgroundColor: '#fef2f2' })}
                        columns={[
                          { key: 'job_number',    label: 'Job #',       type: 'number' },
                          { key: 'customer_name', label: 'Customer',    type: 'string' },
                          { key: 'status',        label: 'Status',      type: 'string' },
                          { key: 'total_amount',  label: 'Total',       type: 'number', render: (j) => `$${(j.total_amount || 0).toFixed(2)}` },
                          { key: 'amount_paid',   label: 'Paid',        type: 'number', cellClassName: 'paid-amount', render: (j) => `$${(j.amount_paid || 0).toFixed(2)}` },
                          { key: 'overpaid_by',   label: 'Overpaid By', type: 'number',
                            sortValue: (j) => Math.abs(j.balance_owing || 0),
                            cellStyle: () => ({ color: '#dc2626', fontWeight: 'bold' }),
                            render: (j) => `$${Math.abs(j.balance_owing || 0).toFixed(2)}` },
                          { key: 'actions',       label: 'Actions',     sortable: false, render: (j) => (
                            <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)}>View &amp; Fix</Button>
                          ) },
                        ]}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Payment Methods Section - Added to Payment Status Tab */}
                <div style={{ marginTop: '3rem' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1e40af' }}>
                    Payment Methods Breakdown
                  </h2>
                  {/* Direction toggle — In (customer payments received) vs Out (purchases + expenses paid) */}
                  <div style={{ display: 'inline-flex', gap: '0.25rem', marginBottom: '0.5rem', padding: '0.25rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '999px' }} data-testid="payment-direction-toggle">
                    <button
                      type="button"
                      onClick={() => setPaymentDirection('in')}
                      data-testid="payment-direction-in"
                      style={{
                        padding: '0.35rem 0.9rem', borderRadius: '999px', border: 'none', cursor: 'pointer',
                        fontSize: '0.8rem', fontWeight: 600,
                        background: paymentDirection === 'in' ? '#10b981' : 'transparent',
                        color: paymentDirection === 'in' ? '#ffffff' : '#475569',
                      }}
                    >
                      ⬇ Money In (customer payments)
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentDirection('out')}
                      data-testid="payment-direction-out"
                      style={{
                        padding: '0.35rem 0.9rem', borderRadius: '999px', border: 'none', cursor: 'pointer',
                        fontSize: '0.8rem', fontWeight: 600,
                        background: paymentDirection === 'out' ? '#ef4444' : 'transparent',
                        color: paymentDirection === 'out' ? '#ffffff' : '#475569',
                      }}
                    >
                      ⬆ Money Out (purchases + expenses)
                    </button>
                  </div>
                  {/* Footnote explaining why counts here may differ from 'Jobs by Payment Status'.
                      Added Jul 2026 after user report of 4-vs-3 mismatch. */}
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1.5rem', fontStyle: 'italic' }} data-testid="payment-methods-footnote">
                    {paymentDirection === 'in' ? (
                      <>
                        Counts individual <strong>payment records</strong> in the selected date range, not jobs. A single fully-paid job with a deposit + final payment counts as <em>2 payments</em> here but only <em>1 Paid job</em> above. Jobs marked as collected without a specific payment record won&rsquo;t appear here at all.
                      </>
                    ) : (
                      <>Aggregates <strong>Finished</strong> business purchases and expenses (excludes Booked/provisional and Internal/Personal). Scoped to the top-of-page date range so you can reconcile a single card/bank statement in one glance.</>
                    )}
                  </p>
                  {(() => {
                    // Unify the two data sources behind a single view model so
                    // the existing table/summary UI below just consumes `pmData`.
                    const pmData = paymentDirection === 'in'
                      ? (paymentMethodsData && {
                          methods: paymentMethodsData.payment_methods,
                          total_count: paymentMethodsData.total_payments,
                          total_amount: paymentMethodsData.total_amount,
                          count_label: 'Total Payments',
                          amount_label: 'Total Amount Collected',
                          count_color: '#1e40af',
                          amount_color: '#10b981',
                        })
                      : (paymentMethodsOutgoing && {
                          methods: paymentMethodsOutgoing.payment_methods,
                          total_count: paymentMethodsOutgoing.total_transactions,
                          total_amount: paymentMethodsOutgoing.total_amount,
                          count_label: 'Total Transactions',
                          amount_label: 'Total Amount Paid',
                          count_color: '#b91c1c',
                          amount_color: '#ef4444',
                        });
                    return pmData ? (
                    <>
                      {/* Summary Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                        <Card>
                          <CardContent style={{ padding: '1.5rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              {pmData.count_label}
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: pmData.count_color }}>
                              {pmData.total_count}
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent style={{ padding: '1.5rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              {pmData.amount_label}
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: pmData.amount_color }}>
                              ${pmData.total_amount.toFixed(2)}
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent style={{ padding: '1.5rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              Payment Methods Used
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
                              {pmData.methods.length}
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Payment Methods Table */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Payment Methods Analysis</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Payment Method</TableHead>
                                <TableHead>Number of {paymentDirection === 'in' ? 'Payments' : 'Transactions'}</TableHead>
                                <TableHead>Percentage</TableHead>
                                <TableHead>Total Amount</TableHead>
                                <TableHead>Average {paymentDirection === 'in' ? 'Payment' : 'Transaction'}</TableHead>
                                {paymentDirection === 'out' && <TableHead>Purchases / Expenses</TableHead>}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pmData.methods
                                .filter(method => !selectedPaymentMethod || method.method === selectedPaymentMethod)
                                .map((method) => {
                                  const avgPayment = method.count > 0 ? method.total_amount / method.count : 0;
                                  return (
                                    <TableRow 
                                      key={method.method}
                                      onClick={() => setSelectedPaymentMethod(selectedPaymentMethod === method.method ? null : method.method)}
                                      data-testid={`payment-method-row-${method.method.replace(/\s+/g, '-')}`}
                                      style={{ 
                                        cursor: 'pointer',
                                        backgroundColor: selectedPaymentMethod === method.method ? '#e0f2fe' : 'transparent'
                                      }}
                                    >
                                      <TableCell>
                                        <span style={{ 
                                          fontWeight: '600',
                                          padding: '0.25rem 0.75rem',
                                          borderRadius: '0.375rem',
                                          backgroundColor: 
                                            method.method === 'Cash' ? '#fef3c7' :
                                            method.method === 'Card' ? '#dbeafe' :
                                            method.method === 'Bank Transfer' ? '#d1fae5' :
                                            '#f3f4f6'
                                        }}>
                                          {method.method}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <strong>{method.count}</strong>
                                      </TableCell>
                                      <TableCell>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <div style={{ 
                                            flex: 1, 
                                            height: '8px', 
                                            backgroundColor: '#e5e7eb', 
                                            borderRadius: '4px',
                                            overflow: 'hidden'
                                          }}>
                                            <div style={{ 
                                              height: '100%', 
                                              width: `${method.percentage}%`, 
                                              backgroundColor: '#3b82f6',
                                              transition: 'width 0.3s ease'
                                            }}></div>
                                          </div>
                                          <span style={{ fontWeight: '600', minWidth: '50px' }}>
                                            {method.percentage.toFixed(1)}%
                                          </span>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <strong style={{ color: '#10b981' }}>
                                          ${method.total_amount.toFixed(2)}
                                        </strong>
                                      </TableCell>
                                      <TableCell>
                                        <span style={{ color: '#6b7280' }}>
                                          ${avgPayment.toFixed(2)}
                                        </span>
                                      </TableCell>
                                      {paymentDirection === 'out' && (
                                        <TableCell>
                                          <div style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.35 }}>
                                            <div>🛒 <strong>{method.purchase_count || 0}</strong> purchase{method.purchase_count === 1 ? '' : 's'} · ${(method.purchase_total || 0).toFixed(2)}</div>
                                            <div>🧾 <strong>{method.expense_count || 0}</strong> expense{method.expense_count === 1 ? '' : 's'} · ${(method.expense_total || 0).toFixed(2)}</div>
                                          </div>
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                          {selectedPaymentMethod && (
                            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem' }}>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setSelectedPaymentMethod(null)}
                              >
                                Clear Filter - Show All Payment Methods
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Inline drill-in — jobs that received a payment via the selected method
                          (only meaningful in "Money In" mode; outgoing txns don't belong to jobs). */}
                      {paymentDirection === 'in' && selectedPaymentMethod && (() => {
                        const jobsLoading = loading && (!jobs || jobs.length === 0);
                        const methodJobs = (jobs || []).filter(j =>
                          Array.isArray(j.payments) && j.payments.some(p => (p.payment_method || 'Unknown') === selectedPaymentMethod)
                        );
                        const innerFiltered = innerStatusFilter ? methodJobs.filter(j => j.status === innerStatusFilter) : methodJobs;
                        return (
                          <Card style={{ marginTop: '2rem' }}>
                            <CardHeader>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <CardTitle>
                                  Jobs paid via {selectedPaymentMethod} {jobsLoading ? '(loading…)' : `(${innerFiltered.length}${innerStatusFilter ? ` of ${methodJobs.length}` : ''})`}
                                  <span
                                    title={'Counts distinct jobs. A job paid across multiple ' + selectedPaymentMethod + ' transactions still counts once here, so this number can be lower than the "Number of Payments" figure above.'}
                                    style={{ marginLeft: '0.5rem', color: '#94a3b8', cursor: 'help', fontSize: '0.85em' }}
                                  >
                                    ⓘ
                                  </span>
                                </CardTitle>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => { setSelectedPaymentMethod(null); setInnerStatusFilter(null); }}
                                >
                                  Clear Filter
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              {jobsLoading ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading jobs…</div>
                              ) : (
                                <>
                                  <WorkflowHealthBar
                                    jobs={methodJobs}
                                    statusColors={settings?.status_colors || {}}
                                    label={`${selectedPaymentMethod} payments`}
                                  />
                                  <StatusBreakdownChips
                                    jobs={methodJobs}
                                    statusColors={settings?.status_colors || {}}
                                    value={innerStatusFilter}
                                    onChange={setInnerStatusFilter}
                                    label={`${selectedPaymentMethod} payments`}
                                  />
                                  <SortableJobsTable
                                    jobs={innerFiltered}
                                    columns={[
                                      { key: 'job_number',    label: 'Job #',    type: 'number', cellClassName: 'font-semibold' },
                                      { key: 'customer_name', label: 'Customer', type: 'string' },
                                      { key: 'status',        label: 'Status',   type: 'string' },
                                      { key: 'job_type',      label: 'Type',     type: 'string' },
                                      { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                                      { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                                      { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                                        <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)} data-testid={`payment-method-drill-view-${j.job_number}`}>View</Button>
                                      ) },
                                    ]}
                                  />
                                </>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })()}
                    </>
                  ) : (
                    <Card>
                      <CardContent style={{ padding: '2rem', textAlign: 'center' }}>
                        <p>Loading payment methods data...</p>
                      </CardContent>
                    </Card>
                  );
                  })()}
                </div>
              </div>
            </TabsContent>

            {/* Job Distribution Tab */}
            <TabsContent value="distribution">
              <div className="distribution-charts">
                <div className="chart-row">
                  <Card className="chart-card">
                    <CardHeader>
                      <CardTitle>Jobs by Status</CardTitle>
                      <CardDescription>How the paid jobs break down by workflow status — filtered by <em>payment received date</em>.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                      ) : statusData && statusData.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Count</TableHead>
                              <TableHead className="text-right">Percentage</TableHead>
                              <TableHead className="text-right">Revenue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {statusData.map((item, index) => {
                              const total = statusData.reduce((sum, d) => sum + d.value, 0);
                              const percentage = ((item.value / total) * 100).toFixed(1);
                              const statusRevenue = revenueFor('by_status', item.name);
                              const isSelected = selectedFilter?.category === 'status' && selectedFilter?.value === item.name;
                              return (
                                <TableRow 
                                  key={index}
                                  onClick={() => setSelectedFilter({ category: 'status', value: item.name })}
                                  style={{ 
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                    fontWeight: isSelected ? '600' : 'normal'
                                  }}
                                  className="hover:bg-gray-50"
                                >
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px' }}></span>
                                    {item.name}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{item.value}</TableCell>
                                  <TableCell className="text-right">{percentage}%</TableCell>
                                  <TableCell className="text-right">${statusRevenue.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                              <TableCell>Total</TableCell>
                              <TableCell className="text-right">{statusData.reduce((s, d) => s + d.value, 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                              <TableCell className="text-right">${statusData.reduce((s, d) => s + revenueFor('by_status', d.name), 0).toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          No data available for selected date range
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="chart-card">
                    <CardHeader>
                      <CardTitle>Jobs by Type</CardTitle>
                      <CardDescription>Media / service mix (3.5", 2.5", SSD, RAID, etc.) across paid jobs — filtered by <em>payment received date</em>.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                      ) : typeData && typeData.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Job Type</TableHead>
                              <TableHead className="text-right">Count</TableHead>
                              <TableHead className="text-right">Percentage</TableHead>
                              <TableHead className="text-right">Revenue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {typeData.map((item, index) => {
                              const total = typeData.reduce((sum, d) => sum + d.value, 0);
                              const percentage = ((item.value / total) * 100).toFixed(1);
                              const typeRevenue = revenueFor('by_type', item.name);
                              const isSelected = selectedFilter?.category === 'job_type' && selectedFilter?.value === item.name;
                              return (
                                <TableRow 
                                  key={index}
                                  onClick={() => setSelectedFilter({ category: 'job_type', value: item.name })}
                                  style={{ 
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                    fontWeight: isSelected ? '600' : 'normal'
                                  }}
                                  className="hover:bg-gray-50"
                                >
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px' }}></span>
                                    {item.name}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{item.value}</TableCell>
                                  <TableCell className="text-right">{percentage}%</TableCell>
                                  <TableCell className="text-right">${typeRevenue.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                              <TableCell>Total</TableCell>
                              <TableCell className="text-right">{typeData.reduce((s, d) => s + d.value, 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                              <TableCell className="text-right">${typeData.reduce((s, d) => s + revenueFor('by_type', d.name), 0).toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="chart-row">
                  <Card className="chart-card">
                    <CardHeader>
                      <CardTitle>Jobs by Brand</CardTitle>
                      <CardDescription>Drive brand mix across paid jobs (WD, Seagate, Samsung, etc.) — filtered by <em>payment received date</em>.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                      ) : brandData && brandData.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Brand</TableHead>
                              <TableHead className="text-right">Count</TableHead>
                              <TableHead className="text-right">Percentage</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {brandData.map((item, index) => {
                              const total = brandData.reduce((sum, d) => sum + d.value, 0);
                              const percentage = ((item.value / total) * 100).toFixed(1);
                              return (
                                <TableRow key={index}>
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px' }}></span>
                                    {item.name}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{item.value}</TableCell>
                                  <TableCell className="text-right">{percentage}%</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                              <TableCell>Total</TableCell>
                              <TableCell className="text-right">{brandData.reduce((s, d) => s + d.value, 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="chart-card">
                    <CardHeader>
                      <CardTitle>Jobs by State</CardTitle>
                      <CardDescription>Where the paid jobs came from geographically — filtered by <em>payment received date</em>.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                      ) : stateData && stateData.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>State</TableHead>
                              <TableHead className="text-right">Count</TableHead>
                              <TableHead className="text-right">Percentage</TableHead>
                              <TableHead className="text-right">Revenue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {stateData.map((item, index) => {
                              const total = stateData.reduce((sum, d) => sum + d.value, 0);
                              const percentage = ((item.value / total) * 100).toFixed(1);
                              const stateRevenue = revenueFor('by_state', item.name);
                              const isSelected = selectedFilter?.category === 'state' && selectedFilter?.value === item.name;
                              return (
                                <TableRow 
                                  key={index}
                                  onClick={() => setSelectedFilter({ category: 'state', value: item.name })}
                                  style={{ 
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                    fontWeight: isSelected ? '600' : 'normal'
                                  }}
                                  className="hover:bg-gray-50"
                                >
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px' }}></span>
                                    {item.name}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{item.value}</TableCell>
                                  <TableCell className="text-right">{percentage}%</TableCell>
                                  <TableCell className="text-right">${stateRevenue.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                              <TableCell>Total</TableCell>
                              <TableCell className="text-right">{stateData.reduce((s, d) => s + d.value, 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                              <TableCell className="text-right">${stateData.reduce((s, d) => s + revenueFor('by_state', d.name), 0).toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Filtered Jobs Display */}
                {selectedFilter && (() => {
                  // Feb 2026 — cash-basis grain to match the aggregation cards above.
                  const categoryFiltered = cashFilteredJobs.filter(job =>
                    selectedFilter.category === 'status'
                      ? job.status === selectedFilter.value
                      : selectedFilter.category === 'job_type'
                        ? job.job_type === selectedFilter.value
                        : selectedFilter.category === 'state'
                          ? job.state === selectedFilter.value
                          : false
                  );
                  // Show chips for category drill-downs (state, job_type) — chips would be redundant when already filtering by status
                  const showChips = selectedFilter.category === 'job_type' || selectedFilter.category === 'state';
                  const innerFiltered = showChips && innerStatusFilter
                    ? categoryFiltered.filter(j => j.status === innerStatusFilter)
                    : categoryFiltered;
                  return (
                  <Card style={{ marginTop: '2rem' }}>
                    <CardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <CardTitle>
                          Jobs - {selectedFilter.category === 'status' ? `Status: ${selectedFilter.value}` :
                                  selectedFilter.category === 'job_type' ? `Type: ${selectedFilter.value}` :
                                  selectedFilter.category === 'state' ? `State: ${selectedFilter.value}` :
                                  selectedFilter.value}
                          {' '}({innerFiltered.length}{showChips && innerStatusFilter ? ` of ${categoryFiltered.length}` : ''})
                        </CardTitle>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedFilter(null); setInnerStatusFilter(null); }}
                        >
                          Clear Filter
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {showChips && (
                        <>
                          <WorkflowHealthBar
                            jobs={categoryFiltered}
                            statusColors={settings?.status_colors || {}}
                            label={selectedFilter.value}
                          />
                          <StatusBreakdownChips
                            jobs={categoryFiltered}
                            statusColors={settings?.status_colors || {}}
                            value={innerStatusFilter}
                            onChange={setInnerStatusFilter}
                            label={selectedFilter.value}
                          />
                        </>
                      )}
                      <SortableJobsTable
                        jobs={innerFiltered}
                        columns={[
                          { key: 'job_number',    label: 'Job #',    type: 'number', cellClassName: 'font-semibold' },
                          { key: 'customer_name', label: 'Customer', type: 'string' },
                          { key: 'status',        label: 'Status',   type: 'string' },
                          { key: 'job_type',      label: 'Type',     type: 'string' },
                          { key: 'brand',         label: 'Brand',    type: 'string' },
                          { key: 'state',         label: 'State',    type: 'string' },
                          { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                          { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                          { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                            <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)} data-testid={`distribution-drill-view-${j.job_number}`}>View</Button>
                          ) },
                        ]}
                      />
                    </CardContent>
                  </Card>
                  );
                })()}
              </div>
            </TabsContent>

            {/* Location Tab */}
            <TabsContent value="location">
              <div className="location-reports">
                {/* Row 1: State Tables */}
                <div className="chart-row">
                  <Card className="location-table-card">
                    <CardHeader>
                      <CardTitle>Jobs by State</CardTitle>
                      <CardDescription>Count of paid jobs per state — filtered by <em>payment received date</em>. Click a row to see the underlying jobs.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>State</TableHead>
                            <TableHead>Jobs</TableHead>
                            <TableHead>Percentage</TableHead>
                            <TableHead>Revenue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stateData.map(({ name, value }, index) => {
                            const stateRevenue = revenueFor('by_state', name);
                            const total = stateData.reduce((sum, d) => sum + d.value, 0);
                            const percentage = ((value / total) * 100).toFixed(1);
                            const isSelected = selectedFilter?.category === 'state' && selectedFilter?.value === name;
                            return (
                              <TableRow 
                                key={name}
                                onClick={() => setSelectedFilter({ category: 'state', value: name })}
                                style={{ 
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#f0f9ff' : 'transparent'
                                }}
                              >
                                <TableCell style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                                  <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px', verticalAlign: 'middle' }}></span>
                                  {name}
                                </TableCell>
                                <TableCell>{value}</TableCell>
                                <TableCell>{percentage}%</TableCell>
                                <TableCell>${stateRevenue.toFixed(2)}</TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                            <TableCell>Total</TableCell>
                            <TableCell>{stateData.reduce((s, d) => s + d.value, 0).toLocaleString()}</TableCell>
                            <TableCell style={{ color: '#475569' }}>100.0%</TableCell>
                            <TableCell>${stateData.reduce((s, d) => s + revenueFor('by_state', d.name), 0).toFixed(2)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  <Card className="location-table-card">
                    <CardHeader>
                      <CardTitle>Revenue by State</CardTitle>
                      <CardDescription>Cash-basis revenue recognised per state — sum of <code>revenue_amount</code> for jobs paid in the period.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>State</TableHead>
                            <TableHead>Revenue</TableHead>
                            <TableHead>Percentage</TableHead>
                            <TableHead>Jobs</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(() => {
                            // Bug fix (Jul 2026): per-row values were computed client-side
                            // from `filteredJobs` (filtered by date_in) while the Total row
                            // used backend `by_state` aggregation (filtered by revenue_date).
                            // That caused the row sums (e.g. NSW $7,299 + VIC $1,410 = $8,709)
                            // to disagree with the displayed Total ($23,429). Both must use
                            // the same server-side cash-basis buckets so numbers reconcile.
                            const totalRevenue = stateData.reduce((s, d) => s + (revenueFor('by_state', d.name) || 0), 0);
                            return stateData.map(({ name, value }, index) => {
                            const stateRevenue = revenueFor('by_state', name);
                            const percentage = totalRevenue > 0 ? ((stateRevenue / totalRevenue) * 100).toFixed(1) : '0.0';
                            const isSelected = selectedFilter?.category === 'state' && selectedFilter?.value === name;
                            return (
                              <TableRow 
                                key={name}
                                onClick={() => setSelectedFilter({ category: 'state', value: name })}
                                style={{ 
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#f0f9ff' : 'transparent'
                                }}
                              >
                                <TableCell style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                                  <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px', verticalAlign: 'middle' }}></span>
                                  {name}
                                </TableCell>
                                <TableCell>${stateRevenue.toFixed(2)}</TableCell>
                                <TableCell>{percentage}%</TableCell>
                                <TableCell>{value}</TableCell>
                              </TableRow>
                            );
                            });
                          })()}
                          <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                            <TableCell>Total</TableCell>
                            <TableCell>${stateData.reduce((s, d) => s + revenueFor('by_state', d.name), 0).toFixed(2)}</TableCell>
                            <TableCell style={{ color: '#475569' }}>100.0%</TableCell>
                            <TableCell>{stateData.reduce((s, d) => s + d.value, 0).toLocaleString()}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Row 2: Postcode Tables */}
                <div className="chart-row">
                  <Card className="location-table-card">
                    <CardHeader>
                      <CardTitle>All Postcodes</CardTitle>
                      <CardDescription>Full postcode list for paid jobs in the period, ranked by job count.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Postcode</TableHead>
                            <TableHead>Jobs</TableHead>
                            <TableHead>Revenue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(distHas
                            ? (distributionData.top_postcodes || []).map(b => ({ name: b.name, value: b.count, revenue: b.revenue }))
                            : getJobsByCategory(filteredJobs, 'postcode').sort((a, b) => b.value - a.value).map(it => ({ ...it, revenue: calculateRevenue(filteredJobs.filter(j => j.postcode === it.name)) }))
                          ).map(({ name, value, revenue: pcRevenue }, index) => {
                              return (
                                <TableRow key={name}>
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px', verticalAlign: 'middle' }}></span>
                                    {name}
                                  </TableCell>
                                  <TableCell>{value}</TableCell>
                                  <TableCell>${(pcRevenue || 0).toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  <Card className="chart-card">
                    <CardHeader>
                      <CardTitle>Top 10 Postcodes</CardTitle>
                      <CardDescription>Ten highest-volume postcodes for paid jobs — quick geographic snapshot.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                      ) : postcodeData && postcodeData.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Postcode</TableHead>
                              <TableHead className="text-right">Count</TableHead>
                              <TableHead className="text-right">Percentage</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {postcodeData.slice(0, 10).map((item, index) => {
                              const total = postcodeData.reduce((sum, d) => sum + d.value, 0);
                              const percentage = ((item.value / total) * 100).toFixed(1);
                              return (
                                <TableRow key={index}>
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px' }}></span>
                                    {item.name}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{item.value}</TableCell>
                                  <TableCell className="text-right">{percentage}%</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Filtered Jobs Display */}
                {selectedFilter?.category === 'state' && (() => {
                  const stateJobs = cashFilteredJobs.filter(j => j.state === selectedFilter.value);
                  const innerFiltered = innerStatusFilter
                    ? stateJobs.filter(j => j.status === innerStatusFilter)
                    : stateJobs;
                  return (
                  <Card style={{ marginTop: '2rem' }}>
                    <CardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <CardTitle>
                          Jobs in {selectedFilter.value} ({innerFiltered.length}{innerStatusFilter ? ` of ${stateJobs.length}` : ''})
                        </CardTitle>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedFilter(null); setInnerStatusFilter(null); }}
                        >
                          Clear Filter
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <WorkflowHealthBar
                        jobs={stateJobs}
                        statusColors={settings?.status_colors || {}}
                        label={selectedFilter.value}
                      />
                      <StatusBreakdownChips
                        jobs={stateJobs}
                        statusColors={settings?.status_colors || {}}
                        value={innerStatusFilter}
                        onChange={setInnerStatusFilter}
                        label={selectedFilter.value}
                      />
                      <SortableJobsTable
                        jobs={innerFiltered}
                        columns={[
                          { key: 'job_number',    label: 'Job #',    type: 'number' },
                          { key: 'customer_name', label: 'Customer', type: 'string' },
                          { key: 'job_type',      label: 'Type',     type: 'string' },
                          { key: 'status',        label: 'Status',   type: 'string' },
                          { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                          { key: 'total_amount',  label: 'Amount',   type: 'number', render: (j) => `$${(j.total_amount || 0).toFixed(2)}` },
                          { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                            <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)}>View</Button>
                          ) },
                        ]}
                      />
                    </CardContent>
                  </Card>
                  );
                })()}
              </div>
            </TabsContent>

            {/* Referral Sources Tab — 2-box redesign (Feb 2026).
                LEFT box  = "Referrals by Source" — volume view, ALL statuses,
                            date_in-scoped so unpaid leads are counted.
                            Answers: "Where are my leads coming from?"
                RIGHT box = "Revenue by Source" — cash-basis $ (only jobs with
                            revenue_date contribute). Status chips let the user
                            narrow to specific closure states (Finish / Not
                            Recoverable / etc). Same job universe as left box.
                            Answers: "Which source drove real cash?"
                For pure-cash-basis "which source paid us in this period", see
                the new "Cash by Source" tab which filters by revenue_date. */}
            <TabsContent value="referrals">
              {(() => {
                const volume  = distributionData?.activity_by_referral_source_volume || [];
                const matrix  = distributionData?.activity_by_referral_source_status || [];
                // "Status chips" data — per-status rollup across all sources.
                // Only surface statuses that actually have jobs in the period.
                const statusRollup = matrix.reduce((acc, m) => {
                  const s = m.status || 'Unknown';
                  if (!acc[s]) acc[s] = { name: s, count: 0, revenue: 0 };
                  acc[s].count   += m.count   || 0;
                  acc[s].revenue += m.revenue || 0;
                  return acc;
                }, {});
                const chipStatuses = Object.values(statusRollup).sort((a, b) => b.count - a.count);
                const totalCount   = volume.reduce((s, v) => s + (v.count || 0), 0);
                // Right-box $ view: filter by selected chip if any, else full period.
                const chip = referralStatusChip; // 'All' or a status name
                const isAll = !chip || chip === 'All';
                const rightRows = isAll
                  ? volume.map(v => ({ name: v.name || 'Not Specified', revenue: v.revenue || 0, count: v.count || 0 }))
                  : (() => {
                      const bySource = {};
                      matrix.filter(m => m.status === chip).forEach(m => {
                        const s = m.source || 'Not Specified';
                        if (!bySource[s]) bySource[s] = { name: s, revenue: 0, count: 0 };
                        bySource[s].revenue += m.revenue || 0;
                        bySource[s].count   += m.count   || 0;
                      });
                      return Object.values(bySource);
                    })();
                const rightRevenue = rightRows.reduce((s, r) => s + r.revenue, 0);
                const rightRowsSorted = [...rightRows].sort((a, b) => b.revenue - a.revenue);

                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }} data-testid="referrals-tab-grid">
                    {/* LEFT — Volume box */}
                    <Card data-testid="referrals-volume-box">
                      <CardHeader>
                        <CardTitle>Referrals by Source</CardTitle>
                        <CardDescription>
                          Where new leads came from. Counts <strong>every job booked</strong> in the selected period regardless of paid / finished status — filtered by <em>booking date</em>.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {loading ? (
                          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading&hellip;</div>
                        ) : volume.length === 0 ? (
                          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                            No jobs booked in this date range.
                          </div>
                        ) : (
                          <Table data-testid="referrals-volume-table">
                            <TableHeader>
                              <TableRow>
                                <TableHead>Source</TableHead>
                                <TableHead className="text-right">Count</TableHead>
                                <TableHead className="text-right">% of Leads</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {volume.map((item, idx) => {
                                const pct = totalCount > 0 ? ((item.count / totalCount) * 100).toFixed(1) : '0.0';
                                const isSelected = selectedFilter?.category === 'referral_source' && selectedFilter?.value === item.name;
                                return (
                                  <TableRow
                                    key={idx}
                                    data-testid={`referrals-volume-row-${(item.name || 'unknown').replace(/\s+/g, '-').toLowerCase()}`}
                                    onClick={() => setSelectedFilter({ category: 'referral_source', value: item.name || 'Unknown' })}
                                    style={{ cursor: 'pointer', backgroundColor: isSelected ? '#f0f9ff' : 'transparent', fontWeight: isSelected ? 600 : 'normal' }}
                                    className="hover:bg-gray-50"
                                  >
                                    <TableCell>
                                      <span style={{ display: 'inline-block', width: 12, height: 12, background: COLORS[idx % COLORS.length], marginRight: 8, borderRadius: 2 }}></span>
                                      {item.name || 'Not Specified'}
                                    </TableCell>
                                    <TableCell className="text-right font-semibold">{item.count}</TableCell>
                                    <TableCell className="text-right">{pct}%</TableCell>
                                  </TableRow>
                                );
                              })}
                              <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                                <TableCell>Total</TableCell>
                                <TableCell className="text-right">{totalCount.toLocaleString()}</TableCell>
                                <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </Card>

                    {/* RIGHT — Revenue box (cash-basis) + status chips */}
                    <Card data-testid="referrals-revenue-box">
                      <CardHeader>
                        <CardTitle>Revenue by Source</CardTitle>
                        <CardDescription>
                          <strong>Cash-basis</strong>: only jobs that customers have <strong>actually paid</strong> contribute. A $10k Google quote where nobody paid = <em>$0 credit to Google</em>. Same period as the left box (booking date).
                        </CardDescription>
                        {/* Status filter chips */}
                        {chipStatuses.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.75rem' }} data-testid="referrals-status-chips">
                            {[{ name: 'All', count: totalCount, revenue: volume.reduce((s, v) => s + (v.revenue || 0), 0) }, ...chipStatuses].map((c) => {
                              const active = (isAll && c.name === 'All') || (!isAll && chip === c.name);
                              const dot = c.name === 'All' ? '#0f172a' : (settings?.status_colors?.[c.name] || '#64748b');
                              return (
                                <button
                                  key={c.name}
                                  type="button"
                                  onClick={() => setReferralStatusChip(c.name === 'All' ? null : c.name)}
                                  data-testid={`referrals-chip-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                                  style={{
                                    padding: '0.35rem 0.75rem',
                                    borderRadius: '999px',
                                    border: `1px solid ${active ? '#0f172a' : '#e2e8f0'}`,
                                    background: active ? '#0f172a' : '#ffffff',
                                    color:      active ? '#ffffff' : '#0f172a',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.35rem',
                                  }}
                                >
                                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dot }}></span>
                                  {c.name} · {c.count} · ${c.revenue.toFixed(0)}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </CardHeader>
                      <CardContent>
                        {loading ? (
                          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading&hellip;</div>
                        ) : rightRowsSorted.length === 0 ? (
                          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                            No paid revenue from {isAll ? 'any source' : chip + ' jobs'} in this date range.
                          </div>
                        ) : (
                          <Table data-testid="referrals-revenue-table">
                            <TableHeader>
                              <TableRow>
                                <TableHead>Source</TableHead>
                                <TableHead className="text-right">Cash Revenue</TableHead>
                                <TableHead className="text-right">% of $</TableHead>
                                <TableHead className="text-right">Jobs</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rightRowsSorted.map((r, idx) => {
                                const pct = rightRevenue > 0 ? ((r.revenue / rightRevenue) * 100).toFixed(1) : '0.0';
                                const rowName = r.name || 'Not Specified';
                                // Feb 2026 — Revenue by Source rows are now
                                // click-to-drill (matches Left box). If a status
                                // chip is active, carry it into the drill's
                                // inner status filter so the drilled jobs list
                                // matches what the row's numbers describe.
                                const isSelected = selectedFilter?.category === 'referral_source' && selectedFilter?.value === rowName;
                                const openDrill = () => {
                                  setSelectedFilter({ category: 'referral_source', value: rowName });
                                  // The mounted useEffect resets innerStatusFilter when
                                  // selectedFilter changes; defer our set so it survives
                                  // that reset and the chip pre-selection carries into
                                  // the drill's StatusBreakdownChips row.
                                  setTimeout(() => {
                                    setInnerStatusFilter(isAll ? null : chip);
                                  }, 0);
                                  setTimeout(() => {
                                    const el = document.querySelector('[data-testid="referrals-drill-in"]');
                                    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
                                  }, 80);
                                };
                                return (
                                  <TableRow
                                    key={idx}
                                    data-testid={`referrals-revenue-row-${(rowName).replace(/\s+/g, '-').toLowerCase()}`}
                                    onClick={openDrill}
                                    style={{
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                      fontWeight: isSelected ? 600 : 'normal',
                                    }}
                                    className="hover:bg-gray-50"
                                    title={isAll ? `Drill into all ${rowName} jobs` : `Drill into ${rowName} × ${chip} jobs`}
                                  >
                                    <TableCell>
                                      <span style={{ display: 'inline-block', width: 12, height: 12, background: COLORS[idx % COLORS.length], marginRight: 8, borderRadius: 2 }}></span>
                                      {rowName}
                                    </TableCell>
                                    <TableCell className="text-right font-semibold">${r.revenue.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">{pct}%</TableCell>
                                    <TableCell className="text-right">{r.count}</TableCell>
                                  </TableRow>
                                );
                              })}
                              <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                                <TableCell>Total</TableCell>
                                <TableCell className="text-right">${rightRevenue.toFixed(2)}</TableCell>
                                <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                                <TableCell className="text-right">{rightRowsSorted.reduce((s, r) => s + (r.count || 0), 0)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </Card>

                    {/* Drill-in modal shared by both boxes — clicking a source
                        row in the LEFT box scopes this list to that source. */}
                    {selectedFilter?.category === 'referral_source' && (() => {
                      const refJobs = filteredJobs.filter(job =>
                        selectedFilter.value === 'Unknown'
                          ? (!job.referral_source || job.referral_source === '' || job.referral_source === 'Unknown')
                          : job.referral_source === selectedFilter.value
                      );
                      const innerFiltered = innerStatusFilter ? refJobs.filter(j => j.status === innerStatusFilter) : refJobs;
                      return (
                        <div style={{ gridColumn: '1 / -1' }} data-testid="referrals-drill-in">
                          <Card>
                            <CardHeader>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <CardTitle>
                                  Jobs from {selectedFilter.value || 'Not Specified'} ({innerFiltered.length}{innerStatusFilter ? ` of ${refJobs.length}` : ''})
                                </CardTitle>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => { setSelectedFilter(null); setInnerStatusFilter(null); }}
                                >
                                  Clear Filter
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <WorkflowHealthBar
                                jobs={refJobs}
                                statusColors={settings?.status_colors || {}}
                                label={selectedFilter.value || 'Not Specified'}
                              />
                              <StatusBreakdownChips
                                jobs={refJobs}
                                statusColors={settings?.status_colors || {}}
                                value={innerStatusFilter}
                                onChange={setInnerStatusFilter}
                                label={selectedFilter.value || 'Not Specified'}
                                revenueField="revenue_amount"
                              />
                              <SortableJobsTable
                                jobs={innerFiltered}
                                columns={[
                                  { key: 'job_number',    label: 'Job #',    type: 'number', cellClassName: 'font-semibold' },
                                  { key: 'customer_name', label: 'Customer', type: 'string' },
                                  { key: 'status',        label: 'Status',   type: 'string' },
                                  { key: 'job_type',      label: 'Type',     type: 'string' },
                                  { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                                  { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                                  { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                                    <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)} data-testid={`referral-drill-view-${j.job_number}`}>View</Button>
                                  ) },
                                ]}
                              />
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </TabsContent>

            {/* Cash by Source Tab (Feb 2026) — pure cash-basis view. Filters
                by revenue_date (day the payment landed) rather than date_in
                (day the job was booked). Answers "of the CASH that landed in
                this period, what source drove it?" — reconciles to P&L / BAS.
                Use this when a job booked in December but paid in January
                should count against January's revenue for reporting purposes.
                Distinct from the "Referral Sources" tab which is booking-date
                scoped for marketing analysis. */}
            <TabsContent value="cash-by-source">
              <div className="distribution-charts">
                <Card data-testid="cash-by-source-card">
                  <CardHeader>
                    <CardTitle>Cash by Source</CardTitle>
                    <CardDescription>
                      Answers <strong>&ldquo;of the cash that landed in this period, which source drove it?&rdquo;</strong> — filtered by <em>payment received date</em>, not booking date.
                      Reconciles to the P&amp;L / BAS. Different question from the &ldquo;Referral Sources&rdquo; tab (which is booking-date scoped for marketing analysis).
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>Loading&hellip;</div>
                    ) : !distributionData?.by_referral_source || distributionData.by_referral_source.length === 0 ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        No payments received from any source in this date range.
                      </div>
                    ) : (
                      (() => {
                        const rows = (distributionData.by_referral_source || [])
                          .map(r => ({ name: r.name || 'Not Specified', count: r.count || r.value || 0, revenue: r.revenue || 0 }))
                          .filter(r => r.revenue > 0 || r.count > 0)
                          .sort((a, b) => b.revenue - a.revenue);
                        const totalRevenue = rows.reduce((s, r) => s + r.revenue, 0);
                        const totalCount   = rows.reduce((s, r) => s + r.count,   0);
                        return (
                          <Table data-testid="cash-by-source-table">
                            <TableHeader>
                              <TableRow>
                                <TableHead>Source</TableHead>
                                <TableHead className="text-right">Cash Received</TableHead>
                                <TableHead className="text-right">% of $</TableHead>
                                <TableHead className="text-right">Paid Jobs</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rows.map((r, idx) => {
                                const pct = totalRevenue > 0 ? ((r.revenue / totalRevenue) * 100).toFixed(1) : '0.0';
                                return (
                                  <TableRow key={idx} data-testid={`cash-by-source-row-${(r.name || 'unknown').replace(/\s+/g, '-').toLowerCase()}`}>
                                    <TableCell>
                                      <span style={{ display: 'inline-block', width: 12, height: 12, background: COLORS[idx % COLORS.length], marginRight: 8, borderRadius: 2 }}></span>
                                      {r.name}
                                    </TableCell>
                                    <TableCell className="text-right font-semibold">${r.revenue.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">{pct}%</TableCell>
                                    <TableCell className="text-right">{r.count}</TableCell>
                                  </TableRow>
                                );
                              })}
                              <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                                <TableCell>Total</TableCell>
                                <TableCell className="text-right">${totalRevenue.toFixed(2)}</TableCell>
                                <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                                <TableCell className="text-right">{totalCount}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        );
                      })()
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Jobs In Tab (Feb 2026) — workload / intake view. Answers
                "of ALL jobs booked in this period, how do they distribute
                across every dimension?". Every section is booking-date
                scoped, every status counted. The Count / Count + Revenue
                toggle lets user eyeball convert-to-cash without switching
                tabs. Reused the activity_by_* buckets we just added.
                Feb 2026 (drill-in): rows are clickable — clicking a row
                pins selectedFilter to that dimension so the drill-in
                table below shows the actual jobs (date_in scoped, carries
                the currently active period selector via `filteredJobs`). */}
            <TabsContent value="jobs-in">
              {(() => {
                const total = distributionData?.activity_summary?.total_jobs || 0;
                // Map each section to (a) the job field it filters by and
                // (b) the selectedFilter.category value we set on click. Kept
                // aligned with the categories already used elsewhere on the
                // Reports page so the same `filteredJobs.filter(...)` pattern
                // works and no new state shape is introduced.
                const sections = [
                  { key: 'activity_by_status',                 title: '📊 Jobs by Status',           headCount: 'Jobs', field: 'status',          category: 'status' },
                  { key: 'activity_by_type',                   title: '💾 Jobs by Type',             headCount: 'Jobs', field: 'job_type',        category: 'job_type' },
                  { key: 'activity_by_brand',                  title: '🏷️ Jobs by Brand',            headCount: 'Jobs', field: 'brand',           category: 'brand' },
                  { key: 'activity_by_state',                  title: '📍 Jobs by State',            headCount: 'Jobs', field: 'state',           category: 'state' },
                  { key: 'activity_by_referral_source_volume', title: '🔗 Jobs by Referral Source',  headCount: 'Jobs', field: 'referral_source', category: 'referral_source' },
                ];
                return (
                  <div>
                    {/* Summary + toggle */}
                    <Card style={{ marginBottom: '1.25rem' }} data-testid="jobs-in-summary">
                      <CardHeader>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                          <div>
                            <CardTitle>Jobs In &mdash; {total.toLocaleString()} total</CardTitle>
                            <CardDescription>
                              Every job <strong>booked</strong> in the selected period. All statuses counted &mdash; Booked, Quoted, Approved, Wait, Finish, Not Recoverable, Not Proceeding. Filtered by <em>booking date (date_in)</em>.
                            </CardDescription>
                          </div>
                          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>View:</span>
                            <button
                              type="button"
                              data-testid="jobs-in-toggle-count"
                              onClick={() => setJobsInShowRevenue(false)}
                              style={{
                                padding: '0.35rem 0.85rem', borderRadius: '999px',
                                border: '1px solid', borderColor: !jobsInShowRevenue ? '#0f172a' : '#e2e8f0',
                                background: !jobsInShowRevenue ? '#0f172a' : '#fff',
                                color: !jobsInShowRevenue ? '#fff' : '#0f172a',
                                fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                              }}
                            >
                              Count only
                            </button>
                            <button
                              type="button"
                              data-testid="jobs-in-toggle-revenue"
                              onClick={() => setJobsInShowRevenue(true)}
                              style={{
                                padding: '0.35rem 0.85rem', borderRadius: '999px',
                                border: '1px solid', borderColor: jobsInShowRevenue ? '#0f172a' : '#e2e8f0',
                                background: jobsInShowRevenue ? '#0f172a' : '#fff',
                                color: jobsInShowRevenue ? '#fff' : '#0f172a',
                                fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                              }}
                            >
                              Count + Cash Revenue
                            </button>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>

                    {/* Section grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '1rem' }}>
                      {sections.map(sec => {
                        const rows = (distributionData?.[sec.key] || []).filter(r => (r.count || 0) > 0);
                        const sectionTotal = rows.reduce((s, r) => s + (r.count || 0), 0);
                        const sectionRev   = rows.reduce((s, r) => s + (r.revenue || 0), 0);
                        return (
                          <Card key={sec.key} data-testid={`jobs-in-section-${sec.key}`}>
                            <CardHeader>
                              <CardTitle style={{ fontSize: '1rem' }}>{sec.title}</CardTitle>
                            </CardHeader>
                            <CardContent>
                              {loading ? (
                                <div style={{ padding: '1.5rem', textAlign: 'center' }}>Loading&hellip;</div>
                              ) : rows.length === 0 ? (
                                <div style={{ padding: '1.5rem', textAlign: 'center', color: '#666' }}>No data for this period.</div>
                              ) : (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Name</TableHead>
                                      <TableHead className="text-right">{sec.headCount}</TableHead>
                                      <TableHead className="text-right">% of Jobs</TableHead>
                                      {jobsInShowRevenue && <TableHead className="text-right">Cash Revenue</TableHead>}
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {rows.map((r, idx) => {
                                      const pct = sectionTotal > 0 ? ((r.count / sectionTotal) * 100).toFixed(1) : '0.0';
                                      const rawName = (r.name || 'Unknown');
                                      // brand names are "code — label"; show the trailing label for readability
                                      const shortName = rawName.includes(' — ') ? rawName.split(' — ').slice(1).join(' — ') : rawName;
                                      const isSelected = selectedFilter?.category === sec.category && selectedFilter?.value === rawName;
                                      return (
                                        <TableRow
                                          key={idx}
                                          data-testid={`jobs-in-row-${sec.key}-${rawName.replace(/\W+/g, '-').toLowerCase()}`}
                                          onClick={() => setSelectedFilter(isSelected ? null : { category: sec.category, value: rawName })}
                                          style={{
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#dbeafe' : undefined,
                                          }}
                                        >
                                          <TableCell>
                                            <span style={{ display: 'inline-block', width: 10, height: 10, background: COLORS[idx % COLORS.length], marginRight: 8, borderRadius: 2 }}></span>
                                            {shortName}
                                          </TableCell>
                                          <TableCell className="text-right font-semibold">{r.count.toLocaleString()}</TableCell>
                                          <TableCell className="text-right">{pct}%</TableCell>
                                          {jobsInShowRevenue && <TableCell className="text-right">${(r.revenue || 0).toFixed(2)}</TableCell>}
                                        </TableRow>
                                      );
                                    })}
                                    <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                                      <TableCell>Total</TableCell>
                                      <TableCell className="text-right">{sectionTotal.toLocaleString()}</TableCell>
                                      <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                                      {jobsInShowRevenue && <TableCell className="text-right">${sectionRev.toFixed(2)}</TableCell>}
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Drill-in table (Feb 2026) — appears when the user clicks
                        a row in any section above. Uses `filteredJobs` which
                        is already `date_in`-scoped by the top-of-page period
                        selector, matching the Jobs In semantics. Every status
                        is included (no implicit status filter). Backend
                        `activity_by_*` counts every job in the period (raw
                        date_in), so the drill-in does too — no closed-unpaid
                        exclusion here (that lives only in the pay-status card
                        sub-pipeline). Users want "Jobs In" to match their
                        diary numbers exactly, including closed-same-day
                        Not Proceeding / Not Recoverable / $0 Finish jobs. */}
                    {selectedFilter && ['status', 'job_type', 'brand', 'state', 'referral_source'].includes(selectedFilter.category) && (() => {
                      const fieldMap = { status: 'status', job_type: 'job_type', brand: 'brand', state: 'state', referral_source: 'referral_source' };
                      const field = fieldMap[selectedFilter.category];
                      const drillJobs = filteredJobs.filter(j => (j[field] || 'Unknown') === selectedFilter.value);
                      const labelMap = {
                        status: 'Status', job_type: 'Type', brand: 'Brand',
                        state: 'State', referral_source: 'Referral Source',
                      };
                      const shortValue = selectedFilter.value.includes(' — ')
                        ? selectedFilter.value.split(' — ').slice(1).join(' — ')
                        : selectedFilter.value;
                      return (
                        <Card style={{ marginTop: '1.5rem' }} data-testid="jobs-in-drill-in">
                          <CardHeader>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <CardTitle>
                                  Jobs — {labelMap[selectedFilter.category]}: {shortValue} ({drillJobs.length})
                                </CardTitle>
                                <CardDescription>
                                  Booking-date scoped (date_in) — matches the row you clicked. Includes every status.
                                </CardDescription>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid="jobs-in-drill-in-clear"
                                onClick={() => setSelectedFilter(null)}
                              >
                                Clear Filter
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <SortableJobsTable
                              jobs={drillJobs}
                              columns={[
                                { key: 'job_number',    label: 'Job #',    type: 'number', cellClassName: 'font-semibold' },
                                { key: 'customer_name', label: 'Customer', type: 'string' },
                                { key: 'status',        label: 'Status',   type: 'string' },
                                { key: 'job_type',      label: 'Type',     type: 'string' },
                                { key: 'brand',         label: 'Brand',    type: 'string' },
                                { key: 'state',         label: 'State',    type: 'string' },
                                { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                                { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                                { key: 'actions', label: 'Actions', sortable: false, render: (j) => (
                                  <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)}>View</Button>
                                ) },
                              ]}
                            />
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </div>
                );
              })()}
            </TabsContent>

            {/* Referral Partners Tab — specific named partners (e.g. DRC Computers) */}
            <TabsContent value="referral-partners">
              <div className="distribution-charts">
                <Card>
                  <CardHeader>
                    <CardTitle>Referral Partners</CardTitle>
                    <CardDescription>
                      Specific businesses/people who sent customers your way — filtered by <strong>booking date</strong> (date_in) so recently-referred jobs show up even before they&apos;re paid.
                      Revenue column is cash-basis: unpaid referrals appear with $0 revenue until payment lands. Distinct from the coarse &quot;Referral Sources&quot; tab (channel bucket).
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {partnersLoading ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>
                    ) : partnersReport.rows.length === 0 ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        No partner-referred jobs in this date range yet. Tag a job with a Referred By partner and it will show up here.
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                            <div style={{ fontSize: '0.75rem', color: '#1e40af', fontWeight: 600 }}>ACTIVE PARTNERS</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a8a' }} data-testid="partners-report-count">
                              {partnersReport.rows.length}
                            </div>
                          </div>
                          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                            <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>REFERRED JOBS</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#14532d' }} data-testid="partners-report-jobs">
                              {partnersReport.total_jobs.toLocaleString()}
                            </div>
                          </div>
                          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                            <div style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>TOTAL REVENUE</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#78350f' }} data-testid="partners-report-revenue">
                              ${partnersReport.total_revenue.toFixed(2)}
                            </div>
                          </div>
                          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                            <div style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 600 }}>DISCOUNT GIVEN</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#991b1b' }} data-testid="partners-report-discount">
                              ${(partnersReport.total_discount || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                        <Table data-testid="partners-report-table">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Partner</TableHead>
                              <TableHead className="text-right">Jobs Count</TableHead>
                              <TableHead className="text-right">Total Revenue</TableHead>
                              <TableHead className="text-right">Total Billed</TableHead>
                              <TableHead className="text-right">Discount Given</TableHead>
                              <TableHead>First Referral</TableHead>
                              <TableHead>Last Referral</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {partnersReport.rows.map((row) => {
                              const isSelected = selectedFilter?.category === 'referral_partner' &&
                                (selectedFilter.partner_id === row.partner_id || selectedFilter.value === row.partner_name);
                              return (
                                <TableRow
                                  key={row.partner_id || row.partner_name}
                                  data-testid={`partner-report-row-${row.partner_id || row.partner_name}`}
                                  onClick={() => setSelectedFilter(isSelected ? null : {
                                    category: 'referral_partner',
                                    value: row.partner_name,
                                    partner_id: row.partner_id,
                                  })}
                                  style={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                    fontWeight: isSelected ? 600 : undefined,
                                  }}
                                  className="hover:bg-gray-50"
                                >
                                  <TableCell style={{ fontWeight: 600 }}>{row.partner_name}</TableCell>
                                  <TableCell className="text-right">{row.jobs_count.toLocaleString()}</TableCell>
                                  <TableCell className="text-right" style={{ fontWeight: 600, color: '#059669' }}>
                                    ${(row.total_revenue || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell className="text-right" style={{ color: '#475569' }}>
                                    ${(row.total_billed || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell className="text-right" style={{ color: '#b91c1c' }}>
                                    ${(row.total_discount || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell>{formatDate(row.first_referral)}</TableCell>
                                  <TableCell>{formatDate(row.last_referral)}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                              <TableCell>Total</TableCell>
                              <TableCell className="text-right">{partnersReport.total_jobs.toLocaleString()}</TableCell>
                              <TableCell className="text-right">${partnersReport.total_revenue.toFixed(2)}</TableCell>
                              <TableCell className="text-right" style={{ color: '#475569' }}>
                                ${partnersReport.rows.reduce((s, r) => s + (r.total_billed || 0), 0).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right" style={{ color: '#b91c1c' }}>
                                ${(partnersReport.total_discount || 0).toFixed(2)}
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Drill-in: click a partner row above → this card renders below,
                    mirroring the By Location drill (Workflow Health + Status Chips
                    + sortable Jobs table). Consistency across all Reports tabs. */}
                {selectedFilter?.category === 'referral_partner' && (() => {
                  const partnerJobs = filteredJobs.filter(j =>
                    (selectedFilter.partner_id && j.referred_by_partner_id === selectedFilter.partner_id) ||
                    (j.referred_by_partner_name && j.referred_by_partner_name === selectedFilter.value)
                  );
                  const innerFiltered = innerStatusFilter
                    ? partnerJobs.filter(j => j.status === innerStatusFilter)
                    : partnerJobs;
                  return (
                    <Card style={{ marginTop: '2rem' }} data-testid="partner-drill-card">
                      <CardHeader>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <CardTitle>
                            Jobs referred by {selectedFilter.value} ({innerFiltered.length}{innerStatusFilter ? ` of ${partnerJobs.length}` : ''})
                          </CardTitle>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedFilter(null); setInnerStatusFilter(null); }}
                            data-testid="partner-drill-clear"
                          >
                            Clear Filter
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <WorkflowHealthBar
                          jobs={partnerJobs}
                          statusColors={settings?.status_colors || {}}
                          label={selectedFilter.value}
                        />
                        <StatusBreakdownChips
                          jobs={partnerJobs}
                          statusColors={settings?.status_colors || {}}
                          value={innerStatusFilter}
                          onChange={setInnerStatusFilter}
                          label={selectedFilter.value}
                        />
                        <SortableJobsTable
                          jobs={innerFiltered}
                          columns={[
                            { key: 'job_number',    label: 'Job #',    type: 'number' },
                            { key: 'customer_name', label: 'Customer', type: 'string' },
                            { key: 'job_type',      label: 'Type',     type: 'string' },
                            { key: 'status',        label: 'Status',   type: 'string' },
                            { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                            { key: 'total_amount',  label: 'Amount',   type: 'number', render: (j) => `$${(j.total_amount || 0).toFixed(2)}` },
                            { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                              <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)}>View</Button>
                            ) },
                          ]}
                        />
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>
            </TabsContent>

            {/* Priority Tab */}
            <TabsContent value="priority">
              <div className="distribution-charts">
                <Card>
                  <CardHeader>
                    <CardTitle>Jobs by Priority</CardTitle>
                    <CardDescription>Count of paid jobs at each priority level (Standard / Urgent / Special). Click a row to see the underlying jobs — filtered by <em>payment received date</em>.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                    ) : priorityData && priorityData.length > 0 ? (
                      <div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Priority</TableHead>
                              <TableHead className="text-right">Count</TableHead>
                              <TableHead className="text-right">Percentage</TableHead>
                              <TableHead className="text-right">Revenue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {priorityData.map((item, index) => {
                              const total = priorityData.reduce((sum, d) => sum + d.value, 0);
                              const percentage = ((item.value / total) * 100).toFixed(1);
                              const isSelected = selectedFilter?.category === 'priority' && selectedFilter?.value === item.name;
                              const priorityJobs = filteredJobs.filter(j => j.priority === item.name);
                              const priorityRevenue = calculateRevenue(priorityJobs);
                              return (
                                <TableRow 
                                  key={index}
                                  onClick={() => setSelectedFilter({ category: 'priority', value: item.name })}
                                  style={{ 
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                    fontWeight: isSelected ? '600' : 'normal'
                                  }}
                                  className="hover:bg-gray-50"
                                >
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[index % COLORS.length], marginRight: '8px', borderRadius: '2px' }}></span>
                                    {item.name || 'Not Specified'}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{item.value}</TableCell>
                                  <TableCell className="text-right">{percentage}%</TableCell>
                                  <TableCell className="text-right">${priorityRevenue.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                              <TableCell>Total</TableCell>
                              <TableCell className="text-right">{priorityData.reduce((s, d) => s + d.value, 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                              <TableCell className="text-right">${priorityData.reduce((s, d) => s + revenueFor('by_priority', d.name), 0).toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>

                        {/* Filtered Jobs Display */}
                        {selectedFilter?.category === 'priority' && (() => {
                          const pJobs = cashFilteredJobs.filter(job =>
                            selectedFilter.value === 'Unknown'
                              ? (!job.priority || job.priority === '' || job.priority === 'Unknown')
                              : job.priority === selectedFilter.value
                          );
                          const innerFiltered = innerStatusFilter ? pJobs.filter(j => j.status === innerStatusFilter) : pJobs;
                          return (
                          <Card style={{ marginTop: '2rem' }}>
                            <CardHeader>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <CardTitle>
                                  Jobs with {selectedFilter.value || 'Not Specified'} Priority ({innerFiltered.length}{innerStatusFilter ? ` of ${pJobs.length}` : ''})
                                </CardTitle>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => { setSelectedFilter(null); setInnerStatusFilter(null); }}
                                >
                                  Clear Filter
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <WorkflowHealthBar
                                jobs={pJobs}
                                statusColors={settings?.status_colors || {}}
                                label={`${selectedFilter.value || 'Not Specified'} priority`}
                              />
                              <StatusBreakdownChips
                                jobs={pJobs}
                                statusColors={settings?.status_colors || {}}
                                value={innerStatusFilter}
                                onChange={setInnerStatusFilter}
                                label={`${selectedFilter.value || 'Not Specified'} priority`}
                              />
                              <SortableJobsTable
                                jobs={innerFiltered}
                                columns={[
                                  { key: 'job_number',    label: 'Job #',    type: 'number', cellClassName: 'font-semibold' },
                                  { key: 'customer_name', label: 'Customer', type: 'string' },
                                  { key: 'status',        label: 'Status',   type: 'string' },
                                  { key: 'job_type',      label: 'Type',     type: 'string' },
                                  { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                                  { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                                  { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                                    <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)} data-testid={`priority-drill-view-${j.job_number}`}>View</Button>
                                  ) },
                                ]}
                              />
                            </CardContent>
                          </Card>
                          );
                        })()}
                      </div>
                    ) : (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        No priority data available for selected date range
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Customer Types Tab */}
            <TabsContent value="customer-types">
              <div className="distribution-charts">
                <Card>
                  <CardHeader>
                    <CardTitle>Customer Type Performance</CardTitle>
                    <CardDescription>
                      Summary of customers, jobs, and revenue by customer type.
                      Use this to compare contribution of Partners vs End Users vs Not Specified.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {customersLoading ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>Loading customer data…</div>
                    ) : loading ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                    ) : customerTypesData && customerTypesData.length > 0 ? (() => {
                      // Build a lookup: customer_name → customer_type (lowercase normalised for matching)
                      const typeByName = new Map();
                      const byTypeCustomerSet = new Map();  // type → Set of customer_names
                      customers.forEach(c => {
                        const t = (c.customer_type && c.customer_type.trim()) ? c.customer_type : 'Not Specified';
                        typeByName.set(c.name, t);
                        if (!byTypeCustomerSet.has(t)) byTypeCustomerSet.set(t, new Set());
                        byTypeCustomerSet.get(t).add(c.name);
                      });
                      
                      // Aggregate jobs (from filteredJobs which respects the page-level date range)
                      // and track UNIQUE customers who had activity in the period per type.
                      // customers_in_period is the Set of customer names that appeared in the
                      // filtered job list, grouped by their customer_type — this is what a
                      // user actually wants for newsletter targeting: "who came in during X".
                      const aggregates = new Map();  // type → {customers, customers_in_period_set, jobs, revenue}
                      customerTypesData.forEach(item => {
                        const t = item.name || 'Not Specified';
                        aggregates.set(t, { customers: item.value || 0, customers_in_period_set: new Set(), jobs: 0, revenue: 0 });
                      });
                      filteredJobs.forEach(j => {
                        const t = typeByName.get(j.customer_name) || 'Not Specified';
                        if (!aggregates.has(t)) aggregates.set(t, { customers: 0, customers_in_period_set: new Set(), jobs: 0, revenue: 0 });
                        const a = aggregates.get(t);
                        a.jobs += 1;
                        a.revenue += (j.total_amount || 0);
                        if (j.customer_name) a.customers_in_period_set.add(j.customer_name);
                      });
                      
                      const rows = Array.from(aggregates.entries()).map(([type, data]) => ({
                        type,
                        customers: data.customers,
                        customers_in_period: data.customers_in_period_set.size,
                        customers_in_period_names: Array.from(data.customers_in_period_set),
                        jobs: data.jobs,
                        revenue: data.revenue,
                      }));
                      const totalCustomers = rows.reduce((s, r) => s + r.customers, 0);
                      const totalCustomersInPeriod = rows.reduce((s, r) => s + r.customers_in_period, 0);
                      const totalJobs      = rows.reduce((s, r) => s + r.jobs, 0);
                      const totalRevenue   = rows.reduce((s, r) => s + r.revenue, 0);
                      
                      // Sort by revenue desc so the biggest contributor surfaces first
                      rows.sort((a, b) => b.revenue - a.revenue);
                      
                      const fmtMoney = (v) => v.toLocaleString(undefined, { style: 'currency', currency: 'AUD' });
                      const pct = (v, total) => total > 0 ? `${(v / total * 100).toFixed(1)}%` : '0.0%';
                      
                      return (
                        <>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Customer Type</TableHead>
                                <TableHead className="text-right" title="Every customer of this type ever on file — never changes with date filter">
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    🌐 Customers <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 400 }}>(all-time)</span>
                                  </span>
                                </TableHead>
                                <TableHead className="text-right">% of Customers</TableHead>
                                <TableHead className="text-right" title="Unique customers who had at least one job in the selected date range — click a row to see the list">
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    📅 Customers <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 400 }}>(period)</span>
                                  </span>
                                </TableHead>
                                <TableHead className="text-right">Jobs</TableHead>
                                <TableHead className="text-right">% of Jobs</TableHead>
                                <TableHead className="text-right">Revenue</TableHead>
                                <TableHead className="text-right">% of Revenue</TableHead>
                                <TableHead className="text-right">Avg per Customer</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rows.map((r, idx) => (
                                <TableRow
                                  key={r.type}
                                  onClick={() => openCustomerTypeDrillIn(r.type, r.customers_in_period_names)}
                                  style={{ cursor: 'pointer' }}
                                  className="hover:bg-slate-50"
                                  title={`Click to see ${r.type} customers`}
                                  data-testid={`ct-row-${r.type.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <TableCell>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[idx % COLORS.length], marginRight: '8px', borderRadius: '2px' }}></span>
                                    <strong>{r.type}</strong>
                                  </TableCell>
                                  <TableCell className="text-right">{r.customers.toLocaleString()}</TableCell>
                                  <TableCell className="text-right" style={{ color: '#64748b' }}>{pct(r.customers, totalCustomers)}</TableCell>
                                  <TableCell className="text-right" style={{ fontWeight: 600, color: r.customers_in_period > 0 ? '#0f766e' : '#94a3b8' }}>{r.customers_in_period.toLocaleString()}</TableCell>
                                  <TableCell className="text-right">{r.jobs.toLocaleString()}</TableCell>
                                  <TableCell className="text-right" style={{ color: '#64748b' }}>{pct(r.jobs, totalJobs)}</TableCell>
                                  <TableCell className="text-right" style={{ fontWeight: 600 }}>{fmtMoney(r.revenue)}</TableCell>
                                  <TableCell className="text-right" style={{ color: '#64748b' }}>{pct(r.revenue, totalRevenue)}</TableCell>
                                  <TableCell className="text-right">{fmtMoney(r.customers > 0 ? r.revenue / r.customers : 0)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                                <TableCell>Total</TableCell>
                                <TableCell className="text-right">{totalCustomers.toLocaleString()}</TableCell>
                                <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                                <TableCell className="text-right" style={{ color: '#0f766e' }}>{totalCustomersInPeriod.toLocaleString()}</TableCell>
                                <TableCell className="text-right">{totalJobs.toLocaleString()}</TableCell>
                                <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                                <TableCell className="text-right">{fmtMoney(totalRevenue)}</TableCell>
                                <TableCell className="text-right" style={{ color: '#475569' }}>100.0%</TableCell>
                                <TableCell className="text-right">{fmtMoney(totalCustomers > 0 ? totalRevenue / totalCustomers : 0)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                          <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.75rem', fontStyle: 'italic' }}>
                            Click any row above to see who the customers are in that group. Or open the Customers page for full management.
                          </p>
                        </>
                      );
                    })() : (
                      <p style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No customer type data available.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Job Details Tab */}
            <TabsContent value="stock-summary">
              <Card>
                <CardHeader>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div>
                      <CardTitle>Stock Inventory Summary</CardTitle>
                      <CardDescription>Live snapshot of inventory levels, value and profit potential.</CardDescription>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Apply current filters to derive the export set
                          const q = stockSearch.toLowerCase().trim();
                          const filtered = stockItems.filter((item) => {
                            const isOut = item.current_quantity === 0;
                            const isLow = item.current_quantity > 0 && item.current_quantity <= item.low_stock_threshold;
                            if (stockStatusFilter === 'in_stock' && (isOut || isLow)) return false;
                            if (stockStatusFilter === 'low' && !isLow) return false;
                            if (stockStatusFilter === 'out' && !isOut) return false;
                            if (q) {
                              const hay = `${item.item_name || ''} ${item.brand || ''} ${item.item_code || ''} ${item.sku || ''} ${item.category || ''}`.toLowerCase();
                              if (!hay.includes(q)) return false;
                            }
                            return true;
                          });
                          const rows = [['Status', 'Item', 'Brand', 'Item Code', 'Category', 'Current Qty', 'Unit', 'Low Stock Alert', 'Cost Price', 'Cost Value', 'Sell Price', 'Sell Value', 'Potential Profit']];
                          filtered.forEach((item) => {
                            const isOut = item.current_quantity === 0;
                            const isLow = item.current_quantity > 0 && item.current_quantity <= item.low_stock_threshold;
                            const status = isOut ? 'Out of Stock' : isLow ? 'Low Stock' : 'In Stock';
                            const costValue = item.current_quantity * item.cost_price;
                            const sellValue = item.current_quantity * item.sell_price;
                            rows.push([
                              status,
                              item.item_name || '',
                              item.brand || '',
                              item.item_code || '',
                              item.category || '',
                              item.current_quantity,
                              item.unit_of_measure || '',
                              item.low_stock_threshold,
                              (item.cost_price || 0).toFixed(2),
                              costValue.toFixed(2),
                              (item.sell_price || 0).toFixed(2),
                              sellValue.toFixed(2),
                              (sellValue - costValue).toFixed(2),
                            ]);
                          });
                          const csv = rows.map(r => r.map(v => {
                            const s = String(v ?? '');
                            return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
                          }).join(',')).join('\n');
                          const today = todayLocalISO();
                          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                          const link = document.createElement('a');
                          link.href = URL.createObjectURL(blob);
                          link.download = `stock_summary_${today}.csv`;
                          link.click();
                        }}
                        data-testid="export-stock-csv"
                        style={{ backgroundColor: '#10b981', color: 'white' }}
                      >
                        📊 Export CSV
                      </Button>
                      <Button
                        onClick={() => {
                          const q = stockSearch.toLowerCase().trim();
                          const filtered = stockItems.filter((item) => {
                            const isOut = item.current_quantity === 0;
                            const isLow = item.current_quantity > 0 && item.current_quantity <= item.low_stock_threshold;
                            if (stockStatusFilter === 'in_stock' && (isOut || isLow)) return false;
                            if (stockStatusFilter === 'low' && !isLow) return false;
                            if (stockStatusFilter === 'out' && !isOut) return false;
                            if (q) {
                              const hay = `${item.item_name || ''} ${item.brand || ''} ${item.item_code || ''} ${item.sku || ''} ${item.category || ''}`.toLowerCase();
                              if (!hay.includes(q)) return false;
                            }
                            return true;
                          });
                          const filterLabel = (
                            stockStatusFilter === 'all' ? 'All items' :
                            stockStatusFilter === 'in_stock' ? 'In Stock only' :
                            stockStatusFilter === 'low' ? 'Low Stock only' : 'Out of Stock only'
                          );
                          const totalCost = filtered.reduce((s, i) => s + (i.current_quantity * i.cost_price), 0);
                          const totalSell = filtered.reduce((s, i) => s + (i.current_quantity * i.sell_price), 0);
                          const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
                          const contentHtml = `
                            <div style="margin-bottom:12px;padding:8px 12px;background:#f1f5f9;border-radius:6px;font-size:12px;color:#334155;">
                              <strong>Filter:</strong> ${filterLabel}${stockSearch ? ` &nbsp;|&nbsp; <strong>Search:</strong> "${stockSearch}"` : ''} &nbsp;|&nbsp; <strong>As at:</strong> ${today}
                            </div>
                            <div class="summary-grid" style="grid-template-columns: repeat(4, 1fr);">
                              <div class="summary-card"><div class="summary-label">Items</div><div class="summary-value">${filtered.length}</div></div>
                              <div class="summary-card red"><div class="summary-label">Cost Value</div><div class="summary-value red">$${totalCost.toFixed(2)}</div></div>
                              <div class="summary-card green"><div class="summary-label">Sell Value</div><div class="summary-value green">$${totalSell.toFixed(2)}</div></div>
                              <div class="summary-card blue"><div class="summary-label">Potential Profit</div><div class="summary-value blue">$${(totalSell - totalCost).toFixed(2)}</div></div>
                            </div>
                            <div class="section-title">Stock Items</div>
                            <table>
                              <thead><tr><th>Status</th><th>Item</th><th>Brand</th><th>Category</th><th class="text-right">Qty</th><th class="text-right">Low Stock</th><th class="text-right">Cost Value</th><th class="text-right">Sell Value</th><th class="text-right">Profit</th></tr></thead>
                              <tbody>
                                ${filtered.length === 0 ? `<tr><td colspan="9" style="text-align:center;padding:20px;color:#666;">No matching stock items.</td></tr>` : filtered.map((item) => {
                                  const isOut = item.current_quantity === 0;
                                  const isLow = item.current_quantity > 0 && item.current_quantity <= item.low_stock_threshold;
                                  const status = isOut ? 'Out' : isLow ? 'Low' : 'OK';
                                  const tint = isOut ? '#fee2e2' : isLow ? '#fef3c7' : '';
                                  const costValue = item.current_quantity * item.cost_price;
                                  const sellValue = item.current_quantity * item.sell_price;
                                  return `<tr${tint ? ` style="background:${tint};"` : ''}><td><strong>${status}</strong></td><td>${item.item_name || ''}</td><td>${item.brand || '-'}</td><td>${item.category || '-'}</td><td class="text-right">${item.current_quantity} ${item.unit_of_measure || ''}</td><td class="text-right">${item.low_stock_threshold}</td><td class="text-right">$${costValue.toFixed(2)}</td><td class="text-right">$${sellValue.toFixed(2)}</td><td class="text-right">$${(sellValue - costValue).toFixed(2)}</td></tr>`;
                                }).join('')}
                                ${filtered.length > 0 ? `<tr class="totals-row"><td colspan="6"><strong>TOTALS</strong></td><td class="text-right"><strong>$${totalCost.toFixed(2)}</strong></td><td class="text-right"><strong>$${totalSell.toFixed(2)}</strong></td><td class="text-right"><strong>$${(totalSell - totalCost).toFixed(2)}</strong></td></tr>` : ''}
                              </tbody>
                            </table>
                          `;
                          openPrintPreview('Stock Inventory Summary', contentHtml, { printerHint: settings?.printer_job_card });
                        }}
                        data-testid="print-stock-summary"
                        style={{ backgroundColor: '#6366f1', color: 'white' }}
                      >
                        🖨️ Print
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {stockItemsLoading && (
                    <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>Loading stock inventory…</div>
                  )}
                  {/* Filter row */}
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <div style={{ flex: '1 1 300px' }}>
                      <Input
                        type="text"
                        placeholder="Search by item, brand, code, SKU or category..."
                        value={stockSearch}
                        onChange={(e) => setStockSearch(e.target.value)}
                        data-testid="stock-summary-search"
                      />
                    </div>
                    {[
                      { value: 'all', label: 'All Items' },
                      { value: 'in_stock', label: 'In Stock' },
                      { value: 'low', label: 'Low Stock' },
                      { value: 'out', label: 'Out of Stock' },
                    ].map((opt) => (
                      <Button
                        key={opt.value}
                        type="button"
                        variant={stockStatusFilter === opt.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setStockStatusFilter(opt.value)}
                        data-testid={`stock-status-${opt.value}`}
                      >
                        {opt.label}
                      </Button>
                    ))}
                    {(stockSearch || stockStatusFilter !== 'all') && (
                      <Button variant="outline" size="sm" onClick={() => { setStockSearch(''); setStockStatusFilter('all'); }} data-testid="stock-clear-filters">
                        Clear
                      </Button>
                    )}
                  </div>

                  {(() => {
                    const q = stockSearch.toLowerCase().trim();
                    const filteredItems = stockItems.filter((item) => {
                      const isOut = item.current_quantity === 0;
                      const isLow = item.current_quantity > 0 && item.current_quantity <= item.low_stock_threshold;
                      if (stockStatusFilter === 'in_stock' && (isOut || isLow)) return false;
                      if (stockStatusFilter === 'low' && !isLow) return false;
                      if (stockStatusFilter === 'out' && !isOut) return false;
                      if (q) {
                        const hay = `${item.item_name || ''} ${item.brand || ''} ${item.item_code || ''} ${item.sku || ''} ${item.category || ''}`.toLowerCase();
                        if (!hay.includes(q)) return false;
                      }
                      return true;
                    });
                    return (
                      <>
                        {/* Summary Cards — reflect the filtered set */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Items Shown</div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{filteredItems.length}<span style={{ fontSize: '0.875rem', color: '#9ca3af', fontWeight: 'normal' }}> / {stockItems.length}</span></div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Low Stock</div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
                                {filteredItems.filter(item => item.current_quantity > 0 && item.current_quantity <= item.low_stock_threshold).length}
                              </div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Out of Stock</div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626' }}>
                                {filteredItems.filter(item => item.current_quantity === 0).length}
                              </div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Total Stock Value</div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#16a34a' }}>
                                ${filteredItems.reduce((sum, item) => sum + (item.current_quantity * item.cost_price), 0).toFixed(2)}
                              </div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Potential Revenue</div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2563eb' }}>
                                ${filteredItems.reduce((sum, item) => sum + (item.current_quantity * item.sell_price), 0).toFixed(2)}
                              </div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Potential Profit</div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7c3aed' }}>
                                ${filteredItems.reduce((sum, item) => sum + (item.current_quantity * (item.sell_price - item.cost_price)), 0).toFixed(2)}
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Stock Items Table */}
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Status</TableHead>
                              <TableHead>Item</TableHead>
                              <TableHead>Brand</TableHead>
                              <TableHead>Category</TableHead>
                              <TableHead>Current Qty</TableHead>
                              <TableHead>Low Stock Alert</TableHead>
                              <TableHead>Cost Value</TableHead>
                              <TableHead>Sell Value</TableHead>
                              <TableHead>Potential Profit</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredItems.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
                                  {stockItems.length === 0 ? 'No stock items found. Add stock items from the Stock page.' : 'No items match the current filters.'}
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredItems.map((item) => {
                                const isOutOfStock = item.current_quantity === 0;
                                const isLowStock = item.current_quantity > 0 && item.current_quantity <= item.low_stock_threshold;
                                const costValue = item.current_quantity * item.cost_price;
                                const sellValue = item.current_quantity * item.sell_price;
                                const profit = sellValue - costValue;
                                const isSelected = selectedStockItem?.id === item.id;
                                
                                return (
                                  <TableRow 
                                    key={item.id}
                                    onClick={() => setSelectedStockItem(isSelected ? null : item)}
                                    style={{ 
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? '#e0f2fe' : (isOutOfStock ? '#fee2e2' : isLowStock ? '#fef3c7' : 'transparent'),
                                      fontWeight: isSelected ? 600 : undefined,
                                    }}
                                    className="hover:bg-gray-50"
                                    data-testid={`stock-row-${item.item_code || item.id}`}
                                  >
                                    <TableCell>
                                      {isOutOfStock ? (
                                        <span style={{ color: '#dc2626', fontWeight: '600', fontSize: '1.2rem' }}>🔴</span>
                                      ) : isLowStock ? (
                                        <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '1.2rem' }}>🟡</span>
                                      ) : (
                                        <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '1.2rem' }}>🟢</span>
                                      )}
                                    </TableCell>
                                    <TableCell style={{ fontWeight: '600' }}>{item.item_name}</TableCell>
                                    <TableCell>{item.brand || '-'}</TableCell>
                                    <TableCell>{item.category || '-'}</TableCell>
                                    <TableCell style={{ fontWeight: '600' }}>
                                      {item.current_quantity} {item.unit_of_measure}
                                    </TableCell>
                                    <TableCell>{item.low_stock_threshold}</TableCell>
                                    <TableCell>
                                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        ${costValue.toFixed(2)}
                                        <StockCostHistoryPopover
                                          stockItemId={item.id}
                                          itemName={item.item_name}
                                          currentCost={item.cost_price}
                                        />
                                      </span>
                                    </TableCell>
                                    <TableCell>${sellValue.toFixed(2)}</TableCell>
                                    <TableCell style={{ 
                                      fontWeight: '600',
                                      color: profit >= 0 ? '#16a34a' : '#dc2626'
                                    }}>
                                      ${profit.toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </>
                    );
                  })()}

                  {/* Inline drill-in — jobs consuming the selected stock item */}
                  {selectedStockItem && (() => {
                    const nameLc = (selectedStockItem.item_name || '').toLowerCase();
                    const codeLc = (selectedStockItem.item_code || '').toLowerCase();
                    const matches = (si) => {
                      if (!si) return false;
                      if (si.stock_item_id && selectedStockItem.id && si.stock_item_id === selectedStockItem.id) return true;
                      const siName = (si.item_name || si.name || '').toLowerCase();
                      const siCode = (si.item_code || si.code || '').toLowerCase();
                      return (nameLc && siName && siName === nameLc) || (codeLc && siCode && siCode === codeLc);
                    };
                    const jobsUsingItem = (jobs || []).filter(j => Array.isArray(j.stock_items) && j.stock_items.some(matches));
                    const totalQty = jobsUsingItem.reduce((sum, j) => {
                      const stk = Array.isArray(j.stock_items) ? j.stock_items : [];
                      const q = stk.filter(matches).reduce((s, si) => s + (parseFloat(si.quantity) || 0), 0);
                      return sum + q;
                    }, 0);
                    const innerFiltered = innerStatusFilter ? jobsUsingItem.filter(j => j.status === innerStatusFilter) : jobsUsingItem;
                    return (
                      <Card style={{ marginTop: '2rem' }} data-testid="stock-drill-in-card">
                        <CardHeader>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CardTitle>
                              Jobs using {selectedStockItem.item_name} ({innerFiltered.length}{innerStatusFilter ? ` of ${jobsUsingItem.length}` : ''})
                            </CardTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setSelectedStockItem(null); setInnerStatusFilter(null); }}
                            >
                              Clear Filter
                            </Button>
                          </div>
                          <CardDescription>
                            {jobsUsingItem.length > 0
                              ? `${totalQty.toFixed(2)} ${selectedStockItem.unit_of_measure || 'units'} consumed across the current date range · Current on-hand: ${selectedStockItem.current_quantity}`
                              : `No jobs in the current date range have consumed this item yet · Current on-hand: ${selectedStockItem.current_quantity}`}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {jobsUsingItem.length > 0 && (
                            <>
                              <WorkflowHealthBar
                                jobs={jobsUsingItem}
                                statusColors={settings?.status_colors || {}}
                                label={selectedStockItem.item_name}
                              />
                              <StatusBreakdownChips
                                jobs={jobsUsingItem}
                                statusColors={settings?.status_colors || {}}
                                value={innerStatusFilter}
                                onChange={setInnerStatusFilter}
                                label={selectedStockItem.item_name}
                              />
                            </>
                          )}
                          <SortableJobsTable
                            jobs={innerFiltered}
                            columns={[
                              { key: 'job_number',    label: 'Job #',    type: 'number', cellClassName: 'font-semibold' },
                              { key: 'customer_name', label: 'Customer', type: 'string' },
                              { key: 'status',        label: 'Status',   type: 'string' },
                              { key: 'job_type',      label: 'Type',     type: 'string' },
                              { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                              { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                              { key: 'actions',       label: 'Actions',  sortable: false, render: (j) => (
                                <Button variant="outline" size="sm" onClick={() => navigate(`/job/${j.id}`)} data-testid={`stock-drill-view-${j.job_number}`}>View</Button>
                              ) },
                            ]}
                          />
                        </CardContent>
                      </Card>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Jul 2026 — Stock Analysis tab. Per-item sales & profit breakdown.
                Cash-basis (job.revenue_date in period). Sortable table + search
                + drill-in per row showing the contributing jobs. */}
            <TabsContent value="stock-analysis">
              <Card>
                <CardHeader>
                  <CardTitle>📦 Stock Analysis</CardTitle>
                  <CardDescription>
                    Per-item sales &amp; profit breakdown from paid jobs in the selected period.
                    Cash-basis (counts stock items whose job was paid in the range).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {stockAnalysisLoading && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading stock analysis…</div>
                  )}
                  {!stockAnalysisLoading && (!stockAnalysisData || stockAnalysisData.items.length === 0) && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                      No stock sold in this period, or no paid jobs with stock items.
                    </div>
                  )}
                  {!stockAnalysisLoading && stockAnalysisData && stockAnalysisData.items.length > 0 && (() => {
                    const q = stockAnalysisSearch.trim().toLowerCase();
                    let rows = stockAnalysisData.items;
                    if (q) {
                      rows = rows.filter(r =>
                        (r.brand || '').toLowerCase().includes(q)
                        || (r.stock_item_name || '').toLowerCase().includes(q)
                      );
                    }
                    const { column, dir } = stockAnalysisSort;
                    const factor = dir === 'asc' ? 1 : -1;
                    rows = [...rows].sort((a, b) => {
                      const av = column === 'brand' ? `${a.brand} ${a.stock_item_name}`.toLowerCase() : a[column];
                      const bv = column === 'brand' ? `${b.brand} ${b.stock_item_name}`.toLowerCase() : b[column];
                      if (av < bv) return -1 * factor;
                      if (av > bv) return 1 * factor;
                      return 0;
                    });
                    const t = rows.reduce((acc, r) => {
                      acc.qty += r.quantity_sold;
                      acc.rev += r.revenue;
                      acc.cost += r.cost;
                      acc.profit += r.profit;
                      return acc;
                    }, { qty: 0, rev: 0, cost: 0, profit: 0 });
                    t.margin = t.rev > 0 ? (t.profit / t.rev * 100) : 0;

                    const clickHeader = (col) => setStockAnalysisSort(s => ({
                      column: col,
                      dir: s.column === col && s.dir === 'desc' ? 'asc' : 'desc',
                    }));
                    const arrow = (col) => stockAnalysisSort.column === col
                      ? (stockAnalysisSort.dir === 'asc' ? ' ▲' : ' ▼')
                      : '';

                    const exportCsv = () => {
                      const header = ['Brand', 'Item', 'Qty Sold', 'Revenue', 'Cost', 'Profit', 'Margin %'];
                      const csv = [header.join(',')].concat(
                        rows.map(r => [
                          `"${(r.brand || '').replace(/"/g, '""')}"`,
                          `"${(r.stock_item_name || '').replace(/"/g, '""')}"`,
                          r.quantity_sold.toFixed(2),
                          r.revenue.toFixed(2),
                          r.cost.toFixed(2),
                          r.profit.toFixed(2),
                          r.margin_pct.toFixed(2),
                        ].join(','))
                      ).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `stock-analysis-${todayLocalISO()}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    };

                    return (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
                          <Input
                            type="text"
                            placeholder="Search brand or item…"
                            value={stockAnalysisSearch}
                            onChange={(e) => setStockAnalysisSearch(e.target.value)}
                            style={{ maxWidth: '280px' }}
                            data-testid="stock-analysis-search"
                          />
                          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            {rows.length} of {stockAnalysisData.items.length} items
                          </span>
                          <div style={{ marginLeft: 'auto' }}>
                            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="stock-analysis-export-csv">
                              📥 Export CSV
                            </Button>
                          </div>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <th onClick={() => clickHeader('brand')} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', cursor: 'pointer', color: '#334155' }}>Brand + Item{arrow('brand')}</th>
                                <th onClick={() => clickHeader('quantity_sold')} style={{ padding: '0.5rem 0.75rem', textAlign: 'right', cursor: 'pointer', color: '#334155' }}>Qty Sold{arrow('quantity_sold')}</th>
                                <th onClick={() => clickHeader('revenue')} style={{ padding: '0.5rem 0.75rem', textAlign: 'right', cursor: 'pointer', color: '#334155' }}>Revenue{arrow('revenue')}</th>
                                <th onClick={() => clickHeader('cost')} style={{ padding: '0.5rem 0.75rem', textAlign: 'right', cursor: 'pointer', color: '#334155' }}>Cost{arrow('cost')}</th>
                                <th onClick={() => clickHeader('profit')} style={{ padding: '0.5rem 0.75rem', textAlign: 'right', cursor: 'pointer', color: '#334155' }}>Profit{arrow('profit')}</th>
                                <th onClick={() => clickHeader('margin_pct')} style={{ padding: '0.5rem 0.75rem', textAlign: 'right', cursor: 'pointer', color: '#334155' }}>Margin{arrow('margin_pct')}</th>
                                <th style={{ width: '30px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r) => {
                                const key = `${r.brand}::${r.stock_item_name}`;
                                const open = !!stockAnalysisExpanded[key];
                                return (
                                  <Fragment key={key}>
                                    <tr
                                      onClick={() => setStockAnalysisExpanded(prev => ({ ...prev, [key]: !open }))}
                                      style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9', backgroundColor: open ? '#f0f9ff' : 'transparent' }}
                                      data-testid={`stock-analysis-row-${key}`}
                                    >
                                      <td style={{ padding: '0.5rem 0.75rem', color: '#111827', fontWeight: 500 }}>
                                        <span style={{ marginRight: '0.4rem', color: '#94a3b8', fontSize: '0.75rem' }}>{open ? '▼' : '▶'}</span>
                                        <strong>{r.brand}</strong>{r.brand && r.stock_item_name && !r.stock_item_name.startsWith(r.brand) ? ` — ${r.stock_item_name}` : (r.brand ? '' : r.stock_item_name)}
                                      </td>
                                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{r.quantity_sold % 1 === 0 ? r.quantity_sold.toFixed(0) : r.quantity_sold.toFixed(2)}</td>
                                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>${r.revenue.toFixed(2)}</td>
                                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#64748b' }}>${r.cost.toFixed(2)}</td>
                                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 700, color: r.profit >= 0 ? '#059669' : '#dc2626' }}>${r.profit.toFixed(2)}</td>
                                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600, color: r.margin_pct >= 20 ? '#059669' : r.margin_pct >= 10 ? '#b45309' : '#dc2626' }}>{r.margin_pct.toFixed(1)}%</td>
                                      <td></td>
                                    </tr>
                                    {open && (
                                      <tr>
                                        <td colSpan={7} style={{ padding: '0.75rem 1.25rem', backgroundColor: '#eff6ff', borderBottom: '1px solid #dbeafe' }}>
                                          <div style={{ fontSize: '0.75rem', color: '#1e40af', fontWeight: 600, marginBottom: '0.4rem' }}>
                                            Contributing job lines ({r.details.length}):
                                          </div>
                                          <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                            <thead>
                                              <tr style={{ color: '#64748b', textAlign: 'left' }}>
                                                <th style={{ padding: '0.25rem 0.5rem' }}>Job #</th>
                                                <th style={{ padding: '0.25rem 0.5rem' }}>Customer</th>
                                                <th style={{ padding: '0.25rem 0.5rem' }}>Paid</th>
                                                <th style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>Qty</th>
                                                <th style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>Sell $</th>
                                                <th style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>Cost $</th>
                                                <th style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>Line Sell</th>
                                                <th style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>Line Profit</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {r.details.map((d, i) => (
                                                <tr
                                                  key={`${d.job_id}-${i}`}
                                                  onClick={(e) => { e.stopPropagation(); navigate(`/job/${d.job_id}`); }}
                                                  style={{ cursor: 'pointer', borderTop: '1px solid #dbeafe' }}
                                                >
                                                  <td style={{ padding: '0.25rem 0.5rem', color: '#2563eb', fontWeight: 600 }}>#{d.job_number}</td>
                                                  <td style={{ padding: '0.25rem 0.5rem' }}>{d.customer_name}</td>
                                                  <td style={{ padding: '0.25rem 0.5rem', color: '#64748b' }}>{d.date ? new Date(d.date).toLocaleDateString('en-AU') : ''}</td>
                                                  <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>{d.quantity % 1 === 0 ? d.quantity.toFixed(0) : d.quantity.toFixed(2)}</td>
                                                  <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>${d.sell_price.toFixed(2)}</td>
                                                  <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', color: '#64748b' }}>${d.cost_price.toFixed(2)}</td>
                                                  <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right' }}>${d.line_sell.toFixed(2)}</td>
                                                  <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', fontWeight: 600, color: d.line_profit >= 0 ? '#059669' : '#dc2626' }}>${d.line_profit.toFixed(2)}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                                <td style={{ padding: '0.6rem 0.75rem' }}>TOTALS</td>
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{t.qty % 1 === 0 ? t.qty.toFixed(0) : t.qty.toFixed(2)}</td>
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>${t.rev.toFixed(2)}</td>
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#64748b' }}>${t.cost.toFixed(2)}</td>
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: t.profit >= 0 ? '#059669' : '#dc2626' }}>${t.profit.toFixed(2)}</td>
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: t.margin >= 20 ? '#059669' : t.margin >= 10 ? '#b45309' : '#dc2626' }}>{t.margin.toFixed(1)}%</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Feb 2026 — Stock Reconciliation. Detects drift between the
                master stock_items.current_quantity and the ledger derived
                from purchases − job allocations. Read-only inspector plus a
                per-row Reconcile action. */}
            <TabsContent value="stock-reconciliation">
              <Card>
                <CardHeader>
                  <CardTitle>⚖️ Stock Reconciliation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ padding: '0.9rem 1.1rem', backgroundColor: '#eff6ff', borderRadius: '0.5rem', border: '1px solid #bfdbfe', borderLeft: '4px solid #2563eb', marginBottom: '1rem', fontSize: '0.85rem', color: '#1e40af', lineHeight: 1.5 }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>How this works</div>
                    For every stock item, <b>Expected qty</b> = (all-time purchases) − (all-time job allocations).
                    If <b>Current qty</b> disagrees with <b>Expected</b>, the ledger has drifted — usually because a stock allocation was reversed at some point without a matching physical action.
                    Click a row to see the contributing purchases &amp; jobs, then hit <b>Reconcile</b> to set Current = Expected. Every reconcile is logged on the stock item.
                  </div>

                  {stockReconLoading && (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b' }} data-testid="stock-recon-loading">Loading reconciliation…</div>
                  )}

                  {!stockReconLoading && stockReconData && (() => {
                    const items = stockReconData.items || [];
                    const totals = stockReconData.totals || {};
                    const filtered = stockReconShowClean ? items : items.filter(i => Math.abs(i.delta || 0) > 0.0001);
                    return (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
                          <div style={{ padding: '0.5rem 0.9rem', backgroundColor: totals.mismatch_count > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: '0.5rem', border: '1px solid', borderColor: totals.mismatch_count > 0 ? '#fecaca' : '#bbf7d0', fontSize: '0.85rem', color: totals.mismatch_count > 0 ? '#991b1b' : '#166534', fontWeight: 600 }} data-testid="stock-recon-summary">
                            {totals.mismatch_count > 0
                              ? `⚠️ ${totals.mismatch_count} item${totals.mismatch_count === 1 ? '' : 's'} with drift · ${totals.clean_count} clean`
                              : `✅ All ${totals.clean_count} stock items reconcile correctly`}
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}>
                            <input type="checkbox" checked={stockReconShowClean} onChange={(e) => setStockReconShowClean(e.target.checked)} data-testid="stock-recon-show-clean" />
                            Show clean items too
                          </label>
                          <Button variant="outline" size="sm" onClick={fetchStockReconciliation} data-testid="stock-recon-refresh">↻ Refresh</Button>
                        </div>

                        {filtered.length === 0 ? (
                          <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }} data-testid="stock-recon-empty">
                            {stockReconShowClean ? 'No stock items found.' : '✅ Every stock item reconciles — no drift detected.'}
                          </div>
                        ) : (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ textAlign: 'left', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                                  <th style={{ padding: '0.5rem 0.6rem' }}></th>
                                  <th style={{ padding: '0.5rem 0.6rem' }}>Item</th>
                                  <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Purchased</th>
                                  <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Allocated</th>
                                  <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Expected</th>
                                  <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Current</th>
                                  <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Δ</th>
                                  <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {filtered.map((row) => {
                                  const isOpen = !!stockReconExpanded[row.stock_item_id];
                                  const drift = Math.abs(row.delta || 0) > 0.0001;
                                  return (
                                    <Fragment key={row.stock_item_id}>
                                      <tr
                                        style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: drift ? '#fef2f2' : 'transparent', cursor: 'pointer' }}
                                        onClick={() => setStockReconExpanded(s => ({ ...s, [row.stock_item_id]: !isOpen }))}
                                        data-testid={`stock-recon-row-${row.stock_item_id}`}
                                      >
                                        <td style={{ padding: '0.5rem 0.6rem', color: '#64748b', width: '1.5rem' }}>{isOpen ? '▾' : '▸'}</td>
                                        <td style={{ padding: '0.5rem 0.6rem', fontWeight: 600 }}>
                                          {row.brand && row.brand !== row.stock_item_name ? `${row.brand} ` : ''}{row.stock_item_name}
                                          {row.deleted_from_master && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: '#991b1b', fontWeight: 700 }}>DELETED FROM MASTER</span>}
                                        </td>
                                        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>{row.purchased_qty}</td>
                                        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>{row.allocated_qty}</td>
                                        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontWeight: 600 }}>{row.expected_qty}</td>
                                        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontWeight: 600 }}>{row.current_qty}</td>
                                        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontWeight: 700, color: drift ? '#dc2626' : '#16a34a' }} data-testid={`stock-recon-delta-${row.stock_item_id}`}>
                                          {drift ? (row.delta > 0 ? `+${row.delta}` : row.delta) : '0'}
                                        </td>
                                        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                          {drift && !row.deleted_from_master && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              disabled={stockReconBusyId === row.stock_item_id}
                                              onClick={() => handleReconcileStock(row)}
                                              data-testid={`stock-recon-fix-${row.stock_item_id}`}
                                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                                            >
                                              {stockReconBusyId === row.stock_item_id ? '…' : 'Reconcile'}
                                            </Button>
                                          )}
                                        </td>
                                      </tr>
                                      {isOpen && (
                                        <tr>
                                          <td colSpan={8} style={{ padding: '0.5rem 1.5rem 1rem 2rem', backgroundColor: '#fafafa' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                              <div>
                                                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f766e', marginBottom: '0.4rem' }}>Purchases ({row.purchases.length})</div>
                                                {row.purchases.length === 0 ? (
                                                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>No purchases recorded.</div>
                                                ) : (
                                                  <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                      <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                                        <th style={{ padding: '0.25rem 0.4rem' }}>Date</th>
                                                        <th style={{ padding: '0.25rem 0.4rem' }}>Supplier</th>
                                                        <th style={{ padding: '0.25rem 0.4rem' }}>Invoice</th>
                                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>Qty</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {row.purchases.map((p) => (
                                                        <tr key={p.purchase_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                          <td style={{ padding: '0.25rem 0.4rem' }}>{p.date}</td>
                                                          <td style={{ padding: '0.25rem 0.4rem' }}>{p.supplier}</td>
                                                          <td style={{ padding: '0.25rem 0.4rem', color: '#64748b' }}>{p.invoice}</td>
                                                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right', fontWeight: 600 }}>{p.quantity}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                )}
                                              </div>
                                              <div>
                                                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#7c2d12', marginBottom: '0.4rem' }}>Job allocations ({row.allocations.length})</div>
                                                {row.allocations.length === 0 ? (
                                                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Not allocated to any job.</div>
                                                ) : (
                                                  <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                      <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                                        <th style={{ padding: '0.25rem 0.4rem' }}>Job #</th>
                                                        <th style={{ padding: '0.25rem 0.4rem' }}>Customer</th>
                                                        <th style={{ padding: '0.25rem 0.4rem' }}>Status</th>
                                                        <th style={{ padding: '0.25rem 0.4rem' }}>Paid</th>
                                                        <th style={{ padding: '0.25rem 0.4rem' }}>Date</th>
                                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>Qty</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {row.allocations.map((a) => (
                                                        <tr
                                                          key={`${a.job_id}-${a.line_id || Math.random()}`}
                                                          onClick={() => navigate(`/job/${a.job_id}`)}
                                                          style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff7ed'; }}
                                                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                        >
                                                          <td style={{ padding: '0.25rem 0.4rem', color: '#2563eb', fontWeight: 600 }}>#{a.job_number}</td>
                                                          <td style={{ padding: '0.25rem 0.4rem' }}>{a.customer_name}</td>
                                                          <td style={{ padding: '0.25rem 0.4rem', color: '#64748b' }}>{a.status}</td>
                                                          <td style={{ padding: '0.25rem 0.4rem', color: '#64748b' }}>{a.payment_status}</td>
                                                          <td style={{ padding: '0.25rem 0.4rem', color: '#64748b' }}>{a.date}</td>
                                                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right', fontWeight: 600 }}>{a.quantity}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                )}
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>


            {/* Feb 2026 — Review Requests: paid customers whose invoice was
                emailed at least N days ago and who haven't yet received a
                review request. Confirm dialog with rendered preview before
                sending. */}
            <TabsContent value="review-requests">
              <Card>
                <CardHeader>
                  <CardTitle>⭐ Review Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ padding: '0.9rem 1.1rem', backgroundColor: '#f5f3ff', borderRadius: '0.5rem', border: '1px solid #ddd6fe', borderLeft: '4px solid #7c3aed', marginBottom: '1rem', fontSize: '0.85rem', color: '#4c1d95', lineHeight: 1.5 }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>How this works</div>
                    Lists <b>paid</b> customers whose invoice was emailed at least the selected number of days ago, and who <b>haven&apos;t</b> been asked for a Google review yet.
                    Turn this into your review call-list — click <b>Send</b>, preview the email, hit Send in the dialog, row disappears once done.
                    Configure the template and Google review URL under <b>Settings → Email Templates</b>.
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#475569' }}>
                      Minimum days since invoice emailed:
                      <select
                        data-testid="review-min-days"
                        value={reviewMinDays}
                        onChange={(e) => setReviewMinDays(parseInt(e.target.value, 10))}
                        style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.85rem', background: 'white' }}
                      >
                        <option value={0}>0 (all)</option>
                        <option value={3}>3</option>
                        <option value={7}>7</option>
                        <option value={14}>14</option>
                        <option value={30}>30</option>
                      </select>
                    </label>
                    <Button variant="outline" size="sm" onClick={fetchReviewCandidates} data-testid="review-refresh">↻ Refresh</Button>
                    {reviewCandidatesData && (
                      <span style={{ padding: '0.4rem 0.8rem', backgroundColor: '#eef2ff', borderRadius: '0.5rem', fontSize: '0.85rem', color: '#3730a3', fontWeight: 600 }} data-testid="review-candidates-count">
                        {reviewCandidatesData.total} candidate{reviewCandidatesData.total === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>

                  {reviewCandidatesLoading && (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b' }} data-testid="review-loading">Loading candidates…</div>
                  )}

                  {!reviewCandidatesLoading && reviewCandidatesData && reviewCandidatesData.items.length === 0 && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }} data-testid="review-empty">
                      No paid customers waiting to be asked. Change the minimum-days filter to see more.
                    </div>
                  )}

                  {!reviewCandidatesLoading && reviewCandidatesData && reviewCandidatesData.items.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ textAlign: 'left', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ padding: '0.5rem 0.6rem' }}>Job #</th>
                            <th style={{ padding: '0.5rem 0.6rem' }}>Customer</th>
                            <th style={{ padding: '0.5rem 0.6rem' }}>Email</th>
                            <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Days since invoiced</th>
                            <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Amount</th>
                            <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {reviewCandidatesData.items.map((row) => (
                            <tr key={row.job_id} style={{ borderBottom: '1px solid #f1f5f9' }} data-testid={`review-row-${row.job_number}`}>
                              <td style={{ padding: '0.5rem 0.6rem' }}>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/job/${row.job_id}`)}
                                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                                >#{row.job_number}</button>
                              </td>
                              <td style={{ padding: '0.5rem 0.6rem' }}>{row.customer_name}</td>
                              <td style={{ padding: '0.5rem 0.6rem', color: '#475569' }}>{row.email}</td>
                              <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>{row.days_since_invoice}</td>
                              <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>${(row.total_amount || 0).toFixed(2)}</td>
                              <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={reviewBusyJobId === row.job_id}
                                  onClick={() => openReviewConfirm(row.job_id)}
                                  data-testid={`review-send-${row.job_number}`}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', backgroundColor: '#7c3aed', color: 'white', borderColor: '#7c3aed' }}
                                >
                                  {reviewBusyJobId === row.job_id ? '…' : '⭐ Send'}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Confirm dialog with rendered preview. */}
              <Dialog open={reviewConfirmState.open} onOpenChange={(open) => { if (!open) setReviewConfirmState({ open: false, jobId: null, preview: null }); }}>
                <DialogContent style={{ maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }} data-testid="review-request-dialog">
                  <DialogHeader>
                    <DialogTitle>⭐ Send Google Review Request</DialogTitle>
                  </DialogHeader>
                  {reviewConfirmState.preview && (
                    <div style={{ padding: '0.5rem 0', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                      <div style={{ padding: '0.75rem', backgroundColor: '#f5f3ff', borderRadius: '0.5rem', fontSize: '0.85rem' }}>
                        <strong>To:</strong> {reviewConfirmState.preview.to}<br />
                        <strong>Subject:</strong> {reviewConfirmState.preview.subject}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.35rem' }}>Message preview</div>
                        <div style={{ whiteSpace: 'pre-wrap', padding: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.85rem', backgroundColor: 'white', maxHeight: '340px', overflowY: 'auto' }} data-testid="review-body-preview">
                          {reviewConfirmState.preview.body}
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <Button variant="outline" onClick={() => setReviewConfirmState({ open: false, jobId: null, preview: null })} disabled={reviewBusyJobId !== null} data-testid="review-cancel-btn">
                      Cancel
                    </Button>
                    <Button onClick={confirmSendReviewCandidate} disabled={reviewBusyJobId !== null} style={{ backgroundColor: '#7c3aed', color: 'white' }} data-testid="review-confirm-send-btn">
                      {reviewBusyJobId !== null ? '⏳ Sending...' : '⭐ Send'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>



            <TabsContent value="job-type-analysis">
              <Card>
                <CardHeader>
                  <CardTitle>🔬 Job Type Analysis</CardTitle>
                  <CardDescription>
                    Recovery outcomes per job type. Only counts jobs that reached a final status
                    (<strong>Finish</strong>, <strong>Not Recoverable</strong>, or <strong>Not Proceeding</strong>) within the selected date range.
                    In-progress jobs (Booked, Quoted, Wait, Approved, Parts) are excluded — their outcome isn't decided yet.
                    Click a row to see the individual jobs; click a job to open it.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {jobTypeAnalysisLoading && (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                  )}
                  {!jobTypeAnalysisLoading && (!jobTypeAnalysisData || jobTypeAnalysisData.rows.length === 0) && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                      No completed jobs in the selected date range.
                    </div>
                  )}
                  {!jobTypeAnalysisLoading && jobTypeAnalysisData && jobTypeAnalysisData.rows.length > 0 && (
                    <>
                      <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="jta-export-csv"
                          onClick={() => {
                            const rows = jobTypeAnalysisData.rows;
                            const t = jobTypeAnalysisData.totals;
                            const header = ['Job Type', 'Total', 'Finish', 'Finish %', 'Not Recoverable', 'Not Recoverable %', 'Not Proceeding', 'Not Proceeding %', 'Success %', 'Revenue'];
                            const lines = [header.join(',')];
                            rows.forEach(r => {
                              lines.push([
                                `"${(r.job_type || '').replace(/"/g, '""')}"`,
                                r.total, r.finish, r.finish_pct,
                                r.not_recoverable, r.not_recoverable_pct,
                                r.not_proceeding, r.not_proceeding_pct,
                                r.success_pct,
                                (r.revenue || 0).toFixed(2),
                              ].join(','));
                            });
                            lines.push([
                              '"TOTAL"',
                              t.total, t.finish, t.finish_pct,
                              t.not_recoverable, t.not_recoverable_pct,
                              t.not_proceeding, t.not_proceeding_pct,
                              t.success_pct,
                              (t.revenue || 0).toFixed(2),
                            ].join(','));
                            const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `job-type-analysis-${jobTypeAnalysisData.start_date || 'all'}_${jobTypeAnalysisData.end_date || 'time'}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          }}
                        >
                          Export CSV
                        </Button>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead style={{ width: '32px' }}></TableHead>
                            <TableHead>Job Type</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="text-right">Finish</TableHead>
                            <TableHead className="text-right">Finish %</TableHead>
                            <TableHead className="text-right">Not Recoverable</TableHead>
                            <TableHead className="text-right">NR %</TableHead>
                            <TableHead className="text-right">Not Proceeding</TableHead>
                            <TableHead className="text-right">NP %</TableHead>
                            <TableHead className="text-right">Success %</TableHead>
                            <TableHead className="text-right">Revenue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {jobTypeAnalysisData.rows.map((row) => {
                            const isOpen = !!jobTypeAnalysisExpanded[row.job_type];
                            return (
                              <Fragment key={row.job_type}>
                                <TableRow
                                  onClick={() => setJobTypeAnalysisExpanded(prev => ({ ...prev, [row.job_type]: !prev[row.job_type] }))}
                                  style={{ cursor: 'pointer', backgroundColor: isOpen ? '#f0f9ff' : 'transparent' }}
                                  className="hover:bg-gray-50"
                                  data-testid={`jta-row-${row.job_type}`}
                                >
                                  <TableCell style={{ fontFamily: 'monospace', color: '#6b7280', textAlign: 'center' }}>{isOpen ? '▼' : '▶'}</TableCell>
                                  <TableCell style={{ fontWeight: 500 }}>{row.job_type}</TableCell>
                                  <TableCell className="text-right font-semibold">{row.total}</TableCell>
                                  <TableCell className="text-right" style={{ color: '#059669' }}>{row.finish}</TableCell>
                                  <TableCell className="text-right" style={{ color: '#059669' }}>{row.finish_pct}%</TableCell>
                                  <TableCell className="text-right" style={{ color: '#dc2626' }}>{row.not_recoverable}</TableCell>
                                  <TableCell className="text-right" style={{ color: '#dc2626' }}>{row.not_recoverable_pct}%</TableCell>
                                  <TableCell className="text-right" style={{ color: '#d97706' }}>{row.not_proceeding}</TableCell>
                                  <TableCell className="text-right" style={{ color: '#d97706' }}>{row.not_proceeding_pct}%</TableCell>
                                  <TableCell className="text-right font-semibold" style={{ color: '#059669' }}>{row.success_pct}%</TableCell>
                                  <TableCell className="text-right font-semibold" style={{ color: '#0f172a' }}>${(row.revenue || 0).toFixed(2)}</TableCell>
                                </TableRow>
                                {isOpen && (
                                  <TableRow>
                                    <TableCell colSpan={11} style={{ backgroundColor: '#fafafa', padding: '0.75rem 1.25rem' }}>
                                      <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#475569' }}>
                                        Showing {row.jobs.length} job{row.jobs.length === 1 ? '' : 's'} — click a job number to open.
                                      </div>
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Job #</TableHead>
                                            <TableHead>Customer</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Date In</TableHead>
                                            <TableHead>Date Finished</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {row.jobs.map(j => {
                                            const statusColor = j.status === 'Finish' ? '#059669'
                                              : j.status === 'Not Recoverable' ? '#dc2626'
                                              : j.status === 'Not Proceeding' ? '#d97706'
                                              : '#475569';
                                            return (
                                              <TableRow
                                                key={j.id}
                                                onClick={() => navigate(`/job/${j.id}`)}
                                                style={{ cursor: 'pointer' }}
                                                className="hover:bg-gray-100"
                                                data-testid={`jta-drill-job-${j.job_number}`}
                                              >
                                                <TableCell style={{ color: '#2563eb', textDecoration: 'underline' }}>{j.job_number}</TableCell>
                                                <TableCell>{j.customer_name}</TableCell>
                                                <TableCell style={{ color: statusColor, fontWeight: 500 }}>{j.status}</TableCell>
                                                <TableCell>{j.date_in ? formatDate(j.date_in) : '—'}</TableCell>
                                                <TableCell>{j.date_finished ? formatDate(j.date_finished) : '—'}</TableCell>
                                              </TableRow>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Fragment>
                            );
                          })}
                          <TableRow style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                            <TableCell></TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell className="text-right">{jobTypeAnalysisData.totals.total}</TableCell>
                            <TableCell className="text-right" style={{ color: '#059669' }}>{jobTypeAnalysisData.totals.finish}</TableCell>
                            <TableCell className="text-right" style={{ color: '#059669' }}>{jobTypeAnalysisData.totals.finish_pct}%</TableCell>
                            <TableCell className="text-right" style={{ color: '#dc2626' }}>{jobTypeAnalysisData.totals.not_recoverable}</TableCell>
                            <TableCell className="text-right" style={{ color: '#dc2626' }}>{jobTypeAnalysisData.totals.not_recoverable_pct}%</TableCell>
                            <TableCell className="text-right" style={{ color: '#d97706' }}>{jobTypeAnalysisData.totals.not_proceeding}</TableCell>
                            <TableCell className="text-right" style={{ color: '#d97706' }}>{jobTypeAnalysisData.totals.not_proceeding_pct}%</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: '#059669' }}>{jobTypeAnalysisData.totals.success_pct}%</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: '#0f172a' }}>${(jobTypeAnalysisData.totals.revenue || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>



            <TabsContent value="supplier-spending">
              <Card>
                <CardHeader>
                  <CardTitle>Supplier Spending Analysis</CardTitle>
                  <CardDescription>How much you spent with each supplier on <strong>stock purchases</strong> in the period — filtered by <em>purchase date</em>. Non-stock supplier bills (utilities, subscriptions, freight, etc.) are on the <strong>Expenses By Supplier</strong> tab. Numbers include GST.</CardDescription>
                </CardHeader>
                <CardContent>
                  {(purchasesLoading || suppliersLoading) && (
                    <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>Loading supplier &amp; purchase data…</div>
                  )}
                  {/* Filters */}
                  <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
                      <div style={{ position: 'relative', minWidth: '260px' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                          Filter by Supplier
                        </label>
                        <Input
                          type="text"
                          placeholder="Start typing a supplier name..."
                          value={supplierFilterSearch}
                          onChange={(e) => {
                            setSupplierFilterSearch(e.target.value);
                            setShowSupplierFilterDropdown(true);
                            if (!e.target.value) setSelectedSupplier(null);
                          }}
                          onFocus={() => setShowSupplierFilterDropdown(true)}
                          onBlur={() => setTimeout(() => setShowSupplierFilterDropdown(false), 200)}
                          data-testid="supplier-spending-filter-input"
                        />
                        {showSupplierFilterDropdown && (() => {
                          const q = supplierFilterSearch.toLowerCase().trim();
                          const list = [...suppliers]
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .filter(s => !q || (s.name || '').toLowerCase().includes(q));
                          return (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '260px', overflowY: 'auto', background: 'white', border: '1px solid #cbd5e1', borderRadius: '0.375rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, marginTop: '0.25rem' }}>
                              <div
                                onClick={() => { setSelectedSupplier(null); setSupplierFilterSearch(''); setShowSupplierFilterDropdown(false); }}
                                onMouseDown={(e) => e.preventDefault()}
                                style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #e2e8f0', fontStyle: 'italic', color: '#475569' }}
                                data-testid="supplier-spending-filter-option-all"
                              >
                                All suppliers
                              </div>
                              {list.slice(0, 100).map((s) => (
                                <div
                                  key={s.id}
                                  onClick={() => { setSelectedSupplier(s); setSupplierFilterSearch(s.name); setShowSupplierFilterDropdown(false); }}
                                  onMouseDown={(e) => e.preventDefault()}
                                  style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', backgroundColor: selectedSupplier?.id === s.id ? '#eff6ff' : 'transparent' }}
                                  data-testid={`supplier-spending-filter-option-${s.id}`}
                                >
                                  {s.name}
                                </div>
                              ))}
                              {list.length === 0 && (
                                <div style={{ padding: '0.5rem 0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>No matching suppliers</div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      {selectedSupplier && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedSupplier(null); setSupplierFilterSearch(''); }}
                          >
                            Clear Supplier Filter
                          </Button>
                        </div>
                      )}
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.75rem' }}>
                      Date range is set by the global filter at the top of the page.
                    </p>
                    {dateRange !== 'all' && (
                      <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          id="compareSupplierPeriods"
                          checked={compareSupplierPeriods}
                          onChange={(e) => setCompareSupplierPeriods(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label 
                          htmlFor="compareSupplierPeriods" 
                          style={{ cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                        >
                          Compare to Previous Period
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Filter purchases by supplier and date range */}
                  {(() => {
                    let allPurchases = purchases;
                    
                    // Filter by supplier if one is selected
                    if (selectedSupplier) {
                      allPurchases = allPurchases.filter(p => p.supplier_id === selectedSupplier.id);
                    }
                    
                    let filteredPurchases = allPurchases;
                    let previousPeriodPurchases = [];
                    let currentPeriodLabel = '';
                    let previousPeriodLabel = '';

                    // Use the global dateRange filter (top of page)
                    const _bounds = getDateBoundaries(dateRange, startDate, endDate);
                    if (_bounds.start && _bounds.end) {
                      const sDate = new Date(_bounds.start + 'T00:00:00');
                      const eDate = new Date(_bounds.end + 'T23:59:59');
                      const daysDiff = Math.ceil((eDate - sDate) / (1000 * 60 * 60 * 24)) + 1;
                      const prevEndDate = new Date(sDate);
                      prevEndDate.setDate(prevEndDate.getDate() - 1);
                      const prevStartDate = new Date(prevEndDate);
                      prevStartDate.setDate(prevStartDate.getDate() - daysDiff + 1);

                      const labelMap = {
                        last7: ['Last 7 Days', 'Previous 7 Days'],
                        last30: ['Last 30 Days', 'Previous 30 Days'],
                        this_month: ['This Month', 'Previous Month'],
                        last_month: ['Last Month', 'Month Before'],
                        this_quarter: ['This Quarter', 'Previous Quarter'],
                        ytd: ['Year to Date', 'Same Period Last Year'],
                        custom: ['Selected Period', 'Previous Period'],
                      };
                      [currentPeriodLabel, previousPeriodLabel] = labelMap[dateRange] || ['Selected Period', 'Previous Period'];

                      filteredPurchases = allPurchases.filter(purchase => {
                        const pd = new Date(purchase.purchase_date);
                        return pd >= sDate && pd <= eDate;
                      });

                      if (compareSupplierPeriods) {
                        previousPeriodPurchases = allPurchases.filter(purchase => {
                          const pd = new Date(purchase.purchase_date);
                          return pd >= prevStartDate && pd <= prevEndDate;
                        });
                      }
                    }

                    // Calculate supplier spending for current period
                    const suppliersToShow = selectedSupplier ? [selectedSupplier] : suppliers;
                    
                    const supplierSpending = suppliersToShow.map(supplier => {
                      const supplierPurchases = filteredPurchases.filter(p => p.supplier_id === supplier.id);
                      const totalSpent = supplierPurchases.reduce((sum, p) => sum + p.total_cost, 0);
                      const purchaseCount = supplierPurchases.length;
                      const lastPurchase = supplierPurchases.length > 0 
                        ? new Date(Math.max(...supplierPurchases.map(p => new Date(p.purchase_date))))
                        : null;
                      const avgPurchase = purchaseCount > 0 ? totalSpent / purchaseCount : 0;
                      
                      // Calculate previous period data if comparison is enabled
                      let prevTotalSpent = 0;
                      let prevPurchaseCount = 0;
                      let change = 0;
                      let changePercent = 0;
                      
                      if (compareSupplierPeriods && previousPeriodPurchases.length > 0) {
                        const prevSupplierPurchases = previousPeriodPurchases.filter(p => p.supplier_id === supplier.id);
                        prevTotalSpent = prevSupplierPurchases.reduce((sum, p) => sum + p.total_cost, 0);
                        prevPurchaseCount = prevSupplierPurchases.length;
                        change = totalSpent - prevTotalSpent;
                        changePercent = prevTotalSpent > 0 ? ((change / prevTotalSpent) * 100) : (totalSpent > 0 ? 100 : 0);
                      }
                      
                      return {
                        ...supplier,
                        totalSpent,
                        purchaseCount,
                        lastPurchase,
                        avgPurchase,
                        purchases: supplierPurchases,
                        prevTotalSpent,
                        prevPurchaseCount,
                        change,
                        changePercent
                      };
                    }).filter(s => s.purchaseCount > 0 || (compareSupplierPeriods && s.prevPurchaseCount > 0)).sort((a, b) => b.totalSpent - a.totalSpent);

                    const totalSpent = supplierSpending.reduce((sum, s) => sum + s.totalSpent, 0);
                    const prevTotalSpent = supplierSpending.reduce((sum, s) => sum + s.prevTotalSpent, 0);
                    const totalChange = totalSpent - prevTotalSpent;
                    const totalChangePercent = prevTotalSpent > 0 ? ((totalChange / prevTotalSpent) * 100) : (totalSpent > 0 ? 100 : 0);

                    return (
                      <>
                        {/* Summary Cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                Active Suppliers {compareSupplierPeriods && `(${currentPeriodLabel})`}
                              </div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{supplierSpending.length}</div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                Total Spent {compareSupplierPeriods && `(${currentPeriodLabel})`}
                              </div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626' }}>
                                ${totalSpent.toFixed(2)}
                              </div>
                              {compareSupplierPeriods && (
                                <div style={{ marginTop: '0.5rem' }}>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    {previousPeriodLabel}: ${prevTotalSpent.toFixed(2)}
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.875rem', 
                                    fontWeight: '600',
                                    color: totalChange >= 0 ? '#dc2626' : '#16a34a'
                                  }}>
                                    {totalChange >= 0 ? '↑' : '↓'} ${Math.abs(totalChange).toFixed(2)} ({totalChangePercent.toFixed(1)}%)
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                Total Purchases {compareSupplierPeriods && `(${currentPeriodLabel})`}
                              </div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                                {filteredPurchases.length}
                              </div>
                              {compareSupplierPeriods && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                                  {previousPeriodLabel}: {previousPeriodPurchases.length}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.5rem' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Top Supplier</div>
                              <div style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                {supplierSpending.length > 0 ? supplierSpending[0].name : 'N/A'}
                              </div>
                              {supplierSpending.length > 0 && (
                                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                  ${supplierSpending[0].totalSpent.toFixed(2)}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>

                        {/* Supplier Spending Table or Purchase Details */}
                        {selectedSupplier ? (
                          // Show detailed purchase history when supplier is filtered
                          <div style={{ marginTop: '1rem' }}>
                            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#e0f2fe', borderRadius: '0.5rem' }}>
                              <h4 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
                                Purchase History: {selectedSupplier.name}
                              </h4>
                              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                                Showing {supplierSpending[0]?.purchaseCount || 0} transaction(s) {compareSupplierPeriods && `for ${currentPeriodLabel}`}
                              </p>
                              {compareSupplierPeriods && supplierSpending[0] && (
                                <div style={{ marginTop: '1rem', display: 'flex', gap: '2rem', fontSize: '0.875rem' }}>
                                  <div>
                                    <span style={{ color: '#6b7280' }}>{currentPeriodLabel}: </span>
                                    <span style={{ fontWeight: '600', color: '#dc2626' }}>
                                      ${supplierSpending[0].totalSpent.toFixed(2)}
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#6b7280' }}>{previousPeriodLabel}: </span>
                                    <span style={{ fontWeight: '600' }}>
                                      ${supplierSpending[0].prevTotalSpent.toFixed(2)}
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#6b7280' }}>Change: </span>
                                    <span style={{ 
                                      fontWeight: '600',
                                      color: supplierSpending[0].change >= 0 ? '#dc2626' : '#16a34a'
                                    }}>
                                      {supplierSpending[0].change >= 0 ? '↑' : '↓'} ${Math.abs(supplierSpending[0].change).toFixed(2)} ({supplierSpending[0].changePercent.toFixed(1)}%)
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Invoice #</TableHead>
                                  <TableHead>Items</TableHead>
                                  <TableHead>Subtotal</TableHead>
                                  <TableHead>GST</TableHead>
                                  <TableHead>Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {supplierSpending.length === 0 || supplierSpending[0].purchases.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                                      No purchases found for {selectedSupplier.name} in the selected date range.
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  supplierSpending[0].purchases.map((purchase) => (
                                    <TableRow key={purchase.id}>
                                      <TableCell>{formatDate(purchase.purchase_date)}</TableCell>
                                      <TableCell>{purchase.invoice_number || '-'}</TableCell>
                                      <TableCell>{purchase.line_items.length} item(s)</TableCell>
                                      <TableCell>${purchase.subtotal?.toFixed(2) || '0.00'}</TableCell>
                                      <TableCell>${purchase.gst_amount?.toFixed(2) || '0.00'}</TableCell>
                                      <TableCell style={{ fontWeight: '600' }}>${purchase.total_cost.toFixed(2)}</TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          // Show supplier summary table when no specific supplier is selected
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Rank</TableHead>
                                <TableHead>Supplier</TableHead>
                                <TableHead>{compareSupplierPeriods ? `${currentPeriodLabel} Purchases` : 'Total Purchases'}</TableHead>
                                <TableHead>{compareSupplierPeriods ? `${currentPeriodLabel} Spent` : 'Total Spent'}</TableHead>
                                {compareSupplierPeriods && (
                                  <>
                                    <TableHead>{previousPeriodLabel} Spent</TableHead>
                                    <TableHead>Change</TableHead>
                                  </>
                                )}
                                <TableHead>Avg Purchase</TableHead>
                                <TableHead>Last Purchase</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {supplierSpending.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={compareSupplierPeriods ? 8 : 6} style={{ textAlign: 'center', padding: '2rem' }}>
                                    No purchases found for the selected date range.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                supplierSpending.map((supplier, index) => (
                                  <TableRow 
                                    key={supplier.id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedSupplier(supplier)}
                                  >
                                    <TableCell style={{ fontWeight: '600' }}>#{index + 1}</TableCell>
                                    <TableCell style={{ fontWeight: '600' }}>{supplier.name}</TableCell>
                                    <TableCell>{supplier.purchaseCount}</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#dc2626' }}>
                                      ${supplier.totalSpent.toFixed(2)}
                                    </TableCell>
                                    {compareSupplierPeriods && (
                                      <>
                                        <TableCell style={{ color: '#6b7280' }}>
                                          ${supplier.prevTotalSpent.toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                          <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '0.25rem',
                                            fontWeight: '600',
                                            color: supplier.change >= 0 ? '#dc2626' : '#16a34a'
                                          }}>
                                            <span>{supplier.change >= 0 ? '↑' : '↓'}</span>
                                            <span>${Math.abs(supplier.change).toFixed(2)}</span>
                                            <span style={{ fontSize: '0.75rem' }}>({supplier.changePercent.toFixed(1)}%)</span>
                                          </div>
                                        </TableCell>
                                      </>
                                    )}
                                    <TableCell>${supplier.avgPurchase.toFixed(2)}</TableCell>
                                    <TableCell>
                                      {supplier.lastPurchase ? formatDate(supplier.lastPurchase) : '-'}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Feb 2026 — Expenses By Supplier tab. Mirrors the Supplier
                Spending layout but aggregates over db.expenses (non-stock
                bills — utilities, subscriptions, freight, etc.) rather than
                db.purchases. Reuses expenseReportData that's fetched by the
                same useEffect. */}
            <TabsContent value="expenses-by-supplier">
              <Card>
                <CardHeader>
                  <CardTitle>Expenses By Supplier</CardTitle>
                  <CardDescription>How much you spent with each supplier on <strong>non-stock expenses</strong> (utilities, subscriptions, freight, etc.) in the period — filtered by <em>expense date</em>. Stock purchases from suppliers live on the <strong>Supplier Spending</strong> tab. Numbers include GST.</CardDescription>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Group expenseReportData by supplier_name (falls back to "(no supplier)").
                    const bySup = {};
                    for (const e of expenseReportData) {
                      const key = (e.supplier_name && e.supplier_name.trim()) || '(no supplier)';
                      if (!bySup[key]) {
                        bySup[key] = { name: key, supplier_id: e.supplier_id || null, count: 0, total: 0, last: null, expenses: [] };
                      }
                      bySup[key].count += 1;
                      bySup[key].total += (e.total_amount || 0);
                      const d = e.date || '';
                      if (!bySup[key].last || d > bySup[key].last) bySup[key].last = d;
                      bySup[key].expenses.push(e);
                    }
                    const rows = Object.values(bySup).sort((a, b) => b.total - a.total);
                    const totalSpent = rows.reduce((s, r) => s + r.total, 0);
                    const totalCount = rows.reduce((s, r) => s + r.count, 0);
                    const top = rows[0] || null;
                    if (rows.length === 0) {
                      return (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          No expenses found for the selected date range.
                        </div>
                      );
                    }
                    const formatDateAU = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU') : '—';
                    return (
                      <>
                        {/* Summary cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                          <Card>
                            <CardContent style={{ padding: '1.25rem' }}>
                              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Active Suppliers</div>
                              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{rows.length}</div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.25rem' }}>
                              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Spent</div>
                              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#dc2626' }}>${totalSpent.toFixed(2)}</div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.25rem' }}>
                              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Expenses</div>
                              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{totalCount}</div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent style={{ padding: '1.25rem' }}>
                              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Top Supplier</div>
                              <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{top?.name || 'N/A'}</div>
                              {top && (<div style={{ fontSize: '0.85rem', color: '#dc2626', marginTop: '0.25rem' }}>${top.total.toFixed(2)}</div>)}
                            </CardContent>
                          </Card>
                        </div>

                        {/* Ranked table */}
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Rank</TableHead>
                              <TableHead>Supplier</TableHead>
                              <TableHead className="text-right">Total Expenses</TableHead>
                              <TableHead className="text-right">Total Spent</TableHead>
                              <TableHead className="text-right">Avg Expense</TableHead>
                              <TableHead>Last Expense</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map((r, idx) => (
                              <TableRow
                                key={r.name}
                                onClick={() => setExpensePieFilter(null) || (() => {
                                  // No dedicated drill card yet — for now just log; future: expand supplier row inline
                                })}
                                data-testid={`expenses-by-supplier-row-${idx}`}
                              >
                                <TableCell>#{idx + 1}</TableCell>
                                <TableCell style={{ fontWeight: 500 }}>{r.name}</TableCell>
                                <TableCell className="text-right">{r.count}</TableCell>
                                <TableCell className="text-right" style={{ color: '#dc2626', fontWeight: 500 }}>${r.total.toFixed(2)}</TableCell>
                                <TableCell className="text-right">${(r.total / r.count).toFixed(2)}</TableCell>
                                <TableCell>{formatDateAU(r.last)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="details">
              <Card>
                <CardHeader>
                  <CardTitle>Filtered Jobs ({filteredJobs.length})</CardTitle>
                  <CardDescription>Every job booked in the selected period regardless of paid / finished status — filtered by <em>booking date</em>. Click a column header to sort.</CardDescription>
                </CardHeader>
                <CardContent>
                  <SortableJobsTable
                    jobs={filteredJobs}
                    columns={[
                      { key: 'job_number',    label: 'Job #',    type: 'number', cellClassName: 'font-semibold' },
                      { key: 'customer_name', label: 'Customer', type: 'string' },
                      { key: 'status',        label: 'Status',   type: 'string' },
                      { key: 'job_type',      label: 'Type',     type: 'string' },
                      { key: 'brand',         label: 'Brand',    type: 'string' },
                      { key: 'state',         label: 'State',    type: 'string' },
                      { key: 'postcode',      label: 'Postcode', type: 'string' },
                      { key: 'date_in',       label: 'Date In',  type: 'string', render: (j) => formatDate(j.date_in) },
                      { key: 'total_amount',  label: 'Total',    type: 'number', render: (j) => `$${parseFloat(j.total_amount || 0).toFixed(2)}` },
                    ]}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Personal / Internal Items drill-in dialog */}
      <Dialog open={personalDrillIn.open} onOpenChange={(open) => setPersonalDrillIn({ ...personalDrillIn, open })}>
        <DialogContent style={{ maxWidth: '900px', maxHeight: '85vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>{personalDrillIn.title}</DialogTitle>
            <DialogDescription>
              {personalDrillIn.items.length} item{personalDrillIn.items.length === 1 ? '' : 's'} · 
              Total ${personalDrillIn.items.reduce((s, i) => s + (i.total || 0), 0).toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          {personalDrillIn.items.length === 0 ? (
            <p style={{ color: '#64748b', textAlign: 'center', padding: '1.5rem' }}>No personal items in this group.</p>
          ) : (
            <div style={{ maxHeight: '65vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Ex GST</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {personalDrillIn.items.map((item, idx) => (
                    <TableRow key={idx} data-testid={`personal-drill-row-${idx}`}>
                      <TableCell>{formatDate(item.date)}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{item.supplier || '—'}</TableCell>
                      <TableCell><span style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '0.25rem' }}>{item.type}</span></TableCell>
                      <TableCell className="text-right">${(item.amount_ex_gst || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right">${(item.gst || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right" style={{ fontWeight: 600 }}>${(item.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter style={{ marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setPersonalDrillIn({ ...personalDrillIn, open: false })} data-testid="personal-drill-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Type drill-in dialog (click Partner / End User / Not Specified row) */}
      <Dialog open={customerTypeDrillIn.open} onOpenChange={(open) => setCustomerTypeDrillIn({ ...customerTypeDrillIn, open })}>
        <DialogContent style={{ maxWidth: '1080px', maxHeight: '88vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>{customerTypeDrillIn.type} Customers</DialogTitle>
            <DialogDescription>
              {(() => {
                if (customerTypeDrillIn.loading) return 'Loading...';
                const periodNamesLC = new Set((customerTypeDrillIn.periodNames || []).map(n => (n || '').toLowerCase()));
                const shown = customerTypeDrillIn.periodOnly
                  ? customerTypeDrillIn.customers.filter(c => periodNamesLC.has((c.name || '').toLowerCase())).length
                  : customerTypeDrillIn.customers.length;
                const totalLbl = customerTypeDrillIn.periodOnly
                  ? `${shown.toLocaleString()} in period (of ${customerTypeDrillIn.customers.length.toLocaleString()} all-time)`
                  : `${shown.toLocaleString()} customer${shown === 1 ? '' : 's'}`;
                return `${totalLbl} · click any column header to sort · click a name to open their card`;
              })()}
            </DialogDescription>
          </DialogHeader>

          {/* Period-only toggle + Copy Emails shortcut (newsletter workflow) */}
          {customerTypeDrillIn.periodNames && customerTypeDrillIn.periodNames.length > 0 && (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', padding: '0.5rem 0.75rem', background: '#f8fafc', borderRadius: '0.5rem', marginBottom: '0.75rem' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!customerTypeDrillIn.periodOnly}
                  onChange={(e) => setCustomerTypeDrillIn({ ...customerTypeDrillIn, periodOnly: e.target.checked })}
                  data-testid="ct-drill-period-only"
                />
                <span>Show only <strong>{customerTypeDrillIn.periodNames.length}</strong> customer{customerTypeDrillIn.periodNames.length === 1 ? '' : 's'} who had jobs in the selected date range</span>
              </label>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="ct-drill-copy-emails"
                  onClick={() => {
                    const periodNamesLC = new Set((customerTypeDrillIn.periodNames || []).map(n => (n || '').toLowerCase()));
                    const rows = customerTypeDrillIn.periodOnly
                      ? customerTypeDrillIn.customers.filter(c => periodNamesLC.has((c.name || '').toLowerCase()))
                      : customerTypeDrillIn.customers;
                    const emails = rows.map(c => (c.email || '').trim()).filter(Boolean);
                    if (emails.length === 0) {
                      toast.error('No email addresses in this view');
                      return;
                    }
                    // Deduplicate while preserving order
                    const uniq = Array.from(new Set(emails.map(e => e.toLowerCase())));
                    navigator.clipboard.writeText(uniq.join(', '))
                      .then(() => toast.success(`Copied ${uniq.length} email${uniq.length === 1 ? '' : 's'} to clipboard`))
                      .catch(() => toast.error('Clipboard blocked — see the list on-screen'));
                  }}
                >
                  📋 Copy Emails
                </Button>
              </div>
            </div>
          )}

          {customerTypeDrillIn.loading ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading customers...</p>
          ) : (() => {
            const periodNamesLC = new Set((customerTypeDrillIn.periodNames || []).map(n => (n || '').toLowerCase()));
            const shown = customerTypeDrillIn.periodOnly
              ? customerTypeDrillIn.customers.filter(c => periodNamesLC.has((c.name || '').toLowerCase()))
              : customerTypeDrillIn.customers;
            if (shown.length === 0) {
              return <p style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No customers found{customerTypeDrillIn.periodOnly ? ' in the selected date range' : ''}.</p>;
            }
            return (
            <div style={{ maxHeight: '65vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead onClick={() => onCtSortClick('name')}        style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="ct-drill-sort-name">        Name <span style={{ color: ctSortBy === 'name' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{ctSortArrow('name')}</span></TableHead>
                    <TableHead onClick={() => onCtSortClick('mobile')}      style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="ct-drill-sort-mobile">      Mobile <span style={{ color: ctSortBy === 'mobile' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{ctSortArrow('mobile')}</span></TableHead>
                    <TableHead onClick={() => onCtSortClick('email')}       style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="ct-drill-sort-email">       Email <span style={{ color: ctSortBy === 'email' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{ctSortArrow('email')}</span></TableHead>
                    <TableHead onClick={() => onCtSortClick('suburb')}      style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="ct-drill-sort-suburb">      Suburb <span style={{ color: ctSortBy === 'suburb' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{ctSortArrow('suburb')}</span></TableHead>
                    <TableHead onClick={() => onCtSortClick('job_count')}   style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'right' }} data-testid="ct-drill-sort-job_count" className="text-right">   Jobs <span style={{ color: ctSortBy === 'job_count' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{ctSortArrow('job_count')}</span></TableHead>
                    <TableHead onClick={() => onCtSortClick('total_spend')} style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'right' }} data-testid="ct-drill-sort-total_spend" className="text-right"> Total Spend <span style={{ color: ctSortBy === 'total_spend' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{ctSortArrow('total_spend')}</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.map(c => (
                    <TableRow
                      key={c.id}
                      onClick={() => {
                        const currentType = customerTypeDrillIn.type;
                        navigate(`/reports?customerType=${encodeURIComponent(currentType)}`, { replace: true });
                        setCustomerTypeDrillIn({ ...customerTypeDrillIn, open: false });
                        setTimeout(() => navigate(`/customers?highlight=${c.id}`), 0);
                      }}
                      style={{ cursor: 'pointer' }}
                      className="hover:bg-slate-50"
                      data-testid={`ct-drill-cust-${c.id}`}
                    >
                      <TableCell style={{ fontWeight: 600, color: '#2563eb' }}>{c.name}</TableCell>
                      <TableCell>{c.mobile || '—'}</TableCell>
                      <TableCell>{c.email || '—'}</TableCell>
                      <TableCell>{c.suburb || '—'}</TableCell>
                      <TableCell className="text-right">{(c.job_count || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right" style={{ fontWeight: 600 }}>${(c.total_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>);
          })()}
          <DialogFooter style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
            <Button
              variant="link"
              onClick={() => { setCustomerTypeDrillIn({ ...customerTypeDrillIn, open: false }); navigate('/customers'); }}
              data-testid="ct-drill-open-customers-page"
              style={{ padding: 0 }}
            >
              Open in Customers page →
            </Button>
            <Button variant="outline" onClick={() => setCustomerTypeDrillIn({ ...customerTypeDrillIn, open: false })} data-testid="ct-drill-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-close job dialog — surfaces from the Payment Status drill-down
          Actions column (single-mode) or the "Close N Selected" toolbar
          (bulk-mode). Lets the user pick a terminal status + close without
          leaving Reports. Added Feb 2026. */}
      <Dialog
        open={closeDialogMode === 'bulk' || !!closeDialogJob}
        onOpenChange={(open) => { if (!open) { setCloseDialogJob(null); setCloseDialogMode('single'); } }}
      >
        <DialogContent data-testid="close-job-dialog" style={{ maxWidth: 460 }}>
          <DialogHeader>
            <DialogTitle>
              {closeDialogMode === 'bulk'
                ? `Close ${selectedJobIds.size} Jobs`
                : `Close Job ${closeDialogJob ? `#${closeDialogJob.job_number}` : ''}`}
            </DialogTitle>
            <DialogDescription>
              {closeDialogMode === 'bulk' ? (
                <>
                  You&rsquo;re about to close <strong>{selectedJobIds.size}</strong> job{selectedJobIds.size === 1 ? '' : 's'} in a single action. All selected jobs will get the status you pick below.
                </>
              ) : closeDialogJob ? (
                <>
                  <strong>{closeDialogJob.customer_name}</strong> · Currently <em>{closeDialogJob.status}</em>
                  {(closeDialogJob.balance_owing || 0) > 0 && (
                    <> · Outstanding <strong style={{ color: '#dc2626' }}>${(closeDialogJob.balance_owing || 0).toFixed(2)}</strong></>
                  )}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div style={{ padding: '0.5rem 0' }}>
            <Label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
              {closeDialogMode === 'bulk' ? 'Move all selected jobs to which status?' : 'Move this job to which status?'}
            </Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {['Finish', 'Not Recoverable', 'Not Proceeding'].map((s) => (
                <label
                  key={s}
                  data-testid={`close-status-${s.replace(/\s+/g, '-').toLowerCase()}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem',
                    cursor: 'pointer',
                    background: closeDialogStatus === s ? '#eff6ff' : '#ffffff',
                    borderColor: closeDialogStatus === s ? '#3b82f6' : '#e2e8f0',
                  }}
                >
                  <input
                    type="radio"
                    name="close-status"
                    value={s}
                    checked={closeDialogStatus === s}
                    onChange={() => setCloseDialogStatus(s)}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s}</span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {s === 'Finish' && 'Job completed successfully.'}
                      {s === 'Not Recoverable' && 'Data / repair attempted but couldn\u2019t be recovered.'}
                      {s === 'Not Proceeding' && 'Customer decided not to continue (stale lead / no comms).'}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.75rem' }}>
              {closeDialogMode === 'bulk' ? (
                <>All selected jobs will be marked <strong>closed</strong> and each will have its <em>date_finished</em> auto-stamped to today (Sydney) if it isn&rsquo;t already set. Any outstanding balances stay on the jobs for the audit trail.</>
              ) : (
                <>The job will be marked <strong>closed</strong> and its <em>date_finished</em> auto-stamped to today (Sydney) if it isn&rsquo;t already set. Any outstanding balance stays on the job for the audit trail.</>
              )}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setCloseDialogJob(null); setCloseDialogMode('single'); }}
              disabled={closeDialogBusy}
              data-testid="close-job-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmCloseJob}
              disabled={closeDialogBusy}
              data-testid="close-job-confirm"
              style={{ background: '#dc2626', color: '#fff' }}
            >
              {closeDialogBusy
                ? 'Closing\u2026'
                : closeDialogMode === 'bulk'
                  ? `Close ${selectedJobIds.size} as ${closeDialogStatus}`
                  : `Close as ${closeDialogStatus}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
